import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, LabelList, LineChart, Line,
} from 'recharts';
import {
  authApi, visitsApi, clientsApi, conversationsApi, sellersApi, vehiclesApi, dashboardApi,
  usersApi, settingsApi, aiApi, organizationsApi, brandsApi, vehicleFeaturesApi, cnpjApi, reportsApi,
  setActiveStoreId, getActiveStoreId, apiErrorMessage, INACTIVITY_LOGOUT_FLAG, proposalsApi,
  testDrivesApi, fipeApi, aftersalesApi,
} from './services/api';
import PrivacyPage from './PrivacyPage';
import {
  pushStatus, subscribeToPush, unsubscribeFromPush, resyncPush, detachPush, PUSH_DEVICE_FLAG,
} from './services/push';
import { pushApi } from './services/api';
import './App.css';

/**
 * Cores dos gráficos.
 *
 * Não são as variáveis do tema (--blue, --green…): aquelas foram escolhidas para
 * texto e botões e reprovam como paleta de série sobre o fundo dos cartões
 * (#242b3d) — claras demais para a faixa de luminosidade do modo escuro, e o par
 * verde/vermelho fica em ΔE 7,4 na deuteranopia, ou seja, "convertida" e
 * "perdida" viram a mesma cor para quem tem daltonismo vermelho-verde.
 *
 * Esta sequência passa em todas as checagens (faixa de luminosidade, croma,
 * separação sob daltonismo, visão normal e contraste ≥ 3:1 contra o cartão). A
 * ORDEM é o que garante a separação: os slots são atribuídos em sequência, nunca
 * escolhidos por gosto ou reciclados.
 */
const CHART_COLORS = ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181'];

// Séries do gráfico de linhas — roxo para visitas, verde para vendas. Não são
// os --purple/--green do tema: aqueles ficam fora da faixa de luminosidade do
// modo escuro. Estes passam em todas as checagens sobre o fundo do cartão.
const LINE_COLORS = { visits: '#9085e9', sales: '#199e70' };

const CHART_INK = { muted: '#898781', grid: '#2c2c2a' };

// Rótulo dos status de visita nos gráficos. Mesma ordem do ciclo de vida, para
// a rosca ser lida na sequência em que a visita realmente caminha.
const VISIT_STATUS_LABELS = {
  active: 'Ativa',
  followup_pending: 'Follow-up pendente',
  followup_sent: 'Follow-up enviado',
  converted: 'Convertida',
  lost: 'Perdida',
};

// Cor de cada status nas etiquetas. Fica junto dos rótulos para que "convertida"
// não seja verde numa tela e âmbar na outra; o texto é que muda de tela para
// tela (o gráfico fala de visitas, a lista fala do cliente).
const VISIT_STATUS_TONE = {
  active: 'blue',
  followup_pending: 'amber',
  followup_sent: 'purple',
  converted: 'green',
  lost: 'red',
};

/** Tooltip com a cara do painel — o padrão do Recharts vem com fundo branco. */
const chartTooltipStyle = {
  contentStyle: {
    background: '#2d3650',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 10,
    fontSize: 13,
  },
  labelStyle: { color: '#e8eaf0', fontWeight: 500 },
  itemStyle: { color: '#9ba3b8' },
  cursor: { fill: 'rgba(255,255,255,0.04)' },
};

/**
 * O Recharts pinta o texto da legenda com a cor da série. Quem carrega a
 * identidade é o quadradinho colorido ao lado; o texto fica na cor de texto do
 * painel, senão viram cinco cores de fonte competindo entre si.
 */
const legendLabel = (value) => <span style={{ color: '#9ba3b8' }}>{value}</span>;

/** Idem, com o valor junto — usado na rosca, que não tem eixo para os números. */
const legendLabelWithCount = (value, entry) => (
  <span style={{ color: '#9ba3b8' }}>
    {value} <strong style={{ color: '#e8eaf0' }}>{entry?.payload?.count}</strong>
  </span>
);

/** Moldura comum dos gráficos, com o aviso de vazio no lugar do gráfico. */
function ChartCard({ title, hint, isEmpty, emptyText, wide, filter, children }) {
  return (
    <div className={`chart-card${wide ? ' chart-card--wide' : ''}`}>
      <div className="chart-card-head">
        <h3>{title}</h3>
        {hint && <p>{hint}</p>}
      </div>
      {/* O filtro fica acima do gráfico e continua visível quando não há dados:
          é justamente aí que se precisa dele para afrouxar a busca. */}
      {filter}
      {isEmpty ? <div className="chart-empty">{emptyText}</div> : children}
    </div>
  );
}

// ─── ICONS (SVG inline) ───────────────────────────────────────────────────────
const Icon = ({ name, size = 20 }) => {
  const icons = {
    car: <><path d="M7 17m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0"/><path d="M17 17m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0"/><path d="M5 17h-2v-6l2 -5h9l4 4h1a2 2 0 0 1 2 2v5h-2m-4 0h-6m-6 -6h15m-6 0v-5"/></>,
    chat: <><path d="M3 20l1.3 -3.9c-2.324 -3.437 -1.426 -7.872 2.1 -10.374c3.526 -2.501 8.59 -2.296 11.845 .48c3.255 2.777 3.695 7.266 1.029 10.501c-2.666 3.235 -7.615 4.215 -11.574 2.293l-4.7 1"/></>,
    dashboard: <><path d="M12 3h-7a2 2 0 0 0 -2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2 -2v-7"/><path d="M18 9v-6"/><path d="M15 12h-10"/><path d="M15 8h-10"/><path d="M15 16h-10"/></>,
    users: <><path d="M9 7m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0"/><path d="M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/><path d="M21 21v-2a4 4 0 0 0 -3 -3.85"/></>,
    settings: <><path d="M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065z"/><path d="M12 12m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0"/></>,
    logout: <><path d="M14 8v-2a2 2 0 0 0 -2 -2h-7a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h7a2 2 0 0 0 2 -2v-2"/><path d="M9 12h12l-3 -3"/><path d="M18 15l3 -3"/></>,
    bell: <><path d="M10 5a2 2 0 1 1 4 0a7 7 0 0 1 4 6v3a4 4 0 0 0 2 3h-16a4 4 0 0 0 2 -3v-3a7 7 0 0 1 4 -6"/><path d="M9 17v1a3 3 0 0 0 6 0v-1"/></>,
    plus: <><path d="M12 5l0 14"/><path d="M5 12l14 0"/></>,
    mic: <><path d="M9 5a3 3 0 0 1 6 0v5a3 3 0 0 1 -6 0z"/><path d="M5 10a7 7 0 0 0 14 0"/><path d="M8 21l8 0"/><path d="M12 17l0 4"/></>,
    send: <><path d="M10 14l11 -11"/><path d="M21 3l-6.5 18a.55 .55 0 0 1 -1 0l-3.5 -7l-7 -3.5a.55 .55 0 0 1 0 -1l18 -6.5"/></>,
    bot: <><path d="M12 18h.01"/><path d="M10 10h.01"/><path d="M14 10h.01"/><rect x="3" y="7" width="18" height="14" rx="3"/><path d="M8 7v-2h8v2"/><path d="M12 3v4"/></>,
    seller: <><path d="M17 8c0 4.418 -5 11 -5 11s-5 -6.582 -5 -11a5 5 0 0 1 10 0z"/><circle cx="12" cy="8" r="2"/></>,
    whatsapp: <><path d="M3 21l1.65 -3.8a9 9 0 1 1 3.4 2.9l-5.05 .9"/><path d="M9 10a.5 .5 0 0 0 1 0v-1a.5 .5 0 0 0 -1 0v1a5 5 0 0 0 5 5h1a.5 .5 0 0 0 0 -1h-1a.5 .5 0 0 0 0 1"/></>,
    calendar: <><rect x="4" y="5" width="16" height="16" rx="2"/><path d="M16 3v4"/><path d="M8 3v4"/><path d="M4 11h16"/><path d="M8 15h2"/><path d="M8 19h2"/><path d="M14 15h2"/><path d="M14 19h2"/></>,
    check: <path d="M5 12l5 5l10 -10"/>,
    x: <><path d="M18 6l-12 12"/><path d="M6 6l12 12"/></>,
    arrow_right: <><path d="M9 6l6 6l-6 6"/></>,
    user: <><path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0"/><path d="M6 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2"/></>,
    phone: <><path d="M5 4h4l2 5l-2.5 1.5a11 11 0 0 0 5 5l1.5 -2.5l5 2v4a2 2 0 0 1 -2 2a16 16 0 0 1 -15 -15a2 2 0 0 1 2 -2"/></>,
    clock: <><path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0"/><path d="M12 7v5l3 3"/></>,
    refresh: <><path d="M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4"/><path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4"/></>,
    edit: <><path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1"/><path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z"/><path d="M16 5l3 3"/></>,
    filter: <><path d="M4 4h16v2.172a2 2 0 0 1 -.586 1.414l-4.414 4.414v7l-6 2v-8.5l-4.48 -4.928a2 2 0 0 1 -.52 -1.345v-2.227z"/></>,
    report: <><path d="M4 19h16"/><path d="M6 16v-5"/><path d="M11 16v-9"/><path d="M16 16v-3"/></>,
    download: <><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2"/><path d="M7 11l5 5l5 -5"/><path d="M12 4v12"/></>,
    paperclip: <path d="M15 7l-6.5 6.5a1.5 1.5 0 0 0 3 3l6.5 -6.5a3 3 0 0 0 -6 -6l-6.5 6.5a4.5 4.5 0 0 0 9 9l6.5 -6.5"/>,
    file: <><path d="M14 3v4a1 1 0 0 0 1 1h4"/><path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z"/></>,
    wrench: <><path d="M7 10h3v-3l-3.5 -3.5a6 6 0 0 1 8 8l6 6a2 2 0 0 1 -3 3l-6 -6a6 6 0 0 1 -8 -8l3.5 3.5"/></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      {icons[name]}
    </svg>
  );
};

/**
 * Estado vazio com orientação: ícone, o que está acontecendo e — quando há um
 * próximo passo local (limpar filtro, abrir o formulário) — o botão para ele.
 * Texto solto numa linha de tabela parecia tela morta.
 */
function EmptyState({ icon = 'file', title, hint, actionLabel, onAction }) {
  return (
    <div className="empty-state">
      <div className="empty-state-icon"><Icon name={icon} size={26} /></div>
      <div className="empty-state-title">{title}</div>
      {hint && <div className="empty-state-hint">{hint}</div>}
      {actionLabel && onAction && (
        <button type="button" className="btn btn-secondary btn-sm" onClick={onAction}>
          {actionLabel}
        </button>
      )}
    </div>
  );
}

/**
 * Esqueletos de carregamento. Trocar de loja remonta o conteúdo inteiro (o
 * `key` no <main>), então cada troca piscava: spinner centralizado → conteúdo.
 * Blocos no formato do que vai chegar seguram o layout no lugar.
 */
function SkeletonList({ rows = 6, card = true }) {
  const body = (
    <div className="skeleton-list" aria-hidden="true">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="skeleton-row">
          <div className="skeleton skeleton-circle" />
          <div className="skeleton-lines">
            {/* Larguras variadas: barras idênticas parecem tabela congelada. */}
            <div className="skeleton" style={{ width: `${52 - (i % 3) * 9}%` }} />
            <div className="skeleton" style={{ width: `${30 - (i % 3) * 5}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
  return card ? <div className="table-container">{body}</div> : body;
}

function SkeletonDashboard() {
  return (
    <div aria-hidden="true">
      <div className="stats-grid">
        {[0, 1, 2, 3].map(i => (
          <div key={i} className="stat-card">
            <div className="skeleton" style={{ width: 24, height: 24, marginBottom: 14 }} />
            <div className="skeleton" style={{ width: '45%', height: 30, marginBottom: 10 }} />
            <div className="skeleton" style={{ width: '70%' }} />
          </div>
        ))}
      </div>
      <div className="chart-card">
        <div className="skeleton" style={{ width: '30%', marginBottom: 16 }} />
        <div className="skeleton" style={{ width: '100%', height: 220 }} />
      </div>
    </div>
  );
}

/**
 * Tempo relativo do inbox: "há 3 dias" comunica urgência de follow-up; a data
 * completa não. A partir de uma semana volta a ser data, que aí é o que importa.
 */
function timeAgo(dateStr) {
  const date = new Date(dateStr);
  const mins = Math.floor((Date.now() - date.getTime()) / 60000);
  if (mins < 1) return 'agora';
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} h`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'ontem';
  if (days < 7) return `${days} d`;
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

// Motivos de perda da visita. Os valores espelham a CHECK constraint de
// visits.lost_reason; 'nao_informado' só existe no gráfico (visitas perdidas
// antes do campo existir, ou marcadas sem informar).
const LOST_REASON_LABELS = {
  preco: 'Preço',
  sem_estoque: 'Sem estoque',
  concorrente: 'Comprou no concorrente',
  sem_resposta: 'Cliente não respondeu',
  outro: 'Outro',
  nao_informado: 'Não informado',
};

/**
 * Gera e baixa um CSV. Separador ';' e BOM porque é o que o Excel em português
 * abre com acentos e colunas certos sem passar pelo assistente de importação.
 */
function downloadCsv(filename, headers, rows) {
  const escapeCell = (value) => {
    const s = value === null || value === undefined ? '' : String(value);
    return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers, ...rows].map(row => row.map(escapeCell).join(';'));
  // ﻿ é o BOM — sem ele o Excel pt-BR abre o arquivo com os acentos quebrados.
  const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

// Dona da plataforma. Fica no lugar que, para os clientes, mostra o nome da
// empresa: quem entra como superadmin está operando a LimpAr Tech, não um
// cliente. É rótulo — o superadmin continua sem organization_id no banco.
const PLATFORM_OWNER = 'LimpAr Tech';

// Rótulos dos papéis, usados na sidebar, no perfil e na tela de usuários.
const ROLE_LABELS = {
  superadmin: 'Administrador da Plataforma',
  admin: 'Administrador',
  receptionist: 'Recepcionista',
  seller: 'Vendedor',
};

// Os três níveis do cadastro de clientes. A ordem é a da árvore: um grupo tem
// concessionárias, e cada concessionária tem lojas.
const ORG_TYPES = [
  { value: 'grupo', label: 'Grupo', plural: 'Grupos', admin: 'Administrador do grupo' },
  { value: 'concessionaria', label: 'Concessionária', plural: 'Concessionárias', admin: 'Administrador da concessionária' },
  { value: 'loja', label: 'Loja', plural: 'Lojas', admin: 'Administrador da loja' },
];

/** Id da página de cadastro de cada nível — um item de menu por nível. */
const orgPageId = (type) => `org-${type}`;

const ORG_TYPE_LABELS = ORG_TYPES.reduce((acc, t) => ({ ...acc, [t.value]: t.label }), {});

/**
 * Submenus de Relatórios.
 *
 * Relatório novo é UMA linha aqui mais a página correspondente no mapa `pages`
 * (em App): o item de menu, o destaque de item ativo e o grupo colapsável saem
 * disso sozinhos. Só o administrador vê o grupo — relatório cruza as lojas da
 * rede, e recepcionista e vendedor trabalham dentro de uma.
 */
const REPORT_ITEMS = [
  { id: 'report-seller-conversion', label: 'Conversão por Vendedor', icon: 'report' },
  { id: 'report-unattended', label: 'Clientes sem Atendimento', icon: 'clock' },
  { id: 'report-proposals', label: 'Propostas Enviadas', icon: 'file' },
];

/**
 * Os logins que se cadastram, na linguagem de quem usa o sistema.
 *
 * "Diretor" e "Gerente" não são papéis novos no banco: são como se chama o `admin`
 * de cada nível. Escolher aqui define as duas coisas de uma vez — o papel e em que
 * nível a pessoa entra —, que é como o cadastro é pensado na prática ("preciso de
 * um gerente para a loja nova").
 */
const LOGIN_KINDS = [
  { key: 'diretor', role: 'admin', orgType: 'concessionaria', label: 'Diretor — administra a concessionária' },
  { key: 'gerente', role: 'admin', orgType: 'loja', label: 'Gerente — administra a loja' },
  { key: 'receptionist', role: 'receptionist', orgType: 'loja', label: 'Recepcionista — cadastra visitas' },
  { key: 'seller', role: 'seller', orgType: 'loja', label: 'Vendedor — atende os clientes' },
];

/**
 * Quais logins cada nível pode cadastrar. Espelha canGrantAdminAt no backend:
 * administrador só para os níveis ABAIXO do próprio; recepcionista e vendedor
 * sempre em loja.
 */
function creatableLoginKinds(user) {
  if (user?.role === 'superadmin') return LOGIN_KINDS;
  if (user?.organization_type === 'grupo') return LOGIN_KINDS;
  if (user?.organization_type === 'concessionaria') {
    return LOGIN_KINDS.filter(k => k.key !== 'diretor');
  }
  if (user?.organization_type === 'loja') {
    return LOGIN_KINDS.filter(k => k.role !== 'admin');
  }
  return [];
}

const ORG_LEVEL_DEPTH = { grupo: 0, concessionaria: 1, loja: 2 };

/**
 * Administrador que quem está logado NÃO pode alterar: outro admin do mesmo
 * nível (ou acima). Espelha a regra do backend em PATCH /users/:id — a conta
 * é do titular, e mudar outro administrador do próprio nível é com o suporte.
 */
function isLockedAdmin(row, user) {
  if (row.role !== 'admin' || user?.role === 'superadmin') return false;
  if (String(row.id) === String(user?.id)) return false;
  return !(ORG_LEVEL_DEPTH[row.organization_type] > ORG_LEVEL_DEPTH[user?.organization_type]);
}

/** Como o painel chama o papel de alguém, considerando o nível da empresa dele. */
function loginKindLabel(row) {
  if (row.role !== 'admin') return ROLE_LABELS[row.role] || row.role;
  if (row.organization_type === 'concessionaria') return 'Diretor';
  if (row.organization_type === 'loja') return 'Gerente';
  return 'Administrador do grupo';
}

/**
 * "o grupo", "a concessionária", "a loja" — o nível com o artigo concordado, para
 * montar rótulos no meio de uma frase ("Nome do grupo", "Nome da loja").
 */
function withArticlePt(type) {
  const article = type === 'grupo' ? 'o' : 'a';
  return `${article} ${(ORG_TYPE_LABELS[type] || 'empresa').toLowerCase()}`;
}

// Opção final do select de marca: libera um campo de texto para o que não estiver
// na lista, em vez de travar o cadastro de uma marca nova no mercado.
const BRAND_OTHER = '__outra__';

// Unidades da federação, para o campo UF do endereço.
const UFS = [
  'AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT',
  'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO',
];

/** 00.000.000/0000-00 — a máscara é só de exibição; o banco guarda os dígitos. */
function maskCnpj(input) {
  const d = String(input ?? '').replace(/\D/g, '').slice(0, 14);
  return d
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
}

/** 00000-000 */
function maskCep(input) {
  const d = String(input ?? '').replace(/\D/g, '').slice(0, 8);
  return d.replace(/^(\d{5})(\d)/, '$1-$2');
}

/**
 * Um contato em branco, para o "+ Adicionar novo contato".
 *
 * `key` existe só para o React: os contatos ainda não salvos não têm id, e usar o
 * índice como key faria o campo em foco pular de linha ao remover uma anterior.
 */
let contactKeySeq = 0;
const emptyContact = () => ({
  key: `novo-${++contactKeySeq}`, name: '', job_title: '', email: '', phone: '',
});

// ─── AUTH ─────────────────────────────────────────────────────────────────────
// Sessão expira por inatividade (segurança: o painel fica aberto em balcão e
// recepção). O instante da última interação vai para o localStorage, e não só
// para a memória, por dois motivos: mexer em uma aba conta para as outras, e
// fechar o navegador e reabrir depois do limite também derruba a sessão — o
// timer só em memória zeraria a contagem a cada recarga.
const INACTIVITY_LIMIT_MS = 5 * 60 * 1000;
const LAST_ACTIVITY_KEY = 'crm_last_activity';

function useInactivityLogout(active, onExpire) {
  useEffect(() => {
    if (!active) return undefined;
    const touch = () => localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
    const expired = () => {
      const last = Number(localStorage.getItem(LAST_ACTIVITY_KEY)) || 0;
      return Date.now() - last > INACTIVITY_LIMIT_MS;
    };
    // Recarga depois do limite: derruba antes de mostrar qualquer dado.
    if (expired()) { onExpire(); return undefined; }
    if (!localStorage.getItem(LAST_ACTIVITY_KEY)) touch();

    // Sem 'scroll': o inbox rola sozinho (scrollIntoView quando chega mensagem
    // nova e a leitura está no fim), e isso contaria como atividade. Rolagem humana já entra por
    // wheel, touchstart e keydown.
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'wheel'];
    // Gravar a cada evento de mouse seria excessivo: no máximo uma vez por segundo.
    let lastWrite = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - lastWrite < 1000) return;
      lastWrite = now;
      touch();
    };
    events.forEach(ev => window.addEventListener(ev, onActivity, { passive: true }));
    const timer = setInterval(() => { if (expired()) onExpire(); }, 10000);
    // Heartbeat: o servidor também expira a sessão por inatividade, contando
    // pela última requisição. Quem lê uma tela sem disparar nada (o dashboard,
    // por exemplo) mexendo o mouse continua ativo — a cada minuto, se houve
    // interação, uma chamada barata avisa o servidor.
    let lastBeat = Date.now();
    const heartbeat = setInterval(() => {
      const last = Number(localStorage.getItem(LAST_ACTIVITY_KEY)) || 0;
      if (last > lastBeat) {
        lastBeat = Date.now();
        authApi.me().catch(() => { /* rede fora: o interceptor cuida do 401 */ });
      }
    }, 60000);
    return () => {
      events.forEach(ev => window.removeEventListener(ev, onActivity));
      clearInterval(timer);
      clearInterval(heartbeat);
    };
  }, [active, onExpire]);
}

function LoginPage({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Aviso de sessão encerrada por inatividade: lido uma vez e apagado, para não
  // reaparecer num logout voluntário mais tarde.
  const [notice] = useState(() => {
    const flagged = localStorage.getItem(INACTIVITY_LOGOUT_FLAG);
    if (flagged) localStorage.removeItem(INACTIVITY_LOGOUT_FLAG);
    return flagged ? 'Sua sessão foi encerrada após 5 minutos sem atividade. Entre novamente.' : '';
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await authApi.login(email, password);
      localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
      localStorage.setItem('crm_token', res.data.token);
      localStorage.setItem('crm_user', JSON.stringify(res.data.user));
      onLogin(res.data.user);
    } catch (err) {
      // O login é a primeira chamada do painel: é aqui que uma API mal
      // configurada aparece, e a mensagem precisa dizer onde olhar.
      setError(apiErrorMessage(err, 'Erro ao fazer login'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo">
          <img src="/icons/app-192.png" alt="New Car CRM" />
        </div>
        <h1>New Car CRM</h1>
        <p>Sistema de Relacionamento com Cliente</p>
        <form onSubmit={handleSubmit}>
          {notice && !error && <div className="alert alert-warning">{notice}</div>}
          {error && <div className="alert alert-error">{error}</div>}
          <div className="field">
            <label>E-mail</label>
            <input type="email" autoComplete="username" value={email} onChange={e => setEmail(e.target.value)} placeholder="seu@email.com" required />
          </div>
          <div className="field">
            <label>Senha</label>
            <input type="password" autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required />
          </div>
          <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
            {loading ? 'Entrando...' : 'Entrar'}
          </button>
        </form>
        <p className="login-footer">
          <a href="/privacidade">Política de Privacidade</a>
        </p>
      </div>
    </div>
  );
}

// ─── SIDEBAR ──────────────────────────────────────────────────────────────────

/**
 * Um item de menu que abre submenus.
 *
 * Abre sozinho quando a página ativa é uma das de dentro — senão, entrar num
 * submenu deixaria o grupo fechado e o item ativo escondido. `startOpen` força a
 * abertura de quem é o menu principal do usuário, e não um agrupamento
 * secundário (o caso do superadmin, que só tem as telas de clientes).
 */
function NavGroup({ label, icon, items, page, setPage, startOpen }) {
  const [open, setOpen] = useState(() => startOpen || items.some(item => item.id === page));

  if (items.length === 0) return null;

  return (
    <>
      <button
        className={`nav-item nav-group${open ? ' open' : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <Icon name={icon} size={18} />
        <span>{label}</span>
        <span className="nav-group-caret">{open ? '−' : '+'}</span>
      </button>
      {open && items.map(item => (
        <button
          key={item.id}
          className={`nav-item nav-subitem ${page === item.id ? 'active' : ''}`}
          onClick={() => setPage(item.id)}
        >
          <Icon name={item.icon} size={16} />
          <span>{item.label}</span>
        </button>
      ))}
    </>
  );
}

function Sidebar({ user, page, setPage, onLogout, stores, activeStore, onChangeStore, unreadCount = 0 }) {
  const isAdmin = user?.role === 'admin';
  const isSuperadmin = user?.role === 'superadmin';

  // O superadmin administra a plataforma, não um cliente: ele não tem dados
  // próprios de visitas ou conversas, então essas telas não fazem sentido.
  //
  // Um item por NÍVEL, e não uma tela só com seletor de nível: cadastrar grupo,
  // concessionária e loja são três tarefas distintas, e a hierarquia (1º grupo,
  // 2º concessionária, 3º loja) fica visível no próprio menu. Cada usuário vê só
  // os níveis que pode cadastrar — o admin de uma loja não vê nenhum.
  const orgItems = ORG_TYPES
    .filter(t => creatableOrgTypes(user).includes(t.value))
    .map(t => ({ id: orgPageId(t.value), label: t.plural, icon: 'car' }));

  // O grupo colapsável junta as telas de cadastro e abre o menu: a ordem segue
  // o fluxo de trabalho (cadastrar → registrar a visita → acompanhar → medir).
  const groupLabel = isSuperadmin ? 'Clientes' : 'Cadastro';
  const groupItems = isSuperadmin
    ? orgItems
    : [
        ...orgItems,
        // A ficha da própria loja (ou da loja ativa, para quem administra a
        // rede): é admin porque GET /organizations é admin.
        ...(isAdmin ? [{ id: 'store-profile', label: 'Minha Loja', icon: 'car' }] : []),
        // Vendedores ficam aqui dentro desde 03/11/2026: o perfil "Vendedor"
        // cria o registro de vendedor junto com o login.
        { id: 'users', label: 'Usuários', icon: 'user' },
        { id: 'vehicles', label: 'Veículos', icon: 'car' },
      ];

  // Itens do dia a dia, renderizados logo abaixo do grupo de cadastro.
  const navItems = isSuperadmin
    ? []
    : [
        { id: 'new-visit', label: 'Nova Visita', icon: 'plus' },
        { id: 'visits', label: 'Visitas', icon: 'car' },
        { id: 'test-drives', label: 'Test-drives', icon: 'calendar' },
        { id: 'proposals', label: 'Propostas', icon: 'file' },
        // Pós-venda é campanha da loja (admin e recepção); o vendedor recebe
        // a conversa quando o cliente responde.
        ...(user?.role !== 'seller' ? [{ id: 'aftersales', label: 'Pós-venda', icon: 'wrench' }] : []),
        { id: 'conversations', label: 'Conversas', icon: 'chat' },
        { id: 'dashboard', label: 'Dashboard', icon: 'dashboard' },
      ];

  const tailItems = isSuperadmin
    ? [{ id: 'profile', label: 'Meu Perfil', icon: 'user' }]
    : [
        ...(isAdmin ? [{ id: 'settings', label: 'Configurações', icon: 'settings' }] : []),
        { id: 'profile', label: 'Meu Perfil', icon: 'user' },
      ];

  // Relatórios é do administrador: cruza as lojas da rede, enquanto recepcionista
  // e vendedor trabalham dentro de uma. O superadmin não tem dados de cliente.
  const reportItems = isAdmin ? REPORT_ITEMS : [];

  return (
    <aside className="sidebar">
      <div className="sidebar-brand">
        <img src="/icons/app-192.png" alt="" />
        <span>New Car CRM</span>
      </div>
      {/* Com vários clientes no mesmo sistema, deixar visível em qual deles se
          está evita cadastrar visita na conta errada. */}
      {user?.organization_name && (
        <div className="sidebar-org" title={`${ORG_TYPE_LABELS[user.organization_type] || 'Empresa'} do usuário`}>
          {user.organization_name}
          {user.organization_brand ? <span className="sidebar-org-note"> {user.organization_brand}</span> : null}
        </div>
      )}
      {isSuperadmin && (
        <>
          <div className="sidebar-org" title="Dono da plataforma">{PLATFORM_OWNER}</div>
          <div className="sidebar-org-note">dono do app</div>
        </>
      )}

      {/* Quem administra um grupo ou uma concessionária tem várias lojas: as telas
          de leitura somam todas, mas cadastrar visita precisa de destino único.
          Um admin de loja não vê este seletor — ele já tem uma loja só. */}
      {!isSuperadmin && stores && stores.length > 0 && user?.organization_type !== 'loja' && (
        <div className="sidebar-store">
          <label htmlFor="store-picker">Loja ativa</label>
          <select
            id="store-picker"
            value={activeStore || ''}
            onChange={e => onChangeStore(e.target.value || null)}
          >
            <option value="">Todas as lojas (só leitura)</option>
            {stores.map(store => (
              <option key={store.id} value={store.id}>
                {store.parent_name ? `${store.parent_name} — ${store.name}` : store.name}
              </option>
            ))}
          </select>
        </div>
      )}
      <nav className="sidebar-nav">
        <NavGroup
          label={groupLabel}
          icon={isSuperadmin ? 'car' : 'plus'}
          items={groupItems}
          page={page}
          setPage={setPage}
          startOpen={isSuperadmin}
        />

        {navItems.map(item => (
          <button
            key={item.id}
            className={`nav-item ${page === item.id ? 'active' : ''}`}
            onClick={() => setPage(item.id)}
          >
            <Icon name={item.icon} size={18} />
            <span>{item.label}</span>
            {/* Mensagem nova precisa aparecer FORA do inbox: sem isto o cliente
                responde e ninguém percebe, a menos que a tela certa esteja
                aberta. O App abastece o contador com um polling próprio. */}
            {item.id === 'conversations' && unreadCount > 0 && (
              <span className="nav-unread">{unreadCount > 99 ? '99+' : unreadCount}</span>
            )}
          </button>
        ))}

        <NavGroup
          label="Relatórios"
          icon="report"
          items={reportItems}
          page={page}
          setPage={setPage}
        />

        {tailItems.map(item => (
          <button
            key={item.id}
            className={`nav-item ${page === item.id ? 'active' : ''}`}
            onClick={() => setPage(item.id)}
          >
            <Icon name={item.icon} size={18} />
            <span>{item.label}</span>
          </button>
        ))}
      </nav>
      <div className="sidebar-footer">
        <div className="user-info">
          <div className="user-avatar">{user?.name?.charAt(0)}</div>
          <div>
            <div className="user-name">{user?.name}</div>
            <div className="user-role">{ROLE_LABELS[user?.role] || 'Vendedor'}</div>
          </div>
        </div>
        <button className="btn-icon" onClick={onLogout} title="Sair">
          <Icon name="logout" size={18} />
        </button>
      </div>
    </aside>
  );
}

// ─── DASHBOARD ────────────────────────────────────────────────────────────────
function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filtro do cartão de desempenho: vive à parte do resto do dashboard, para
  // trocar o período não recarregar a tela inteira.
  const [sellers, setSellers] = useState([]);
  const [selectedSellers, setSelectedSellers] = useState([]);
  const [range, setRange] = useState({ from: '', to: '' });
  const [performance, setPerformance] = useState(null);
  const [loadingPerformance, setLoadingPerformance] = useState(true);
  // Quais métricas o gráfico de desempenho mostra. As duas ligadas = comparativo.
  const [metrics, setMetrics] = useState({ visits: true, sales: true });

  // Período próprio da rosca de status, independente do filtro de desempenho.
  const [statusRange, setStatusRange] = useState({ from: '', to: '' });
  const [statusRows, setStatusRows] = useState(null);

  useEffect(() => {
    dashboardApi.getStats()
      .then(res => setStats(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    sellersApi.list({ include_inactive: true })
      .then(res => setSellers(res.data))
      .catch(() => setSellers([]));
  }, []);

  const loadPerformance = useCallback(async () => {
    setLoadingPerformance(true);
    try {
      const res = await dashboardApi.getSellerPerformance({
        seller_ids: selectedSellers.join(',') || undefined,
        from: range.from || undefined,
        to: range.to || undefined,
      });
      setPerformance(res.data.sellers);
    } catch (e) {
      console.error(e);
      setPerformance([]);
    } finally {
      setLoadingPerformance(false);
    }
  }, [selectedSellers, range.from, range.to]);

  useEffect(() => { loadPerformance(); }, [loadPerformance]);

  // Só vai ao servidor quando há período escolhido; sem filtro, a rosca usa os
  // 30 dias que já vieram em /stats, evitando uma chamada repetida na abertura.
  useEffect(() => {
    if (!statusRange.from && !statusRange.to) {
      setStatusRows(null);
      return;
    }
    dashboardApi.getVisitsByStatus({
      from: statusRange.from || undefined,
      to: statusRange.to || undefined,
    })
      .then(res => setStatusRows(res.data.statuses))
      .catch(() => setStatusRows([]));
  }, [statusRange.from, statusRange.to]);

  const toggleSeller = (id) => {
    setSelectedSellers(current =>
      current.includes(id) ? current.filter(s => s !== id) : [...current, id]
    );
  };

  const clearFilter = () => {
    setSelectedSellers([]);
    setRange({ from: '', to: '' });
  };

  const bySeller = performance || [];
  const filterActive = selectedSellers.length > 0 || range.from || range.to;

  const cards = stats ? [
    { label: 'Visitas (30 dias)', value: stats.total_visits, icon: 'car', color: 'blue' },
    { label: 'Follow-ups Enviados', value: stats.followups_sent, icon: 'whatsapp', color: 'green' },
    { label: 'Conversas Ativas', value: stats.active_conversations, icon: 'chat', color: 'purple' },
    { label: 'Conversões', value: stats.conversions, icon: 'check', color: 'amber' },
  ] : [];

  const topVehicles = stats?.top_vehicles || [];

  const dailySeries = (stats?.daily_visits || []).map(d => ({
    // T12:00 evita o susto do fuso: "2026-08-14" sozinho é meia-noite UTC, que
    // no Brasil ainda é dia 13 e faria o gráfico exibir o dia anterior.
    label: new Date(`${d.date.slice(0, 10)}T12:00`).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
    visits: Number(d.count) || 0,
    sales: Number(d.conversions) || 0,
  }));

  // Percorre os status na ordem do ciclo de vida (e não na que o banco devolveu)
  // e descarta os zerados, que só poluiriam a legenda.
  const statusSource = statusRows || stats?.by_status || [];
  const byStatus = Object.entries(VISIT_STATUS_LABELS)
    .map(([key, label]) => ({
      label,
      count: statusSource.find(s => s.status === key)?.count || 0,
    }))
    .filter(s => s.count > 0);

  return (
    <div className="page">
      <div className="page-header">
        <h1>Dashboard</h1>
        <p>Visão geral dos últimos 30 dias</p>
      </div>
      {loading ? <SkeletonDashboard /> : (
        <>
          <div className="stats-grid">
            {cards.map(card => (
              <div key={card.label} className={`stat-card stat-card--${card.color}`}>
                <div className="stat-icon"><Icon name={card.icon} size={24} /></div>
                <div className="stat-value">{card.value}</div>
                <div className="stat-label">{card.label}</div>
              </div>
            ))}
          </div>
          <div className="chart-card">
            <div className="chart-card-head">
              <h3>Visitas por dia</h3>
              <p>Visitas recebidas e vendas fechadas nas últimas 2 semanas.</p>
            </div>
            {dailySeries.length === 0 ? (
              <div className="chart-empty">Nenhuma visita nas últimas 2 semanas.</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={dailySeries} margin={{ top: 8, right: 16, bottom: 4, left: -16 }}>
                  <CartesianGrid vertical={false} stroke={CHART_INK.grid} />
                  <XAxis dataKey="label" stroke={CHART_INK.muted} fontSize={12} />
                  <YAxis allowDecimals={false} stroke={CHART_INK.muted} fontSize={12} />
                  <Tooltip {...chartTooltipStyle} />
                  <Legend formatter={legendLabel} wrapperStyle={{ fontSize: 12 }} />
                  {/* Marcador de 8px e linha de 2px: pontos menores somem no
                      fundo escuro e ficam difíceis de acertar com o mouse. */}
                  <Line
                    type="monotone"
                    dataKey="visits"
                    name="Visitas"
                    stroke={LINE_COLORS.visits}
                    strokeWidth={2}
                    dot={{ r: 4, strokeWidth: 2 }}
                    activeDot={{ r: 5 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="sales"
                    name="Vendas"
                    stroke={LINE_COLORS.sales}
                    strokeWidth={2}
                    dot={{ r: 4, strokeWidth: 2 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="chart-grid">
            <ChartCard
              title="Desempenho por vendedor"
              hint={
                range.from || range.to
                  ? `Visitas e conversões de ${range.from ? new Date(`${range.from}T12:00`).toLocaleDateString('pt-BR') : 'o início'} até ${range.to ? new Date(`${range.to}T12:00`).toLocaleDateString('pt-BR') : 'hoje'}.`
                  : 'Visitas recebidas e quantas viraram venda, nos últimos 30 dias.'
              }
              isEmpty={!loadingPerformance && bySeller.length === 0}
              emptyText={
                filterActive
                  ? 'Nenhum vendedor corresponde ao filtro. Ajuste o período ou a seleção.'
                  : 'Nenhum vendedor cadastrado ainda. Cadastre a equipe em Cadastro › Usuários (perfil Vendedor).'
              }
              filter={
                <div className="chart-filter">
                  <div className="chart-filter-row">
                    <label>
                      <span>De</span>
                      <input
                        type="date"
                        value={range.from}
                        max={range.to || undefined}
                        onChange={e => setRange(r => ({ ...r, from: e.target.value }))}
                      />
                    </label>
                    <label>
                      <span>Até</span>
                      <input
                        type="date"
                        value={range.to}
                        min={range.from || undefined}
                        onChange={e => setRange(r => ({ ...r, to: e.target.value }))}
                      />
                    </label>
                    {filterActive && (
                      <button className="btn btn-secondary btn-sm" onClick={clearFilter}>Limpar</button>
                    )}
                  </div>
                  {/* Multisseleção por botão em vez de <select multiple>: com
                      select múltiplo, um clique desmarca todo o resto sem aviso. */}
                  <div className="chart-filter-chips">
                    {sellers.length === 0
                      ? <span className="chart-filter-empty">Nenhum vendedor cadastrado</span>
                      : sellers.map(s => (
                        <button
                          key={s.id}
                          type="button"
                          className={`chip${selectedSellers.includes(s.id) ? ' chip--on' : ''}`}
                          onClick={() => toggleSeller(s.id)}
                          aria-pressed={selectedSellers.includes(s.id)}
                        >
                          {s.name}
                        </button>
                      ))}
                  </div>
                  {/* Métrica exibida. Desligar as duas deixaria um gráfico sem
                      barra nenhuma, então a última ligada não pode ser desligada. */}
                  <div className="chart-filter-chips">
                    <button
                      type="button"
                      className={`chip${metrics.visits ? ' chip--on' : ''}`}
                      onClick={() => setMetrics(m => (m.sales ? { ...m, visits: !m.visits } : m))}
                      aria-pressed={metrics.visits}
                    >
                      Atendimento
                    </button>
                    <button
                      type="button"
                      className={`chip${metrics.sales ? ' chip--on' : ''}`}
                      onClick={() => setMetrics(m => (m.visits ? { ...m, sales: !m.sales } : m))}
                      aria-pressed={metrics.sales}
                    >
                      Venda
                    </button>
                  </div>
                  {selectedSellers.length > 0 && (
                    <div className="chart-filter-note">
                      Comparando {selectedSellers.length} vendedor{selectedSellers.length > 1 ? 'es' : ''} — clique de novo para tirar da comparação.
                    </div>
                  )}
                </div>
              }
            >
              <ResponsiveContainer width="100%" height={340}>
                <BarChart data={bySeller} margin={{ top: 16, right: 8, bottom: 48, left: -16 }}>
                  <CartesianGrid vertical={false} stroke={CHART_INK.grid} />
                  {/* interval={0} força todo nome a aparecer, e a inclinação
                      evita o que acontecia sem ela: "Ana Beatriz RamosCarlos
                      Eduardo" emendado num borrão. O nome inteiro fica no
                      tooltip. */}
                  <XAxis
                    dataKey="name"
                    stroke={CHART_INK.muted}
                    fontSize={12}
                    interval={0}
                    angle={-30}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis allowDecimals={false} stroke={CHART_INK.muted} fontSize={12} />
                  <Tooltip {...chartTooltipStyle} />
                  {/* Legenda no topo: embaixo ela cruzava com os nomes
                      inclinados do eixo, e os dois viravam um emaranhado. */}
                  <Legend formatter={legendLabel} verticalAlign="top" wrapperStyle={{ fontSize: 12, paddingBottom: 8 }} />
                  {/* radius só no topo da barra: a base fica encostada no eixo */}
                  {metrics.visits && (
                  <Bar dataKey="visits" name="Atendimento" fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} maxBarSize={46}>
                    <LabelList dataKey="visits" position="top" fill={CHART_INK.muted} fontSize={12} />
                  </Bar>
                  )}
                  {metrics.sales && (
                  <Bar dataKey="conversions" name="Venda" fill={CHART_COLORS[2]} radius={[4, 4, 0, 0]} maxBarSize={46}>
                    <LabelList dataKey="conversions" position="top" fill={CHART_INK.muted} fontSize={12} />
                  </Bar>
                  )}
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard
              title="Visitas por status"
              hint={
                statusRange.from || statusRange.to
                  ? `De ${statusRange.from ? new Date(`${statusRange.from}T12:00`).toLocaleDateString('pt-BR') : 'o início'} até ${statusRange.to ? new Date(`${statusRange.to}T12:00`).toLocaleDateString('pt-BR') : 'hoje'}.`
                  : 'Onde está cada visita dos últimos 30 dias.'
              }
              isEmpty={byStatus.length === 0}
              emptyText="Sem visitas no período."
              filter={
                <div className="chart-filter">
                  <div className="chart-filter-row">
                    <label>
                      <span>De</span>
                      <input
                        type="date"
                        value={statusRange.from}
                        max={statusRange.to || undefined}
                        onChange={e => setStatusRange(r => ({ ...r, from: e.target.value }))}
                      />
                    </label>
                    <label>
                      <span>Até</span>
                      <input
                        type="date"
                        value={statusRange.to}
                        min={statusRange.from || undefined}
                        onChange={e => setStatusRange(r => ({ ...r, to: e.target.value }))}
                      />
                    </label>
                    {(statusRange.from || statusRange.to) && (
                      <button className="btn btn-secondary btn-sm" onClick={() => setStatusRange({ from: '', to: '' })}>
                        Limpar
                      </button>
                    )}
                  </div>
                </div>
              }
            >
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  {/* Sem rótulo externo: "Follow-up pendente: 13" ao redor de uma
                      rosca em card estreito sai cortado nas bordas. Nome e número
                      vão na legenda, que tem largura para os dois. */}
                  <Pie
                    data={byStatus}
                    dataKey="count"
                    nameKey="label"
                    innerRadius={58}
                    outerRadius={92}
                    paddingAngle={2}
                    stroke="none"
                  >
                    {byStatus.map((s, i) => (
                      <Cell key={s.label} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip {...chartTooltipStyle} />
                  <Legend formatter={legendLabelWithCount} wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard
              wide
              title="Motivos de perda"
              hint="Por que as visitas perdidas dos últimos 30 dias não viraram venda. O motivo é informado ao marcar a visita como perdida, na tela de Visitas."
              isEmpty={(stats?.lost_reasons || []).length === 0}
              emptyText="Nenhuma visita perdida nos últimos 30 dias — ou nenhuma com o motivo informado ainda."
            >
              <ResponsiveContainer width="100%" height={220}>
                <BarChart
                  data={(stats?.lost_reasons || []).map(r => ({ ...r, label: LOST_REASON_LABELS[r.reason] || r.reason }))}
                  layout="vertical"
                  margin={{ top: 4, right: 40, bottom: 4, left: 8 }}
                >
                  <CartesianGrid horizontal={false} stroke={CHART_INK.grid} />
                  <XAxis type="number" allowDecimals={false} stroke={CHART_INK.muted} fontSize={12} />
                  <YAxis type="category" dataKey="label" width={170} stroke={CHART_INK.muted} fontSize={12} />
                  <Tooltip {...chartTooltipStyle} />
                  {/* Série única, sem legenda — o título já diz o que a barra mede. */}
                  <Bar dataKey="count" name="Visitas perdidas" fill={CHART_COLORS[1]} radius={[0, 4, 4, 0]} barSize={14}>
                    <LabelList dataKey="count" position="right" fill={CHART_INK.muted} fontSize={12} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard
              wide
              title="Veículos mais procurados"
              hint="Os 5 mais citados no campo de interesse, nos últimos 30 dias."
              isEmpty={topVehicles.length === 0}
              emptyText="Nenhum veículo informado ainda. O campo “interesse” da visita alimenta este gráfico."
            >
              <ResponsiveContainer width="100%" height={Math.max(180, topVehicles.length * 42 + 40)}>
                <BarChart data={topVehicles} layout="vertical" margin={{ top: 4, right: 40, bottom: 4, left: 8 }}>
                  <CartesianGrid horizontal={false} stroke={CHART_INK.grid} />
                  <XAxis type="number" allowDecimals={false} stroke={CHART_INK.muted} fontSize={12} />
                  <YAxis type="category" dataKey="vehicle" width={140} stroke={CHART_INK.muted} fontSize={12} />
                  <Tooltip {...chartTooltipStyle} />
                  {/* Série única: o título já diz o que a barra mede, então não
                      entra legenda — seria uma linha repetindo o cabeçalho. */}
                  <Bar dataKey="count" name="Visitas" fill={CHART_COLORS[0]} radius={[0, 4, 4, 0]} barSize={14}>
                    <LabelList dataKey="count" position="right" fill={CHART_INK.muted} fontSize={12} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
        </>
      )}
    </div>
  );
}

// ─── NOVA VISITA ──────────────────────────────────────────────────────────────
// ─── Rascunho offline de visita ───────────────────────────────────────────────
// Sem conexão, o formulário de Nova Visita guarda a visita NESTE aparelho e o
// painel a cadastra sozinho quando a rede volta. localStorage em vez de
// IndexedDB: são poucos formulários pequenos, e o valor sobrevive a refresh e a
// fechar o navegador.
const VISIT_DRAFTS_KEY = 'crm_visit_drafts';
// Aviso para as telas re-lerem a fila (rascunho salvo, enviado ou descartado).
const DRAFTS_EVENT = 'crm:visit-drafts';

function readVisitDrafts() {
  try { return JSON.parse(localStorage.getItem(VISIT_DRAFTS_KEY)) || []; }
  catch { return []; }
}

function writeVisitDrafts(drafts) {
  if (drafts.length) localStorage.setItem(VISIT_DRAFTS_KEY, JSON.stringify(drafts));
  else localStorage.removeItem(VISIT_DRAFTS_KEY);
  window.dispatchEvent(new Event(DRAFTS_EVENT));
}

function saveVisitDraft(form) {
  const draft = {
    // O id vai ao backend como chave de idempotência: se o reenvio se repetir
    // (a requisição chegou e a resposta se perdeu na rede instável), o servidor
    // reconhece o rascunho e não duplica a visita.
    id: typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `d${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
    // A loja é a do momento da captura: se o usuário trocar a loja ativa antes
    // de a rede voltar, a visita ainda entra onde de fato aconteceu.
    organization_id: getActiveStoreId() || null,
    form,
    saved_at: new Date().toISOString(),
  };
  writeVisitDrafts([...readVisitDrafts(), draft]);
  return draft;
}

function discardVisitDraft(id) {
  writeVisitDrafts(readVisitDrafts().filter(d => d.id !== id));
}

// Uma leva por vez: o listener de 'online' e o botão "Enviar agora" podem
// disparar juntos, e duas levas reenviariam os mesmos rascunhos em paralelo.
let flushingDrafts = false;

/**
 * Tenta cadastrar os rascunhos pendentes, um a um.
 *
 * - Sucesso → sai da fila.
 * - Servidor recusou (4xx: telefone inválido, vendedor removido da loja…) → fica
 *   na fila MARCADO com o erro e fora das tentativas automáticas — reenviar dado
 *   recusado para sempre não conserta nada; a pessoa corrige ou descarta na tela.
 * - Rede fora ou 5xx → fica como está e a leva PARA: se a conexão caiu de novo,
 *   os demais rascunhos também vão falhar.
 */
async function flushVisitDrafts({ includeFailed = false } = {}) {
  if (flushingDrafts) return;
  flushingDrafts = true;
  try {
    for (const draft of readVisitDrafts()) {
      if (draft.error && !includeFailed) continue;
      try {
        await visitsApi.create({ ...draft.form, client_request_id: draft.id }, draft.organization_id);
        discardVisitDraft(draft.id);
      } catch (err) {
        if (err.response && err.response.status < 500) {
          writeVisitDrafts(readVisitDrafts().map(d =>
            d.id === draft.id
              ? { ...d, error: err.response.data?.error || 'O servidor recusou este cadastro.' }
              : d
          ));
        } else {
          break;
        }
      }
    }
  } finally {
    flushingDrafts = false;
  }
}

// Cada formulário recebe a própria lista: um objeto compartilhado faria o
// repetidor de veículos de um cadastro vazar para o próximo.
const emptyVisitForm = () => ({ full_name: '', cpf: '', phone: '', email: '', vehicles: [''], seller_id: '', notes: '' });

/** Lista limpa para enviar: sem vazios e sem espaços nas pontas. */
const cleanVehicles = (list) => (list || []).map(v => String(v).trim()).filter(Boolean);

/**
 * Veículos de uma visita, para exibir: a lista nova ou, nas visitas anteriores
 * a ela (e nos rascunhos offline guardados antes desta versão), o campo antigo.
 */
const visitVehicles = (v) => (v?.vehicles?.length ? v.vehicles : [v?.vehicle_interest].filter(Boolean));

/**
 * Repetidor de veículos de interesse: um campo por veículo. Quem chega em
 * dúvida entre dois ou três modelos é o caso comum, e um campo só obrigava o
 * atendente a inventar um separador. O follow-up menciona todos.
 */
const OTHER_VEHICLE = '__outro__';

/**
 * Veículos de interesse da visita. Desde 24/09/2026 (pedido do usuário) cada
 * linha é um SELECT do catálogo da loja, e não um campo de texto: o nome fica
 * igual ao do cadastro (o que a IA busca e o que o dashboard conta), sem
 * variações de digitação. "Outro veículo" abre o texto livre para o carro que
 * a loja não tem em estoque. O que vai ao backend continua sendo a lista de
 * textos (`vehicles`), então nada muda na API nem nos rascunhos offline — e se
 * o catálogo não carregar (sem rede), o campo volta a ser texto.
 */
function VehiclesField({ vehicles, onChange, required = false }) {
  const [catalog, setCatalog] = useState(null); // null = carregando; [] = vazio
  const [failed, setFailed] = useState(false);
  const [custom, setCustom] = useState({}); // índice → true quando "Outro veículo" foi escolhido

  useEffect(() => {
    vehiclesApi.list()
      .then(res => setCatalog((res.data || []).filter(v => v.active !== false)))
      .catch(() => { setFailed(true); setCatalog([]); });
  }, []);

  const list = vehicles.length ? vehicles : [''];
  const labels = (catalog || []).map(describeVehicleRow);
  const useText = failed || (catalog && catalog.length === 0);
  const change = (index, value) => onChange(list.map((v, i) => (i === index ? value : v)));
  const add = () => onChange([...list, '']);
  const remove = (index) => {
    setCustom(c => { const next = {}; Object.keys(c).forEach(k => { const i = Number(k); if (i < index) next[i] = c[k]; else if (i > index) next[i - 1] = c[k]; }); return next; });
    onChange(list.length > 1 ? list.filter((_, i) => i !== index) : ['']);
  };
  const pick = (index, picked) => {
    if (picked === OTHER_VEHICLE) { setCustom(c => ({ ...c, [index]: true })); change(index, ''); return; }
    setCustom(c => ({ ...c, [index]: false }));
    change(index, picked);
  };

  return (
    <>
      {list.map((value, index) => {
        const inCatalog = labels.includes(value);
        // Texto que não está no catálogo (visita antiga, ou "Outro") continua
        // editável como texto, em vez de sumir num select sem a opção.
        const isCustom = useText || custom[index] || (Boolean(value) && !inCatalog);
        return (
          <div key={index} className="vehicle-row">
            {!useText && (
              <select
                value={isCustom ? OTHER_VEHICLE : value}
                onChange={e => pick(index, e.target.value)}
                required={required && index === 0 && !isCustom}
                disabled={catalog === null}
              >
                <option value="">{catalog === null ? 'Carregando o catálogo…' : index === 0 ? 'Escolha o veículo do catálogo…' : 'Outro veículo de interesse…'}</option>
                {labels.map((label, i) => <option key={catalog[i].id} value={label}>{label}</option>)}
                <option value={OTHER_VEHICLE}>Outro veículo (digitar)…</option>
              </select>
            )}
            {isCustom && (
              <input
                value={value}
                onChange={e => change(index, e.target.value)}
                placeholder={useText ? (index === 0 ? 'Ex: Toyota Corolla 2024 XEi' : 'Outro veículo de interesse') : 'Marca, modelo e ano'}
                required={required && index === 0}
                autoFocus={!useText}
              />
            )}
            {list.length > 1 && (
              <button type="button" className="btn-icon" title="Remover este veículo" onClick={() => remove(index)}>
                <Icon name="x" size={16} />
              </button>
            )}
          </div>
        );
      })}
      <button type="button" className="btn btn-secondary btn-sm" onClick={add}>
        <Icon name="plus" size={14} /> Adicionar outro veículo
      </button>
      {failed && <div className="field-hint">O catálogo não carregou; digite o veículo.</div>}
    </>
  );
}

function NewVisitPage({ hasStore = true }) {
  const [sellers, setSellers] = useState([]);
  const [sellersFailed, setSellersFailed] = useState(false);
  const [form, setForm] = useState(emptyVisitForm);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');
  const [drafts, setDrafts] = useState(readVisitDrafts);
  const [savedDraft, setSavedDraft] = useState(false);

  // A fila muda por fora da tela (a conexão volta e o App envia sozinho):
  // re-lê no aviso para o painel de pendências encolher na hora.
  useEffect(() => {
    const refresh = () => setDrafts(readVisitDrafts());
    window.addEventListener(DRAFTS_EVENT, refresh);
    return () => window.removeEventListener(DRAFTS_EVENT, refresh);
  }, []);

  // A falha precisa aparecer e ter botão de repetir: a lista carrega uma vez no
  // mount, e um catch silencioso deixava o select vazio até sair e voltar à tela
  // — parecia que a loja não tinha vendedor quando a API só estava reiniciando
  // (todo deploy derruba o backend por um instante).
  const loadSellers = () => {
    setSellersFailed(false);
    sellersApi.list()
      .then(res => setSellers(res.data))
      .catch(() => setSellersFailed(true));
  };
  useEffect(loadSellers, []);

  const formatCPF = (val) => {
    const n = val.replace(/\D/g, '').slice(0, 11);
    return n.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
            .replace(/(\d{3})(\d{3})(\d{3})/, '$1.$2.$3')
            .replace(/(\d{3})(\d{3})/, '$1.$2')
            .replace(/(\d{3})/, '$1');
  };

  // A máscara sai do TAMANHO, num passo só. A versão anterior encadeava
  // replaces como fallback de dígito parcial, e o último (`/(\d{2})/` → `($1`)
  // rodava sobre a string JÁ formatada: "(81) ..." virava "((81) ...".
  const formatPhone = (val) => {
    const n = val.replace(/\D/g, '').slice(0, 11);
    if (n.length === 0) return '';
    if (n.length <= 2) return `(${n}`;
    const prefixEnd = n.length <= 10 ? 6 : 7;
    if (n.length <= prefixEnd) return `(${n.slice(0, 2)}) ${n.slice(2)}`;
    return `(${n.slice(0, 2)}) ${n.slice(2, prefixEnd)}-${n.slice(prefixEnd)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(null);
    setSavedDraft(false);
    const payload = { ...form, vehicles: cleanVehicles(form.vehicles) };
    try {
      const res = await visitsApi.create(payload);
      setSuccess(res.data);
      setForm(emptyVisitForm());
    } catch (err) {
      // Sem resposta = a requisição não chegou (sem internet, API reiniciando).
      // O preenchimento não pode se perder por isso: vira rascunho local, e o
      // painel cadastra sozinho quando a conexão voltar. Erro COM resposta é
      // outra história — o servidor recusou o dado, e guardar não conserta.
      if (!err.response) {
        saveVisitDraft(payload);
        setForm(emptyVisitForm());
        setSavedDraft(true);
      } else {
        setError(err.response.data?.error || 'Erro ao cadastrar visita');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1>Nova Visita</h1>
        <p>Cadastre os dados do cliente que está visitando a loja</p>
      </div>

      {/* A visita nasce em uma loja: sem loja escolhida o backend recusaria o
          cadastro, e avisar antes evita o usuário preencher o formulário inteiro
          para só então descobrir. */}
      {!hasStore && (
        <div className="alert alert-error">
          <Icon name="x" size={18} />
          <div>
            Escolha a <strong className="inline">loja ativa</strong> no menu à esquerda antes de
            cadastrar a visita — é nela que o cliente entrou.
          </div>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <Icon name="check" size={18} />
          <div>
            <strong>Visita cadastrada com sucesso!</strong>
            <p>{success.message}</p>
          </div>
        </div>
      )}
      {error && <div className="alert alert-error">{error}</div>}

      {savedDraft && (
        <div className="alert alert-success">
          <Icon name="clock" size={18} />
          <div>
            <strong>Sem conexão com o sistema agora.</strong>
            <p>A visita foi guardada neste aparelho e será cadastrada automaticamente quando a conexão voltar.</p>
          </div>
        </div>
      )}

      {/* Visitas guardadas offline. Cada uma vai para a loja onde foi capturada,
          mesmo que a loja ativa tenha mudado depois. */}
      {drafts.length > 0 && (
        <div className="form-card" style={{ marginBottom: 24 }}>
          <div className="form-section">
            <h3><Icon name="clock" size={16} /> Aguardando conexão ({drafts.length})</h3>
            <p className="field-hint" style={{ marginBottom: 12 }}>
              Guardadas neste aparelho. Serão cadastradas sozinhas quando a conexão voltar — ou clique em Enviar agora.
            </p>
            {drafts.map(d => (
              <div key={d.id} className="field" style={{ marginBottom: 10 }}>
                <div>
                  <strong>{d.form.full_name}</strong>
                  {' — '}{visitVehicles(d.form).join(', ')}
                  <span className="field-hint" style={{ display: 'inline', marginLeft: 8 }}>
                    salva em {stampDateTime(d.saved_at)}
                  </span>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    style={{ marginLeft: 12 }}
                    onClick={() => { if (window.confirm(`Descartar o cadastro de ${d.form.full_name}?`)) discardVisitDraft(d.id); }}
                  >
                    Descartar
                  </button>
                </div>
                {d.error && (
                  <div className="field-hint field-hint--error">
                    O servidor recusou este cadastro: {d.error} Corrija cadastrando de novo, ou descarte.
                  </div>
                )}
              </div>
            ))}
            <button type="button" className="btn btn-primary btn-sm" onClick={() => flushVisitDrafts({ includeFailed: true })}>
              <Icon name="send" size={14} /> Enviar agora
            </button>
          </div>
        </div>
      )}

      <div className="form-card">
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3><Icon name="user" size={16} /> Dados do Cliente</h3>
            <div className="form-grid">
              <div className="field field--full">
                <label>Nome Completo *</label>
                <input value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} placeholder="João da Silva" required />
              </div>
              <div className="field">
                <label>CPF *</label>
                <input value={form.cpf} onChange={e => setForm(f => ({ ...f, cpf: formatCPF(e.target.value) }))} placeholder="000.000.000-00" required />
              </div>
              <div className="field">
                <label>Telefone/WhatsApp *</label>
                <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: formatPhone(e.target.value) }))} placeholder="(81) 99999-9999" required />
              </div>
              <div className="field field--full">
                <label>E-mail</label>
                <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="joao@email.com" />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3><Icon name="car" size={16} /> Informações da Visita</h3>
            <div className="form-grid">
              <div className="field field--full">
                <label>Veículos de Interesse *</label>
                <VehiclesField vehicles={form.vehicles} onChange={vehicles => setForm(f => ({ ...f, vehicles }))} required />
                <div className="field-hint">Cliente em dúvida entre modelos? Informe um por linha — o follow-up menciona todos.</div>
              </div>
              <div className="field">
                <label>Vendedor Responsável</label>
                <select value={form.seller_id} onChange={e => setForm(f => ({ ...f, seller_id: e.target.value }))}>
                  <option value="">Selecione um vendedor</option>
                  {sellers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                {sellersFailed && (
                  <div className="field-hint field-hint--error">
                    Não foi possível carregar os vendedores.{' '}
                    <button type="button" className="btn btn-secondary btn-sm" onClick={loadSellers}>
                      Tentar de novo
                    </button>
                  </div>
                )}
                {!sellersFailed && sellers.length === 0 && (
                  <div className="field-hint">
                    Nenhum vendedor ativo nesta loja — cadastre em Cadastro › Usuários (perfil Vendedor).
                  </div>
                )}
              </div>
              <div className="field field--full">
                <label>Observações</label>
                <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Anotações sobre o atendimento..." rows={3} />
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary btn-lg" disabled={loading || !hasStore}>
              <Icon name="calendar" size={18} />
              {loading ? 'Cadastrando...' : 'Cadastrar Visita'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── LISTA DE VISITAS ─────────────────────────────────────────────────────────
// O vendedor recebe do backend só as visitas dele (services/sellerScope.js);
// `isSeller` aqui só troca os textos, para a lista curta não parecer defeito.
function VisitsPage({ isAdmin = false, isSeller = false }) {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [total, setTotal] = useState(0);
  // Paginação: 50 por página de saída, com a opção de subir para 100.
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [sellers, setSellers] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [firingId, setFiringId] = useState(null);
  const [feedback, setFeedback] = useState(null);
  // Ficha do cliente, expandida abaixo da linha clicada (mesmo padrão da
  // edição). Ancorada na VISITA, não no cliente: o mesmo cliente pode ter duas
  // linhas na lista, e a ficha abre embaixo da que foi clicada.
  const [profileVisitId, setProfileVisitId] = useState(null);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const removeClient = async (client) => {
    const first = String(client.full_name || '').split(' ')[0];
    const typed = window.prompt(
      `Isto apaga ${client.full_name} desta loja com TODAS as visitas, conversas, mensagens, propostas e test-drives. Não dá para desfazer.

Para confirmar, digite o primeiro nome: ${first}`
    );
    if (typed === null) return;
    if (typed.trim().toLowerCase() !== first.toLowerCase()) {
      setFeedback({ type: 'error', text: 'Nome diferente — nada foi apagado.' });
      return;
    }
    try {
      const res = await clientsApi.remove(client.id);
      const d = res.data;
      setProfileVisitId(null);
      setProfile(null);
      setFeedback({ type: 'success', text: `${d.full_name} excluído: ${d.visits} visita(s), ${d.conversations} conversa(s) e ${d.test_drives} test-drive(s) apagados. Uma nova visita recria o cadastro pelo telefone.` });
      load();
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.error || 'Erro ao excluir o cliente' });
    }
  };

  const toggleProfile = async (visit) => {
    if (profileVisitId === visit.id) { setProfileVisitId(null); setProfile(null); return; }
    setProfileVisitId(visit.id);
    setProfile(null);
    setProfileLoading(true);
    try {
      const res = await clientsApi.profile(visit.client_id);
      setProfile(res.data);
    } catch (err) {
      setProfileVisitId(null);
      setFeedback({ type: 'error', text: apiErrorMessage(err, 'Erro ao carregar a ficha do cliente') });
    } finally {
      setProfileLoading(false);
    }
  };

  // O texto é o desta tela (fala do cliente: "Convertido"); a cor vem do mapa
  // comum, para a etiqueta não mudar de cor entre esta tela e os relatórios.
  const statusLabels = {
    active: { label: 'Ativa', color: VISIT_STATUS_TONE.active },
    followup_pending: { label: 'Follow-up Pendente', color: VISIT_STATUS_TONE.followup_pending },
    followup_sent: { label: 'Follow-up Enviado', color: VISIT_STATUS_TONE.followup_sent },
    converted: { label: 'Convertido', color: VISIT_STATUS_TONE.converted },
    lost: { label: 'Perdido', color: VISIT_STATUS_TONE.lost },
  };

  // Quem administra uma rede vê as visitas de várias lojas na mesma lista. A loja
  // só aparece quando há mais de uma: para um admin de loja seria a mesma palavra
  // repetida em toda linha.
  const showStore = new Set(visits.map(v => v.organization_id)).size > 1;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await visitsApi.list({
        search,
        status: statusFilter,
        limit: pageSize,
        offset: page * pageSize,
      });
      setVisits(res.data.visits);
      setTotal(res.data.total);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, page, pageSize]);

  // Trocar busca, filtro ou tamanho de página volta para a primeira: a página 3
  // de uma busca não existe na busca seguinte.
  useEffect(() => { setPage(0); }, [search, statusFilter, pageSize]);

  // Os 350ms seguem o padrão dos relatórios: digitar "Thiago" na busca mandava
  // seis consultas ao servidor, cinco delas de prefixos que ninguém pediu.
  useEffect(() => {
    const timer = setTimeout(load, 350);
    return () => clearTimeout(timer);
  }, [load]);

  // Vendedores inativos entram na lista: uma visita antiga pode estar com um
  // deles, e o select precisa conseguir exibir o valor atual.
  useEffect(() => {
    sellersApi.list({ include_inactive: true })
      .then(res => setSellers(res.data))
      .catch(() => setSellers([]));
  }, []);

  const startEdit = (visit) => {
    setEditingId(visit.id);
    setFeedback(null);
    setEditForm({
      full_name: visit.full_name || '',
      cpf: visit.cpf || '',
      email: visit.email || '',
      vehicles: visitVehicles(visit),
      seller_id: visit.seller_id ? String(visit.seller_id) : '',
      status: visit.status,
      lost_reason: visit.lost_reason || '',
      notes: visit.notes || '',
      source: visit.source || 'manual',
    });
  };

  const cancelEdit = () => { setEditingId(null); setEditForm(null); };

  // Exporta com os MESMOS filtros da tela, mas pedindo até 500 linhas (o teto
  // do backend) em vez da página atual — exportar só a página visível seria
  // uma pegadinha para quem vai somar a planilha.
  const exportCsv = async () => {
    try {
      const res = await visitsApi.list({ search, status: statusFilter, limit: 500, offset: 0 });
      const rows = res.data.visits.map(v => [
        v.full_name,
        v.cpf || '',
        v.phone,
        v.email || '',
        v.vehicle_interest || '',
        v.seller_name || '',
        v.store_name || '',
        new Date(v.visit_date).toLocaleDateString('pt-BR'),
        statusLabels[v.status]?.label || v.status,
        v.status === 'lost' ? (LOST_REASON_LABELS[v.lost_reason] || '') : '',
      ]);
      downloadCsv(
        `visitas-${new Date().toISOString().slice(0, 10)}.csv`,
        ['Cliente', 'CPF', 'Telefone', 'E-mail', 'Veículo de interesse', 'Vendedor', 'Loja', 'Data', 'Status', 'Motivo da perda'],
        rows
      );
      setFeedback({
        type: 'success',
        text: res.data.total > rows.length
          ? `CSV exportado com as ${rows.length} visitas mais recentes (de ${res.data.total} no total — refine a busca para exportar as demais).`
          : `CSV exportado com ${rows.length} visita${rows.length === 1 ? '' : 's'}.`,
      });
    } catch {
      setFeedback({ type: 'error', text: 'Erro ao exportar o CSV' });
    }
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    setFeedback(null);
    try {
      // Quem não é admin nem manda seller_id: ausente significa "não mexi" no
      // backend (que barraria a troca com 403 de qualquer forma).
      const { seller_id, source, ...rest } = editForm;
      const vehicles = cleanVehicles(rest.vehicles);
      await visitsApi.update(editingId, {
        ...rest,
        // Lead do WhatsApp nasce sem veículo: salvar a ficha (atribuir vendedor,
        // corrigir o nome) sem preencher o veículo é "não mexi", não erro.
        ...(vehicles.length || source !== 'whatsapp' ? { vehicles } : {}),
        ...(isAdmin ? { seller_id: seller_id ? Number(seller_id) : null } : {}),
      });
      cancelEdit();
      load();
      setFeedback({ type: 'success', text: 'Visita atualizada.' });
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.error || 'Erro ao salvar a visita' });
    } finally {
      setSavingEdit(false);
    }
  };

  // Antecipa o follow-up pendente para agora. A rota só muda o agendamento —
  // quem envia continua sendo o cron do backend, na próxima varredura (até 5
  // minutos) —, por isso o aviso fala em "sai em até 5 minutos" e não em
  // "enviado". Serve para validar a integração com a Meta numa visita de teste
  // sem esperar as horas da setting followup_delay_hours.
  const fireFollowup = async (v) => {
    if (!window.confirm(`Disparar agora o follow-up de ${v.full_name} (${v.phone})? A mensagem sai em até 5 minutos.`)) return;
    setFiringId(v.id);
    setFeedback(null);
    try {
      const res = await visitsApi.sendFollowupNow(v.id);
      setFeedback({ type: 'success', text: res.data.message || 'Follow-up antecipado: sai em até 5 minutos.' });
      load();
    } catch (err) {
      setFeedback({ type: 'error', text: apiErrorMessage(err, 'Erro ao antecipar o follow-up') });
    } finally {
      setFiringId(null);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1>Visitas <span className="badge">{total}</span></h1>
        <p>{isSeller
          ? 'Seus clientes: as visitas atribuídas a você pela recepção ou pelo gerente'
          : 'Histórico de clientes que visitaram a loja'}</p>
      </div>

      {feedback && (
        <div className={`alert alert-${feedback.type}`}>
          <Icon name={feedback.type === 'success' ? 'check' : 'x'} size={18} />
          <div>{feedback.text}</div>
        </div>
      )}

      <div className="filters-bar">
        <input className="search-input" placeholder="Buscar por nome, CPF ou telefone..." value={search} onChange={e => setSearch(e.target.value)} />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">Todos os status</option>
          {Object.entries(statusLabels).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <button className="btn btn-secondary" onClick={load} title="Recarregar"><Icon name="refresh" size={16} /></button>
        <button className="btn btn-secondary" onClick={exportCsv} title="Exportar CSV com os filtros atuais">
          <Icon name="download" size={16} /> CSV
        </button>
      </div>

      {loading ? <SkeletonList rows={8} /> : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Contato</th>
                <th>Veículo de Interesse</th>
                <th>Vendedor</th>
                <th>Data</th>
                <th>Status</th>
                <th>Follow-up</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visits.length === 0 ? (
                <tr><td colSpan={8} className="empty-row">
                  {search || statusFilter ? (
                    <EmptyState
                      icon="filter"
                      title="Nenhuma visita com esse filtro"
                      actionLabel="Limpar filtros"
                      onAction={() => { setSearch(''); setStatusFilter(''); }}
                    />
                  ) : (
                    <EmptyState
                      icon="calendar"
                      title={isSeller ? 'Nenhum cliente atribuído a você ainda' : 'Nenhuma visita registrada ainda'}
                      hint={isSeller
                        ? 'Os clientes aparecem aqui quando a recepção ou o gerente passam o atendimento para você.'
                        : 'Registre a primeira em Nova Visita, no menu ao lado.'}
                    />
                  )}
                </td></tr>
              ) : visits.map(v => {
                const st = statusLabels[v.status] || { label: v.status, color: 'gray' };
                return (
                  <React.Fragment key={v.id}>
                  <tr>
                    <td data-label="Cliente">
                      <button
                        type="button"
                        className="cell-name cell-name-btn"
                        title="Ver a ficha do cliente"
                        onClick={() => toggleProfile(v)}
                      >
                        {v.full_name}
                      </button>
                      <div className="cell-sub">{v.cpf}</div>
                    </td>
                    <td data-label="Contato">
                      <div>{v.phone}</div>
                      <div className="cell-sub">{v.email}</div>
                    </td>
                    <td data-label="Veículo">
                      <div>{v.vehicle_interest || <span className="cell-sub">a definir</span>}</div>
                      {/* Lead espontâneo: escreveu no WhatsApp da loja sem visita;
                          o cadastro nasceu do webhook e pode estar incompleto. */}
                      {v.source === 'whatsapp' && <div className="cell-sub">📲 Lead do WhatsApp</div>}
                    </td>
                    <td data-label="Vendedor">
                      <div>{v.seller_name || '—'}</div>
                      {showStore && <div className="cell-sub">{v.store_name}</div>}
                    </td>
                    <td data-label="Data">{new Date(v.visit_date).toLocaleDateString('pt-BR')}</td>
                    <td data-label="Status">
                      <span className={`status-badge status-badge--${st.color}`}>{st.label}</span>
                      {v.status === 'lost' && v.lost_reason && (
                        <div className="cell-sub">{LOST_REASON_LABELS[v.lost_reason] || v.lost_reason}</div>
                      )}
                      {/* O cliente pediu para não receber mensagens automáticas
                          (bot). O follow-up desta pessoa não sai. */}
                      {v.opted_out && <div className="cell-sub">🚫 opt-out de mensagens</div>}
                    </td>
                    <td data-label="Follow-up">
                      {v.followup_sent_at
                        ? <span className="cell-sub">Enviado {new Date(v.followup_sent_at).toLocaleDateString('pt-BR')}</span>
                        : v.followup_scheduled_at
                        ? <span className="cell-sub"><Icon name="clock" size={12} /> {new Date(v.followup_scheduled_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                        : '—'}
                      {/* Só admin, só pendente e sem opt-out: antecipar uma já
                          enviada mandaria o template de novo ao cliente, e o
                          backend recusa quem pediu para não receber. */}
                      {isAdmin && v.status === 'followup_pending' && !v.followup_sent_at && !v.opted_out && (
                        <div style={{ marginTop: 4 }}>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => fireFollowup(v)}
                            disabled={firingId === v.id}
                            title="Antecipa o follow-up para agora; o envio sai na próxima varredura do scheduler (até 5 minutos)"
                          >
                            <Icon name="send" size={14} /> {firingId === v.id ? 'Disparando...' : 'Disparar agora'}
                          </button>
                        </div>
                      )}
                    </td>
                    <td>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => (editingId === v.id ? cancelEdit() : startEdit(v))}
                      >
                        <Icon name="edit" size={14} /> {editingId === v.id ? 'Fechar' : 'Editar'}
                      </button>
                    </td>
                  </tr>

                  {/* Ficha do cliente, expandida abaixo da linha: a pessoa, os
                      veículos que já a interessaram e o histórico de visitas —
                      é o que junta os interesses de quem voltou à loja. */}
                  {profileVisitId === v.id && (
                    <tr className="row-edit">
                      <td colSpan={8}>
                        {profileLoading || !profile ? (
                          <div className="loading">Carregando ficha...</div>
                        ) : (
                          <div>
                            <div className="chart-card-head" style={{ marginBottom: 10 }}>
                              <h3>{profile.client.full_name}</h3>
                              <p>
                                {profile.client.cpf ? `CPF ${profile.client.cpf} · ` : ''}
                                {profile.client.phone}
                                {profile.client.email ? ` · ${profile.client.email}` : ''}
                                {` · Cliente desde ${stampDate(profile.client.created_at)}`}
                                {showStore ? ` · ${profile.client.store_name}` : ''}
                                {profile.client.opted_out ? ' · 🚫 opt-out de mensagens' : ''}
                              </p>
                              {/* Zera o cliente para repetir um teste do início, ou
                                  atende pedido de exclusão de dados. Apaga TUDO
                                  dele nesta loja, pelas cascatas do banco. */}
                              {isAdmin && (
                                <button
                                  type="button"
                                  className="btn btn-danger btn-sm"
                                  onClick={() => removeClient(profile.client)}
                                  title="Apaga o cliente com visitas, conversas, mensagens, propostas e test-drives"
                                >
                                  Excluir cliente e histórico
                                </button>
                              )}
                            </div>

                            <div style={{ marginBottom: 12 }}>
                              <strong>Veículos de interesse: </strong>
                              {[...new Set(profile.visits.flatMap(visitVehicles))].map(vi => (
                                <span key={vi} className="status-badge status-badge--blue" style={{ marginRight: 6 }}>{vi}</span>
                              ))}
                            </div>

                            {profile.visits.map(pv => (
                              <div key={pv.id} style={{ padding: '6px 0', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                                {stampDate(pv.visit_date)} — <strong>{pv.vehicle_interest || 'sem veículo informado'}</strong>
                                {' · '}{pv.seller_name || 'Sem vendedor'}
                                {' · '}
                                <span className={`status-badge status-badge--${VISIT_STATUS_TONE[pv.status] || 'gray'}`}>
                                  {VISIT_STATUS_LABELS[pv.status] || pv.status}
                                </span>
                                {pv.status === 'lost' && pv.lost_reason
                                  ? <span className="cell-sub"> ({LOST_REASON_LABELS[pv.lost_reason] || pv.lost_reason})</span>
                                  : null}
                              </div>
                            ))}

                            <div className="field-hint" style={{ marginTop: 10 }}>
                              {profile.conversations.length === 0
                                ? 'Nenhuma conversa de WhatsApp ainda.'
                                : `${profile.conversations.length} conversa${profile.conversations.length > 1 ? 's' : ''} de WhatsApp (${profile.conversations
                                    .map(cv => ({ bot: 'com o bot', seller: 'com vendedor', closed: 'encerrada' }[cv.status] || cv.status))
                                    .join(', ')}) — veja no inbox de Conversas.`}
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}

                  {/* Edição na própria linha, expandida abaixo dela: mantém o
                      contexto da visita à vista enquanto se altera o vendedor. */}
                  {editingId === v.id && editForm && (
                    <tr className="row-edit">
                      <td colSpan={8}>
                        <div className="form-grid">
                          <div className="field">
                            <label>Nome do cliente</label>
                            <input value={editForm.full_name} onChange={e => setEditForm(f => ({ ...f, full_name: e.target.value }))} />
                          </div>
                          <div className="field">
                            <label>CPF</label>
                            <input value={editForm.cpf} onChange={e => setEditForm(f => ({ ...f, cpf: e.target.value }))} />
                          </div>
                          <div className="field">
                            <label>E-mail</label>
                            <input type="email" value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} />
                          </div>
                          <div className="field field--full">
                            <label>Veículos de interesse</label>
                            <VehiclesField vehicles={editForm.vehicles} onChange={vehicles => setEditForm(f => ({ ...f, vehicles }))} />
                          </div>
                          <div className="field">
                            <label>Vendedor</label>
                            {/* Transferir o cliente entre vendedores é decisão de
                                gestão: só o admin. O backend barra de qualquer
                                jeito — aqui o select desabilitado explica antes. */}
                            <select
                              value={editForm.seller_id}
                              onChange={e => setEditForm(f => ({ ...f, seller_id: e.target.value }))}
                              disabled={!isAdmin}
                            >
                              <option value="">Sem vendedor</option>
                              {sellers.map(s => (
                                <option key={s.id} value={s.id}>{s.name}{s.active ? '' : ' (inativo)'}</option>
                              ))}
                            </select>
                            {!isAdmin && (
                              <div className="field-hint">Somente o administrador pode trocar o vendedor.</div>
                            )}
                          </div>
                          <div className="field">
                            <label>Status</label>
                            <select value={editForm.status} onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}>
                              {Object.entries(statusLabels).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
                            </select>
                          </div>
                          {/* O motivo só aparece quando a visita vira perdida —
                              é o dado que explica o "Perdido" nos relatórios. */}
                          {editForm.status === 'lost' && (
                            <div className="field">
                              <label>Motivo da perda</label>
                              <select
                                value={editForm.lost_reason}
                                onChange={e => setEditForm(f => ({ ...f, lost_reason: e.target.value }))}
                              >
                                <option value="">Não informado</option>
                                <option value="preco">Preço</option>
                                <option value="sem_estoque">Sem estoque</option>
                                <option value="concorrente">Comprou no concorrente</option>
                                <option value="sem_resposta">Cliente não respondeu</option>
                                <option value="outro">Outro</option>
                              </select>
                            </div>
                          )}
                          <div className="field field--full">
                            <label>Observações</label>
                            <input value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} />
                          </div>
                        </div>
                        <div className="field-hint">
                          O telefone ({v.phone}) não é editável: é por ele que a resposta do
                          cliente no WhatsApp encontra este cadastro.
                        </div>
                        <div className="form-actions">
                          <button className="btn btn-secondary" onClick={cancelEdit}>Cancelar</button>
                          <button className="btn btn-primary" onClick={saveEdit} disabled={savingEdit}>
                            {savingEdit ? 'Salvando...' : 'Salvar'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          {/* > 50 (e não > pageSize): quem subiu para 100 numa lista de 60
              ainda precisa da barra para conseguir voltar a 50. */}
          {total > 50 && (
            <div className="pagination-bar">
              <span className="pagination-info">
                Mostrando {page * pageSize + 1}–{Math.min((page + 1) * pageSize, total)} de {total}
              </span>
              <div className="pagination-controls">
                <label className="pagination-size">
                  Por página:
                  <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))}>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </label>
                <button
                  className="btn btn-secondary btn-sm"
                  disabled={page === 0}
                  onClick={() => setPage(p => p - 1)}
                >
                  ‹ Anterior
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  disabled={(page + 1) * pageSize >= total}
                  onClick={() => setPage(p => p + 1)}
                >
                  Próxima ›
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── CONVERSAS (INBOX) ────────────────────────────────────────────────────────
/** Tamanho de arquivo legível para o chip de anexo do chat. */
function formatFileSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Object URLs dos áudios já baixados, por id de anexo, pela sessão: a lista
// de mensagens recarrega a cada 5s e sem o cache cada polling baixaria os
// áudios de novo.
const audioUrls = new Map();

/**
 * Player de um áudio da conversa (gravado no inbox ou recebido do cliente).
 * A rota de anexo é autenticada, então o binário vem como blob e vira uma
 * object URL — um <audio src> direto não levaria o token.
 */
// Conteúdo de uma mensagem de áudio: "🎤 Áudio" (sem transcrição), o rótulo
// pendente, ou "🎤 <texto transcrito pela IA>". Devolve só o texto, ou null.
const audioTranscript = (content) => {
  const text = String(content || '').replace(/^🎤\s*/, '').trim();
  if (!text || text === 'Áudio' || text.startsWith('Áudio —')) return null;
  return text;
};

function AudioMessage({ conversationId, attachmentId, onDownload }) {
  const [url, setUrl] = useState(() => audioUrls.get(attachmentId) || null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (audioUrls.has(attachmentId)) { setUrl(audioUrls.get(attachmentId)); return undefined; }
    conversationsApi.downloadAttachment(conversationId, attachmentId)
      .then((res) => {
        const objectUrl = URL.createObjectURL(res.data);
        audioUrls.set(attachmentId, objectUrl);
        if (!cancelled) setUrl(objectUrl);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [conversationId, attachmentId]);

  if (failed) {
    return (
      <button type="button" className="message-attachment" onClick={onDownload} title="Baixar áudio">
        🎤 <span className="message-attachment-name">Áudio (baixar)</span>
      </button>
    );
  }
  if (!url) return <div className="message-audio message-audio--loading">🎤 Carregando áudio…</div>;
  return (
    <div className="message-audio">
      <audio controls preload="metadata" src={url} onError={() => setFailed(true)} />
    </div>
  );
}

function formatRecordingTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

const MAX_RECORDING_SECONDS = 10 * 60;

function ConversationsPage({ isAdmin = false, onNewProposal, onNewTestDrive, initialConversationId = null, onInitialConsumed }) {
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [notice, setNotice] = useState(null);
  // Busca e filtro da lista: com dezenas de conversas, achar "o cliente do
  // Corolla" por rolagem não escala. O filtro é local — a lista inteira (até
  // 200) já está no estado.
  const [listSearch, setListSearch] = useState('');
  const [listFilter, setListFilter] = useState('all'); // all | unread | pending
  // Respostas rápidas da loja da conversa aberta — vêm junto das mensagens.
  const [quickReplies, setQuickReplies] = useState([]);
  const [showReplies, setShowReplies] = useState(false);
  // Condições de pagamento pré-prontas dos veículos da LOJA DA CONVERSA
  // (botão 💳): carregadas ao abrir o popover, uma vez por conversa.
  const [showTerms, setShowTerms] = useState(false);
  const [termVehicles, setTermVehicles] = useState(null); // null = ainda não carregado
  const [sendingTerm, setSendingTerm] = useState(false);
  // Permissão de notificação do navegador. O pedido fica no sino do cabeçalho:
  // pedir no login abriria popup na cara de quem nem usa o inbox.
  const [notifPerm, setNotifPerm] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
  );
  const messagesEndRef = useRef(null);
  const messagesBoxRef = useRef(null);
  // Rolagem automática só quando quem atende já está no fim da conversa. As
  // mensagens recarregam a cada 5s, e rolar ao fim em toda recarga impedia de
  // ler o que ficou acima: quem subia era empurrado de volta.
  const stickToBottomRef = useRef(true);
  const lastConvIdRef = useRef(null);
  const lastMsgIdRef = useRef(null);
  const [newBelow, setNewBelow] = useState(false);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);
  const noticeTimer = useRef(null);

  const requestNotifications = async () => {
    if (typeof Notification === 'undefined') return;
    const perm = await Notification.requestPermission();
    setNotifPerm(perm);
    if (perm === 'granted') {
      // Já aproveita a permissão para inscrever o aparelho no push: é o que faz
      // o aviso chegar com o painel fechado. Falha aqui (plataforma sem chaves,
      // iPhone fora da tela de início) não tira a notificação da aba aberta.
      const result = await subscribeToPush().catch(() => ({ ok: false }));
      showNotice(result.ok
        ? 'Notificações ativadas neste aparelho, inclusive com o painel fechado.'
        : 'Notificações ativadas com o painel aberto. Para receber com ele fechado, veja Meu Perfil.');
    }
  };

  // Aviso de erro na própria tela, no lugar de alert(): não trava o polling
  // nem rouba o foco, e some sozinho depois de 6 segundos.
  const showNotice = useCallback((text) => {
    setNotice(text);
    clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(null), 6000);
  }, []);

  useEffect(() => () => clearTimeout(noticeTimer.current), []);

  const loadConversations = useCallback(async () => {
    try {
      const res = await conversationsApi.list();
      setConversations(res.data.conversations);
      setLoading(false);
    } catch (e) { console.error(e); }
  }, []);

  const loadMessages = useCallback(async (convId) => {
    try {
      const res = await conversationsApi.getMessages(convId);
      setMessages(res.data.messages);
      setSelected(res.data.conversation);
      setQuickReplies(res.data.quick_replies || []);
      // O backend acabou de marcar a conversa como lida — zera o contador
      // local sem esperar o próximo polling de 10s da lista.
      setConversations(cs => cs.map(c => (c.id === convId ? { ...c, unread: 0 } : c)));
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    loadConversations();
    pollRef.current = setInterval(loadConversations, 10000);
    return () => clearInterval(pollRef.current);
  }, [loadConversations]);

  // Conversa pedida de fora (toque numa notificação push, URL ?conversation=N):
  // abre direto pelo id, sem esperar a lista — getMessages já devolve a
  // conversa. Fora do escopo (404) fica na lista, sem erro na tela.
  useEffect(() => {
    if (!initialConversationId) return;
    loadMessages(Number(initialConversationId));
    onInitialConsumed?.();
  }, [initialConversationId, loadMessages, onInitialConsumed]);

  useEffect(() => {
    if (selected?.id) {
      const interval = setInterval(() => loadMessages(selected.id), 5000);
      return () => clearInterval(interval);
    }
  }, [selected?.id, loadMessages]);

  const scrollToBottom = useCallback((behavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior, block: 'end' });
    stickToBottomRef.current = true;
    setNewBelow(false);
  }, []);

  // Marca se a leitura está no fim (folga de 80px). Trocar de conversa ou
  // enviar uma mensagem volta ao fim explicitamente, por scrollToBottom.
  const handleMessagesScroll = () => {
    const el = messagesBoxRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    stickToBottomRef.current = atBottom;
    if (atBottom) setNewBelow(false);
  };

  useEffect(() => {
    const convId = selected?.id ?? null;
    const lastId = messages.length ? messages[messages.length - 1].id : null;
    if (convId !== lastConvIdRef.current) {
      // Conversa recém-aberta: vai direto ao fim, sem animação.
      lastConvIdRef.current = convId;
      lastMsgIdRef.current = lastId;
      scrollToBottom('auto');
      return;
    }
    if (lastId === lastMsgIdRef.current) return; // recarga sem mensagem nova
    lastMsgIdRef.current = lastId;
    if (stickToBottomRef.current) scrollToBottom('smooth');
    else setNewBelow(true); // quem está lendo acima recebe o aviso, não o empurrão
  }, [messages, selected?.id, scrollToBottom]);

  const handleSelect = (conv) => {
    setShowReplies(false);
    setShowTerms(false);
    setTermVehicles(null);
    loadMessages(conv.id);
  };

  const toggleTerms = async () => {
    if (showTerms) { setShowTerms(false); return; }
    setShowReplies(false);
    setShowTerms(true);
    if (termVehicles !== null || !selected) return;
    try {
      const res = await conversationsApi.paymentTerms(selected.id);
      setTermVehicles(res.data.vehicles || []);
    } catch (err) {
      setTermVehicles([]);
      showNotice(err?.response?.data?.error || 'Erro ao carregar as condições de pagamento');
    }
  };

  // Manda a condição pré-pronta (texto + arquivo) pelo WhatsApp da loja da
  // conversa. O mesmo material que a IA envia quando o cliente pergunta de parcela.
  const handleSendTerm = async (term) => {
    if (!selected || sendingTerm) return;
    setSendingTerm(true);
    setShowTerms(false);
    try {
      const res = await conversationsApi.sendPaymentTerm(selected.id, term.id);
      if (res.data?.simulated) showNotice('Loja sem WhatsApp configurado: a condição foi registrada, mas nada chegou ao cliente.');
      else if (res.data?.file_error) showNotice(`O texto saiu, mas o arquivo não: ${res.data.file_error}`);
      await loadMessages(selected.id);
    } catch (err) {
      showNotice(err?.response?.data?.error || 'Erro ao enviar a condição de pagamento');
    } finally {
      setSendingTerm(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selected) return;
    setSending(true);
    try {
      await conversationsApi.send(selected.id, newMessage);
      setNewMessage('');
      stickToBottomRef.current = true; // a própria mensagem sempre aparece
      await loadMessages(selected.id);
    } catch (err) {
      showNotice(err?.response?.data?.error || 'Erro ao enviar mensagem');
    } finally {
      setSending(false);
    }
  };

  // Espelha a validação do backend para o erro aparecer antes do upload; a
  // palavra final continua sendo do servidor.
  const handleAttachFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite reanexar o mesmo arquivo em seguida
    if (!file || !selected) return;

    if (!/\.(pdf|docx|xlsx)$/i.test(file.name)) {
      showNotice('Tipo de arquivo não permitido. Envie .pdf, .docx ou .xlsx');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      showNotice('Arquivo maior que 10MB');
      return;
    }

    setUploading(true);
    try {
      await conversationsApi.sendAttachment(selected.id, file);
      await loadMessages(selected.id);
    } catch (err) {
      showNotice(err?.response?.data?.error || 'Erro ao enviar o arquivo');
    } finally {
      setUploading(false);
    }
  };

  // ── Gravação de áudio ──────────────────────────────────────────────────
  // O MediaRecorder grava no contêiner que o navegador souber (WebM no
  // Chrome/Edge, Ogg no Firefox, MP4 no Safari); o backend converte o que a
  // Meta não aceita. Exige HTTPS (ou localhost) para o microfone.
  const [recording, setRecording] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const [sendingAudio, setSendingAudio] = useState(false);
  const recorderRef = useRef(null);
  const recordTimerRef = useRef(null);

  const stopRecorderTracks = () => {
    const rec = recorderRef.current;
    if (rec?.stream) rec.stream.getTracks().forEach((t) => t.stop());
    clearInterval(recordTimerRef.current);
  };

  const startRecording = async () => {
    if (!selected || recording) return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      showNotice('Este navegador não grava áudio. Use o Chrome, Edge, Firefox ou Safari atualizados.');
      return;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      showNotice('Permita o acesso ao microfone no navegador para gravar áudio.');
      return;
    }
    const mimeType = ['audio/ogg;codecs=opus', 'audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']
      .find((t) => MediaRecorder.isTypeSupported(t));
    let recorder;
    try {
      recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    } catch (err) {
      stream.getTracks().forEach((t) => t.stop());
      showNotice('Não foi possível iniciar a gravação neste navegador.');
      return;
    }
    const chunks = [];
    recorder.ondataavailable = (e) => { if (e.data?.size) chunks.push(e.data); };
    recorderRef.current = { recorder, stream, chunks, conversationId: selected.id };
    recorder.start(250);
    setRecordSeconds(0);
    setRecording(true);
    const startedAt = Date.now();
    recordTimerRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startedAt) / 1000);
      setRecordSeconds(elapsed);
      if (elapsed >= MAX_RECORDING_SECONDS) finishRecording(true);
    }, 500);
  };

  // send=false descarta a gravação; send=true espera o último pedaço
  // (evento stop) e sobe o Blob inteiro.
  const finishRecording = (send) => {
    const rec = recorderRef.current;
    if (!rec) return;
    stopRecorderTracks();
    recorderRef.current = null;
    setRecording(false);

    const { recorder, chunks, conversationId } = rec;
    if (!send) {
      if (recorder.state !== 'inactive') recorder.stop();
      return;
    }
    const upload = async () => {
      const blob = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' });
      if (blob.size < 1000) { showNotice('Gravação muito curta.'); return; }
      setSendingAudio(true);
      try {
        const res = await conversationsApi.sendAudio(conversationId, blob);
        if (res.data?.simulated) showNotice('Loja sem WhatsApp configurado: o áudio foi registrado, mas nada chegou ao cliente.');
        await loadMessages(conversationId);
      } catch (err) {
        showNotice(err?.response?.data?.error || 'Erro ao enviar o áudio');
      } finally {
        setSendingAudio(false);
      }
    };
    if (recorder.state === 'inactive') { upload(); return; }
    recorder.onstop = upload;
    recorder.stop();
  };

  // Trocar de conversa ou sair da tela no meio de uma gravação descarta-a.
  useEffect(() => () => {
    const rec = recorderRef.current;
    if (!rec) return;
    rec.stream.getTracks().forEach((t) => t.stop());
    clearInterval(recordTimerRef.current);
    if (rec.recorder.state !== 'inactive') rec.recorder.stop();
    recorderRef.current = null;
  }, []);
  useEffect(() => {
    if (recording) finishRecording(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);

  const handleDownloadAttachment = async (msg) => {
    try {
      const res = await conversationsApi.downloadAttachment(selected.id, msg.attachment_id);
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = msg.attachment_filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      showNotice('Erro ao baixar o anexo');
    }
  };

  // Vendedores da LOJA DA CONVERSA, para o select de transferência do admin —
  // e não da loja ativa do painel: um adm-grupo atendendo pela rede vê aqui só
  // quem pode assumir aquele cliente. Carregado por loja, uma vez cada.
  const [storeSellers, setStoreSellers] = useState({});
  useEffect(() => {
    if (!isAdmin || !selected?.organization_id) return;
    const orgId = selected.organization_id;
    if (storeSellers[orgId]) return;
    sellersApi.list({ organization_id: orgId })
      .then(res => setStoreSellers(prev => ({ ...prev, [orgId]: res.data || [] })))
      .catch(() => { /* sem lista, o select simplesmente não aparece */ });
  }, [isAdmin, selected?.organization_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReassign = async (sellerId) => {
    if (!selected || !sellerId) return;
    try {
      // Sem mensagem: trocar o responsável é interno, o cliente não é avisado.
      await conversationsApi.handoffToSeller(selected.id, Number(sellerId), null);
      await loadConversations();
      const seller = (storeSellers[selected.organization_id] || []).find(s => s.id === Number(sellerId));
      setSelected(prev => prev ? { ...prev, status: 'seller', assigned_seller_id: Number(sellerId), seller_name: seller?.name || prev.seller_name } : prev);
      showNotice(`Atendimento transferido para ${seller?.name || 'o vendedor'}`);
    } catch (e) { showNotice(e?.response?.data?.error || 'Erro ao transferir o atendimento'); }
  };

  const handleHandoff = async () => {
    if (!selected) return;
    try {
      // O "vou te conectar" só faz sentido para um cliente que já está
      // conversando. Assumir uma conversa ainda vazia (follow-up pendente) não
      // manda nada — só libera o campo de mensagem para puxar assunto.
      const intro = messages.some(m => m.sender_type === 'client')
        ? 'Olá! Vou te conectar agora com nosso consultor. Um momento!'
        : null;
      await conversationsApi.handoffToSeller(selected.id, null, intro);
      await loadMessages(selected.id);
      await loadConversations();
    } catch (e) { showNotice(e?.response?.data?.error || 'Erro ao assumir o atendimento'); }
  };

  const handleReturnBot = async () => {
    if (!selected) return;
    try {
      await conversationsApi.returnBot(selected.id);
      await loadMessages(selected.id);
      await loadConversations();
    } catch (e) { showNotice(e?.response?.data?.error || 'Erro ao devolver ao bot'); }
  };

  const statusColors = { bot: 'blue', seller: 'green', closed: 'gray' };
  const statusLabels = { bot: '🤖 Bot', seller: '👤 Vendedor', closed: '✓ Encerrada' };

  // Filtro local da lista. O telefone só entra na busca quando há dígitos no
  // termo — senão a parte numérica casaria com tudo.
  const normalizedSearch = listSearch.trim().toLowerCase();
  const searchDigits = normalizedSearch.replace(/\D/g, '');
  const visibleConversations = conversations.filter(conv => {
    if (listFilter === 'unread' && !(conv.unread > 0)) return false;
    if (listFilter === 'pending' && conv.visit_status !== 'followup_pending') return false;
    if (!normalizedSearch) return true;
    if ((conv.full_name || '').toLowerCase().includes(normalizedSearch)) return true;
    return searchDigits.length > 0 && String(conv.phone || '').replace(/\D/g, '').includes(searchDigits);
  });
  const listFiltered = Boolean(normalizedSearch) || listFilter !== 'all';

  return (
    // conv-open dirige o modo celular: lista em tela cheia OU chat em tela
    // cheia, nunca os dois espremidos lado a lado.
    <div className={`conversations-layout${selected ? ' conv-open' : ''}`}>
      {/* Lista de conversas */}
      <div className="conv-list">
        <div className="conv-list-header">
          <h2>Conversas</h2>
          <span className="badge">{conversations.length}</span>
          {/* O sino pede a permissão de notificação — e some depois de
              concedida. Pedir no login abriria popup sem contexto. */}
          {notifPerm === 'default' && (
            <button
              className="btn-icon conv-bell"
              onClick={requestNotifications}
              title="Ativar notificações de mensagem nova no navegador"
            >
              🔔
            </button>
          )}
        </div>
        <div className="conv-list-tools">
          <input
            placeholder="Buscar nome ou telefone..."
            value={listSearch}
            onChange={e => setListSearch(e.target.value)}
          />
          <div className="conv-list-filters">
            <button
              type="button"
              className={`chip${listFilter === 'all' ? ' chip--on' : ''}`}
              onClick={() => setListFilter('all')}
            >
              Todas
            </button>
            <button
              type="button"
              className={`chip${listFilter === 'unread' ? ' chip--on' : ''}`}
              onClick={() => setListFilter(f => (f === 'unread' ? 'all' : 'unread'))}
            >
              Não lidas
            </button>
            <button
              type="button"
              className={`chip${listFilter === 'pending' ? ' chip--on' : ''}`}
              onClick={() => setListFilter(f => (f === 'pending' ? 'all' : 'pending'))}
            >
              Follow-up pendente
            </button>
          </div>
        </div>
        {loading ? <SkeletonList rows={6} card={false} /> : conversations.length === 0 ? (
          <div className="empty-conv">Nenhuma conversa ainda.<br />Clientes aparecem aqui assim que a visita é cadastrada.</div>
        ) : visibleConversations.length === 0 ? (
          <div className="empty-conv">
            Nada {listFiltered ? 'com esse filtro' : 'por aqui'}.<br />
            <button className="btn btn-secondary btn-sm mt-8" onClick={() => { setListSearch(''); setListFilter('all'); }}>
              Limpar filtros
            </button>
          </div>
        ) : visibleConversations.map(conv => (
          <div
            key={conv.id}
            className={`conv-item ${selected?.id === conv.id ? 'active' : ''}`}
            onClick={() => handleSelect(conv)}
          >
            <div className="conv-avatar">{conv.full_name?.charAt(0)}</div>
            <div className="conv-info">
              <div className={`conv-name${conv.unread > 0 ? ' conv-name--unread' : ''}`}>{conv.full_name}</div>
              <div className="conv-preview">
                {conv.last_message
                  || (conv.visit_status === 'followup_pending' ? '⏳ Follow-up pendente' : 'Sem mensagens')}
              </div>
            </div>
            <div className="conv-meta">
              {/* Não lida É POR USUÁRIO (conversation_reads): o colega abrir a
                  conversa não apaga o aviso de quem ainda não a viu. */}
              {conv.unread > 0 && (
                <span className="unread-badge">{conv.unread > 99 ? '99+' : conv.unread}</span>
              )}
              {/* O ponto âmbar sinaliza o follow-up ainda não enviado — é o que a
                  equipe varre na lista para saber com quem puxar assunto. */}
              <span className={`status-dot status-dot--${conv.visit_status === 'followup_pending' ? 'amber' : statusColors[conv.status]}`} />
              {conv.last_message_at && (
                /* Tempo relativo: no inbox de follow-up, "3 d" grita urgência;
                   o horário completo fica no title, para quem precisar. */
                <div className="conv-time" title={new Date(conv.last_message_at).toLocaleString('pt-BR')}>
                  {timeAgo(conv.last_message_at)}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Área de chat */}
      <div className="chat-area">
        {!selected ? (
          <div className="chat-empty">
            <Icon name="chat" size={48} />
            <p>Selecione uma conversa</p>
          </div>
        ) : (
          <>
            <div className="chat-header">
              <div className="chat-header-info">
                {/* Só existe no celular (CSS): volta do chat para a lista. */}
                <button
                  className="btn-icon chat-back"
                  onClick={() => setSelected(null)}
                  title="Voltar para a lista"
                >
                  <Icon name="arrow_right" size={18} />
                </button>
                <div className="conv-avatar conv-avatar--lg">{selected.full_name?.charAt(0)}</div>
                <div>
                  <div className="chat-name">{selected.full_name}</div>
                  {/* A loja entra no cabeçalho: quem atende pela rede precisa saber
                      de qual número o cliente recebeu a mensagem antes de responder.
                      O vendedor também: a transferência é só do admin (select ao
                      lado, ou edição da visita), então quem atende precisa ver de
                      quem é o cliente. */}
                  <div className="chat-sub">
                    {selected.phone} · {selected.vehicle_interest || (selected.visit_source === 'whatsapp' ? '📲 lead do WhatsApp, veículo a definir' : 'sem veículo')}
                    {selected.store_name ? ` · ${selected.store_name}` : ''}
                    {selected.seller_name ? ` · 👤 ${selected.seller_name}` : ''}
                  </div>
                </div>
              </div>
              <div className="chat-header-actions">
                {onNewProposal && (
                  <button
                    className="btn btn-secondary btn-sm"
                    title="Montar uma proposta para este cliente"
                    onClick={() => onNewProposal(selected)}
                  >
                    <Icon name="file" size={14} /> Proposta
                  </button>
                )}
                {onNewTestDrive && (
                  <button
                    className="btn btn-secondary btn-sm"
                    title="Agendar um test-drive para este cliente"
                    onClick={() => onNewTestDrive(selected)}
                  >
                    <Icon name="calendar" size={14} /> Test-drive
                  </button>
                )}
                {/* Cliente que pediu para não receber mensagens automáticas.
                    Responder pelo chat continua permitido — o que não sai são
                    os follow-ups do sistema. */}
                {selected.opted_out && (
                  <span className="status-badge status-badge--red" title="O cliente pediu para não receber mensagens automáticas. Follow-ups desativados; respostas manuais continuam permitidas.">
                    🚫 Opt-out
                  </span>
                )}
                {/* O envio do follow-up é só do cron — o aviso fica, para a
                    equipe saber que o template ainda vai sair. */}
                {selected.visit_status === 'followup_pending' && (
                  <span className="status-badge status-badge--amber">⏳ Follow-up pendente</span>
                )}
                <span className={`status-badge status-badge--${statusColors[selected.status]}`}>
                  {statusLabels[selected.status]}
                </span>
                {/* Só o gerente troca o vendedor da conversa: o vendedor vê
                    apenas as suas, e a transferência é decisão de quem gerencia
                    a equipe. Lista os vendedores ativos da loja da conversa. */}
                {isAdmin && (storeSellers[selected.organization_id] || []).length > 0 && (
                  <select
                    className="chat-seller-select"
                    value={selected.assigned_seller_id || ''}
                    onChange={e => handleReassign(e.target.value)}
                    title="Vendedor responsável por esta conversa"
                  >
                    <option value="" disabled>Vendedor responsável…</option>
                    {(storeSellers[selected.organization_id] || []).map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                )}
                {selected.status === 'bot' && (
                  <button className="btn btn-secondary btn-sm" onClick={handleHandoff}>
                    <Icon name="seller" size={14} />
                    Assumir Atendimento
                  </button>
                )}
                {selected.status === 'seller' && (
                  <button className="btn btn-secondary btn-sm" onClick={handleReturnBot}>
                    <Icon name="bot" size={14} />
                    Devolver ao Bot
                  </button>
                )}
              </div>
            </div>

            {notice && (
              <div className="chat-alert">
                <span>{notice}</span>
                <button
                  type="button"
                  className="chat-alert-close"
                  onClick={() => { clearTimeout(noticeTimer.current); setNotice(null); }}
                  aria-label="Fechar aviso"
                >
                  ✕
                </button>
              </div>
            )}

            <div className="chat-messages-wrap">
            <div className="chat-messages" ref={messagesBoxRef} onScroll={handleMessagesScroll}>
              {messages.map(msg => (
                <div key={msg.id} className={`message message--${msg.direction} message--${msg.sender_type}`}>
                  <div className="message-bubble">
                    {msg.sender_type !== 'client' && (
                      <div className="message-sender">
                        {msg.sender_type === 'bot' ? '🤖 Bot' : msg.sender_type === 'seller' ? '👤 Vendedor' : '⚙️ Sistema'}
                        {msg.channel === 'email' && msg.sender_type !== 'system' && ' · ✉️ E-mail'}
                      </div>
                    )}
                    {msg.attachment_id && /^audio\//i.test(msg.attachment_mime_type || '') ? (
                      <>
                        <AudioMessage
                          conversationId={selected.id}
                          attachmentId={msg.attachment_id}
                          onDownload={() => handleDownloadAttachment(msg)}
                        />
                        {/* A transcrição automática (IA) do áudio do cliente: é o
                            que o bot leu e respondeu, então a equipe vê o mesmo. */}
                        {audioTranscript(msg.content) && (
                          <div className="message-transcript" title="Transcrição automática do áudio">
                            “{audioTranscript(msg.content)}”
                          </div>
                        )}
                      </>
                    ) : msg.attachment_id ? (
                      // O chip substitui o texto "📎 nome" — clicar baixa o
                      // arquivo (o download é autenticado, não dá para ser um
                      // link direto).
                      <button
                        type="button"
                        className="message-attachment"
                        onClick={() => handleDownloadAttachment(msg)}
                        title="Baixar arquivo"
                      >
                        <Icon name="file" size={22} />
                        <span className="message-attachment-info">
                          <span className="message-attachment-name">{msg.attachment_filename}</span>
                          <span className="message-attachment-size">{formatFileSize(msg.attachment_size)}</span>
                        </span>
                        <Icon name="download" size={16} />
                      </button>
                    ) : (
                      <div className="message-content">{msg.content}</div>
                    )}
                    <div className="message-time">
                      {new Date(msg.sent_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
            {newBelow && (
              <button type="button" className="chat-new-below" onClick={() => scrollToBottom('smooth')}>
                ↓ Novas mensagens
              </button>
            )}
            </div>

            {selected.status !== 'closed' && (
              <form className="chat-input" onSubmit={handleSend}>
                {/* Respostas rápidas da loja (Configurações › Respostas rápidas).
                    Clicar PREENCHE o campo em vez de enviar: quem atende ajusta
                    o texto antes de mandar. */}
                {showReplies && quickReplies.length > 0 && (
                  <div className="quick-replies-pop">
                    {quickReplies.map((reply, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => { setNewMessage(reply); setShowReplies(false); }}
                      >
                        {reply}
                      </button>
                    ))}
                  </div>
                )}
                {/* Condições de pagamento pré-prontas, por veículo da loja da
                    conversa. Clicar ENVIA na hora (texto + arquivo), como a IA
                    faria — é material pronto, não rascunho. */}
                {showTerms && (
                  <div className="quick-replies-pop terms-pop">
                    {termVehicles === null ? (
                      <div className="terms-pop-empty">Carregando...</div>
                    ) : termVehicles.length === 0 ? (
                      <div className="terms-pop-empty">
                        Nenhum veículo desta loja tem condição comercial ou ficha técnica cadastrada. Cadastre em Veículos, abrindo o card do veículo.
                      </div>
                    ) : termVehicles.map(v => (
                      <div key={v.id} className="terms-pop-group">
                        <div className="terms-pop-vehicle">🚗 {[v.brand, v.model, v.version, v.model_year].filter(Boolean).join(' ')}</div>
                        {v.terms.map(t => (
                          <button key={t.id} type="button" onClick={() => handleSendTerm(t)} title="Enviar este material ao cliente">
                            <span className="terms-pop-title">{t.kind === 'datasheet' ? '📋' : '💳'} {t.title}</span>
                            {t.description && <span className="terms-pop-desc">{t.description}</span>}
                            {t.filename && <span className="terms-pop-file">📎 {t.filename}</span>}
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
                {/* input escondido: o clipe ao lado é quem o aciona */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.xlsx"
                  style={{ display: 'none' }}
                  onChange={handleAttachFile}
                />
                {quickReplies.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-secondary chat-attach-btn"
                    onClick={() => { setShowTerms(false); setShowReplies(s => !s); }}
                    disabled={selected.status === 'bot' || sending || uploading}
                    title="Respostas rápidas"
                  >
                    ⚡
                  </button>
                )}
                {!recording && (
                  <button
                    type="button"
                    className="btn btn-secondary chat-attach-btn"
                    onClick={toggleTerms}
                    disabled={selected.status === 'bot' || sending || uploading || sendingAudio || sendingTerm}
                    title={sendingTerm ? 'Enviando material...' : 'Enviar condição comercial ou ficha técnica de um veículo'}
                  >
                    💳
                  </button>
                )}
                {recording ? (
                  // Gravando: o campo de texto dá lugar ao cronômetro. Cancelar
                  // descarta; o botão de enviar para a gravação e sobe o áudio.
                  <>
                    <div className="chat-recording">
                      <span className="chat-recording-dot" />
                      <span className="chat-recording-time">{formatRecordingTime(recordSeconds)}</span>
                      <span className="chat-recording-hint">Gravando áudio…</span>
                    </div>
                    <button
                      type="button"
                      className="btn btn-secondary chat-attach-btn"
                      onClick={() => finishRecording(false)}
                      title="Cancelar gravação"
                    >
                      ✕
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => finishRecording(true)}
                      title="Enviar áudio"
                    >
                      <Icon name="send" size={18} />
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="btn btn-secondary chat-attach-btn"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={selected.status === 'bot' || sending || uploading || sendingAudio}
                      title={uploading ? 'Enviando arquivo...' : 'Anexar arquivo (.pdf, .docx, .xlsx)'}
                    >
                      <Icon name="paperclip" size={18} />
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary chat-attach-btn"
                      onClick={startRecording}
                      disabled={selected.status === 'bot' || sending || uploading || sendingAudio}
                      title={sendingAudio ? 'Enviando áudio...' : 'Gravar áudio'}
                    >
                      <Icon name="mic" size={18} />
                    </button>
                    <input
                      value={newMessage}
                      onChange={e => setNewMessage(e.target.value)}
                      placeholder={
                        selected.status === 'bot' ? 'Assuma o atendimento para enviar mensagens...'
                          : uploading ? 'Enviando arquivo...'
                          : sendingAudio ? 'Enviando áudio...'
                          : 'Digite uma mensagem...'
                      }
                      disabled={selected.status === 'bot' || sending || sendingAudio}
                    />
                    <button type="submit" className="btn btn-primary" disabled={selected.status === 'bot' || !newMessage.trim() || sending || sendingAudio}>
                      <Icon name="send" size={18} />
                    </button>
                  </>
                )}
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── VENDEDORES ───────────────────────────────────────────────────────────────
// ─── VEÍCULOS ─────────────────────────────────────────────────────────────────
/**
 * Catálogo de MODELOS, não de unidades: uma linha por marca/modelo/versão, com
 * a quantidade em estoque num campo. Formulário sempre à vista, que também
 * recebe os dados ao editar.
 */
const emptyVehicle = { brand: '', model: '', version: '', model_year: '', color: '', fuel: '', price: '', quantity: '', features: [] };

/**
 * Máscara de moeda no padrão brasileiro "R$ 0,00" (estilo caixa eletrônico):
 * os dígitos entram pela direita e os dois últimos são sempre os centavos —
 * "1" → R$ 0,01, "185" → R$ 1,85, "18500000" → R$ 185.000,00. Tudo que não
 * é dígito (vírgula, ponto, "R$") é descartado, então o valor gravado é só
 * dígitos ÷ 100 e não depende de onde a pessoa digitou a vírgula.
 *
 * Pedido do usuário em 09/09/2026, substituindo a versão em que a vírgula
 * era digitada. Cuidado ao cadastrar: "185000" aqui é R$ 1.850,00 — é o que
 * o campo mostra enquanto se digita, então o valor 100x menor fica visível.
 */
function maskCurrency(input) {
  const digits = String(input ?? '').replace(/\D/g, '').replace(/^0+(?=\d)/, '');
  if (!digits) return '';
  const cents = Number(digits) / 100;
  return cents.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 });
}

/** "R$ 185.000,50" → "185000.50", que é o que o backend grava. */
function unmaskCurrency(masked) {
  const digits = String(masked ?? '').replace(/\D/g, '');
  if (!digits) return '';
  return (Number(digits) / 100).toFixed(2);
}

// ─── Fotos do veículo ─────────────────────────────────────────────────────────
const MAX_VEHICLE_PHOTOS = 10;
const PHOTO_MAX_SIDE = 1600;
const MAX_VEHICLE_TERMS = 5;
const emptyTermForm = { title: '', description: '', file: null };

/**
 * Reduz a foto no navegador antes do upload: lado maior 1600px, JPEG 0.85.
 * Foto de celular tem 3–8MB; assim sobe com ~300KB e o banco (BYTEA) não
 * incha. Se o navegador não decodificar (HEIC, por exemplo), sobe o original
 * e o backend decide se aceita.
 */
async function compressPhoto(file) {
  if (!/^image\//.test(file.type)) return file;
  try {
    // from-image: respeita a orientação EXIF, senão foto tirada em pé deita.
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const scale = Math.min(1, PHOTO_MAX_SIDE / Math.max(bitmap.width, bitmap.height));
    if (scale === 1 && file.size < 600 * 1024) { bitmap.close(); return file; }
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
    if (!blob) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
  } catch (e) {
    return file;
  }
}

// Object URLs por foto, guardadas pela sessão: a lista recarrega a cada salvar
// e refazer o download de todas as capas a cada vez seria desperdício.
const vehiclePhotoUrls = new Map();

function VehiclePhoto({ vehicleId, photoId, alt }) {
  const key = `${vehicleId}/${photoId}`;
  const [url, setUrl] = useState(() => vehiclePhotoUrls.get(key) || null);

  useEffect(() => {
    let cancelled = false;
    if (vehiclePhotoUrls.has(key)) { setUrl(vehiclePhotoUrls.get(key)); return undefined; }
    vehiclesApi.photo(vehicleId, photoId)
      .then((res) => {
        const objectUrl = URL.createObjectURL(res.data);
        vehiclePhotoUrls.set(key, objectUrl);
        if (!cancelled) setUrl(objectUrl);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [key, vehicleId, photoId]);

  if (!url) return <div className="photo-loading" />;
  return <img src={url} alt={alt} />;
}

// Materiais do veículo: dois tipos com a mesma estrutura (título + texto e/ou
// arquivo), cada um no seu bloco do formulário.
const VEHICLE_MATERIAL_KINDS = {
  commercial: {
    title: 'Condição comercial',
    emoji: '💳',
    titlePlaceholder: 'Título (ex.: Entrada facilitada em 48x)',
    textPlaceholder: 'Texto que o cliente recebe no WhatsApp (ex.: Entrada de R$ 10.000 + 48x de R$ 1.290 no banco parceiro, sujeito a aprovação). Opcional se houver arquivo.',
    hint: 'Vai direto ao cliente que pede "condições comerciais" ou pergunta de parcela: pelo botão 💳 do inbox ou pelo assistente de IA. Quem não fechar nessas condições recebe uma proposta personalizada pela tela Propostas.',
  },
  datasheet: {
    title: 'Ficha técnica',
    emoji: '📋',
    titlePlaceholder: 'Título (ex.: Ficha técnica 2026, Folheto do fabricante)',
    textPlaceholder: 'Texto que o cliente recebe (motor, potência, consumo, dimensões...). Opcional se houver arquivo.',
    hint: 'Vai direto ao cliente que pede "informações do veículo" ou "ficha técnica": pelo botão 💳 do inbox ou pelo assistente de IA.',
  },
};
const MATERIAL_ACCEPT = '.pdf,.jpg,.jpeg,.png,.mp4';
const MATERIAL_MAX_BYTES = { image: 5 * 1024 * 1024, video: 16 * 1024 * 1024, pdf: 16 * 1024 * 1024 };

function materialFileProblem(file) {
  const m = /\.(pdf|jpe?g|png|mp4)$/i.exec(file.name);
  if (!m) return 'Envie o arquivo em PDF, JPG, PNG ou MP4';
  const ext = m[1].toLowerCase();
  const limit = ext === 'pdf' ? MATERIAL_MAX_BYTES.pdf : ext === 'mp4' ? MATERIAL_MAX_BYTES.video : MATERIAL_MAX_BYTES.image;
  if (file.size > limit) return `O WhatsApp aceita este tipo de arquivo até ${Math.round(limit / (1024 * 1024))}MB`;
  return null;
}

/**
 * Um bloco de materiais (condição comercial OU ficha técnica). Na edição os
 * itens já existem e sobem na hora; no cadastro ficam pendentes (`pending`) e
 * a página os sobe depois do create, como as fotos.
 */
function VehicleMaterialsField({
  kind, items, pending, editingId, canAdd, onAdd, onUpdate, onRemove, onRemovePending, onOpenFile, onError,
}) {
  const meta = VEHICLE_MATERIAL_KINDS[kind];
  const [form, setForm] = useState(emptyTermForm);
  const [editingTermId, setEditingTermId] = useState(null);
  const [saving, setSaving] = useState(false);
  const list = editingId ? items : pending;
  const count = list.length;
  const valid = form.title.trim() && (form.description.trim() || form.file);

  useEffect(() => { setForm(emptyTermForm); setEditingTermId(null); }, [editingId]);

  const pickFile = (file) => {
    if (!file) return;
    const problem = materialFileProblem(file);
    if (problem) { onError(problem); return; }
    setForm(f => ({ ...f, file }));
  };

  const submit = async () => {
    if (!valid) return;
    const data = { kind, title: form.title.trim(), description: form.description.trim(), file: form.file };
    setSaving(true);
    try {
      if (editingTermId) await onUpdate(editingTermId, data);
      else await onAdd(data);
      setForm(emptyTermForm);
      setEditingTermId(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="terms-field">
      <label>{meta.emoji} {meta.title} <span className="field-hint">{count} de {MAX_VEHICLE_TERMS}</span></label>
      {count > 0 && (
        <ul className="terms-list">
          {list.map((t) => (
            <li key={t.id || t.key} className={`term-item${editingTermId && editingTermId === t.id ? ' term-item--editing' : ''}`}>
              <div className="term-body">
                <div className="term-title">{meta.emoji} {t.title}</div>
                {t.description && <div className="term-desc">{t.description}</div>}
                {t.filename && (
                  <button type="button" className="term-file" onClick={() => onOpenFile(t)} title="Abrir o arquivo">
                    📎 {t.filename} · {formatFileSize(t.size_bytes)}
                  </button>
                )}
                {!t.filename && t.file && <div className="term-file">📎 {t.file.name} · {formatFileSize(t.file.size)}</div>}
              </div>
              <div className="term-actions">
                {editingId && (
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setEditingTermId(t.id); setForm({ title: t.title || '', description: t.description || '', file: null }); }}>Editar</button>
                )}
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={() => (editingId ? onRemove(t) : onRemovePending(t.key))}
                >
                  Remover
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {(count < MAX_VEHICLE_TERMS || editingTermId) && canAdd && (
        <div className="term-add">
          <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder={meta.titlePlaceholder} maxLength={120} />
          <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder={meta.textPlaceholder} rows={3} maxLength={4000} />
          <div className="term-add-row">
            {/* Também na edição (24/09/2026): anexa ou troca o arquivo de um
                material já salvo — antes o botão sumia ao editar e o arquivo
                "não ficava". */}
            <label className="btn btn-secondary btn-sm">
              <input type="file" accept={MATERIAL_ACCEPT} hidden onChange={e => { const file = e.target.files?.[0]; e.target.value = ''; pickFile(file); }} />
              <Icon name="paperclip" size={14} /> {form.file ? form.file.name : (editingTermId && list.find(t => t.id === editingTermId)?.filename ? 'Trocar arquivo' : 'Anexar imagem, PDF ou vídeo')}
            </label>
            {form.file && (
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setForm(f => ({ ...f, file: null }))}>Tirar arquivo</button>
            )}
            <span className="term-add-spacer" />
            {editingTermId && (
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setEditingTermId(null); setForm(emptyTermForm); }}>Cancelar</button>
            )}
            <button type="button" className="btn btn-primary btn-sm" onClick={submit} disabled={!valid || saving}>
              {saving ? 'Salvando...' : editingTermId ? 'Salvar material' : 'Adicionar material'}
            </button>
          </div>
          {(form.title.trim() || form.description.trim() || form.file) && !saving && (
            <div className="field-hint field-hint--warn">
              Clique em <strong>{editingTermId ? 'Salvar material' : 'Adicionar material'}</strong> para guardar este item — o botão Salvar do veículo, lá embaixo, não o inclui.
            </div>
          )}
        </div>
      )}
      <div className="field-hint">{meta.hint} Imagem até 5MB; PDF e vídeo (MP4) até 16MB. O cliente recebe a foto de capa do veículo em cima, o texto embaixo e botões (agendar test drive, ver o outro material, falar com a loja); o arquivo vai logo em seguida.</div>
    </div>
  );
}

/**
 * "Buscar na tabela FIPE": marca → modelo → ano, e o valor de tabela do mês.
 * Escolher o ano preenche o formulário (marca, modelo, versão, ano, combustível
 * e preço); tudo continua editável. A FIPE junta modelo e versão num nome só
 * ("Corolla XEi 2.0 Flex 16V Aut."): a primeira palavra vira o modelo e o
 * resto a versão — quem cadastra ajusta se preferir.
 */
function FipeLookup({ knownBrands, fuels, onPick }) {
  const [open, setOpen] = useState(false);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [years, setYears] = useState([]);
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const run = async (fn) => {
    setBusy(true); setError(null);
    try { await fn(); } catch (err) { setError(err.response?.data?.error || 'A tabela FIPE não respondeu'); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    if (!open || brands.length) return;
    run(async () => { const res = await fipeApi.brands(); setBrands(res.data || []); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const chooseBrand = (code) => {
    setBrand(code); setModel(''); setYear(''); setModels([]); setYears([]); setResult(null);
    if (!code) return;
    run(async () => { const res = await fipeApi.models(code); setModels(res.data || []); });
  };
  const chooseModel = (code) => {
    setModel(code); setYear(''); setYears([]); setResult(null);
    if (!code) return;
    run(async () => { const res = await fipeApi.years(brand, code); setYears(res.data || []); });
  };
  const chooseYear = (code) => {
    setYear(code); setResult(null);
    if (!code) return;
    run(async () => {
      const res = await fipeApi.price(brand, model, code);
      const data = res.data;
      setResult(data);
      // Marca: prefere o nome da lista do sistema ("Volkswagen") ao da FIPE
      // ("VW - VolksWagen"), para o catálogo não ter a mesma marca escrita de
      // dois jeitos.
      const fipeBrand = String(data.brand || '');
      const known = knownBrands.find(b => fipeBrand.toLowerCase().includes(b.toLowerCase()) || b.toLowerCase().includes(fipeBrand.toLowerCase()));
      const words = String(data.model || '').trim().split(/\s+/);
      const modelName = words[0] ? words[0].charAt(0).toUpperCase() + words[0].slice(1).toLowerCase() : '';
      const fuel = fuels.find(f => f.toLowerCase() === String(data.fuel || '').toLowerCase()) || '';
      onPick({
        brand: known || fipeBrand,
        model: modelName,
        version: words.slice(1).join(' '),
        model_year: data.model_year ? String(data.model_year) : '',
        fuel,
        price: data.price ? maskCurrency(Math.round(data.price * 100)) : '',
      });
    });
  };

  if (!open) {
    return (
      <div className="field field--full">
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setOpen(true)}>
          🔎 Buscar na tabela FIPE
        </button>
        <div className="field-hint">Preenche marca, modelo, versão, ano, combustível e o valor de tabela do mês. Tudo pode ser ajustado depois.</div>
      </div>
    );
  }

  return (
    <div className="field field--full fipe-box">
      <div className="fipe-box-head">
        <strong>🔎 Tabela FIPE</strong>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setOpen(false)}>Fechar</button>
      </div>
      <div className="fipe-row">
        <select value={brand} onChange={e => chooseBrand(e.target.value)} disabled={busy && !brands.length}>
          <option value="">{brands.length ? 'Marca...' : 'Carregando marcas...'}</option>
          {brands.map(b => <option key={b.codigo} value={b.codigo}>{b.nome}</option>)}
        </select>
        <select value={model} onChange={e => chooseModel(e.target.value)} disabled={!brand || !models.length}>
          <option value="">{brand && !models.length ? 'Carregando modelos...' : 'Modelo...'}</option>
          {models.map(m => <option key={m.codigo} value={m.codigo}>{m.nome}</option>)}
        </select>
        <select value={year} onChange={e => chooseYear(e.target.value)} disabled={!model || !years.length}>
          <option value="">{model && !years.length ? 'Carregando anos...' : 'Ano...'}</option>
          {years.map(y => <option key={y.codigo} value={y.codigo}>{String(y.nome).replace(/^32000/, '0 km')}</option>)}
        </select>
      </div>
      {busy && <div className="field-hint">Consultando...</div>}
      {error && <div className="field-hint field-hint--error">{error}</div>}
      {result && (
        <div className="field-hint field-hint--success">
          Valor de tabela FIPE: <strong>{result.price_label}</strong>
          {result.reference_month ? ` (${result.reference_month})` : ''}
          {result.fipe_code ? ` · código ${result.fipe_code}` : ''} — campos preenchidos abaixo.
        </div>
      )}
    </div>
  );
}

function VehiclesPage({ hasStore = true }) {
  const [vehicles, setVehicles] = useState([]);
  const [brands, setBrands] = useState([]);
  const [featureGroups, setFeatureGroups] = useState([]);
  const [fuels, setFuels] = useState([]);
  const [form, setForm] = useState(emptyVehicle);
  const [editingId, setEditingId] = useState(null);
  // O formulário só aparece ao clicar em "Novo Veículo" ou num card; os cards
  // vêm no topo da página (pedido do usuário, 23/09/2026).
  const [showForm, setShowForm] = useState(false);
  const formRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  // Fotos escolhidas ANTES de o veículo existir (cadastro): sobem logo após o
  // create. Na edição o veículo já tem id e a foto sobe na hora.
  const [pendingPhotos, setPendingPhotos] = useState([]); // { key, file, url }
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  // Materiais (condição comercial e ficha técnica): no cadastro ficam
  // pendentes (sobem após o create, como as fotos); na edição sobem na hora.
  const [pendingTerms, setPendingTerms] = useState([]); // { key, kind, title, description, file }

  const showStore = new Set(vehicles.map(v => v.organization_id)).size > 1;

  // Mesma lista que a tela de Clientes usa para a marca da concessionária: uma
  // fonte só evita "VW" aqui e "Volkswagen" lá.
  useEffect(() => {
    brandsApi.list().then(res => setBrands(res.data)).catch(() => setBrands([]));
    vehicleFeaturesApi.list().then(res => setFeatureGroups(res.data)).catch(() => setFeatureGroups([]));
    vehicleFeaturesApi.fuels().then(res => setFuels(res.data)).catch(() => setFuels([]));
  }, []);

  const toggleFeature = (item) => setForm(f => ({
    ...f,
    features: f.features.includes(item) ? f.features.filter(x => x !== item) : [...f.features, item],
  }));

  // Item gravado que saiu da lista (rótulo renomeado): continua aparecendo, em
  // "Outros", senão ficaria no veículo para sempre sem ter onde desmarcar.
  const knownFeatures = new Set(featureGroups.flatMap(g => g.items));
  const orphanFeatures = featureGroups.length ? form.features.filter(x => !knownFeatures.has(x)) : [];
  const visibleFeatureGroups = orphanFeatures.length
    ? [...featureGroups, { group: 'Outros', items: orphanFeatures }]
    : featureGroups;

  const load = useCallback(async () => {
    try {
      const res = await vehiclesApi.list({ include_inactive: true });
      setVehicles(res.data);
    } catch (e) {
      setFeedback({ type: 'error', text: 'Erro ao carregar veículos' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const clearPendingPhotos = () => {
    setPendingPhotos(list => { list.forEach(p => URL.revokeObjectURL(p.url)); return []; });
  };
  const resetForm = () => {
    setForm(emptyVehicle); setEditingId(null); clearPendingPhotos();
    setPendingTerms([]); setShowForm(false);
  };

  const scrollToForm = () => {
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const startNew = () => {
    setForm(emptyVehicle); setEditingId(null); clearPendingPhotos(); setPendingTerms([]);
    setFeedback(null); setShowForm(true); scrollToForm();
  };

  const startEdit = (v) => {
    setForm({
      brand: v.brand || '',
      model: v.model || '',
      version: v.version || '',
      model_year: v.model_year ?? '',
      color: v.color || '',
      fuel: v.fuel || '',
      // Vem do banco como "89900.00"; a máscara só lê dígitos e trata os dois
      // últimos como centavos, então o valor entra em centavos inteiros.
      price: v.price === null || v.price === undefined ? '' : maskCurrency(Math.round(Number(v.price) * 100)),
      quantity: v.quantity ?? '',
      features: v.features || [],
    });
    setEditingId(v.id);
    clearPendingPhotos();
    setPendingTerms([]);
    setFeedback(null);
    setShowForm(true);
    scrollToForm();
  };

  const editingVehicle = editingId ? vehicles.find(v => v.id === editingId) : null;
  const editingPhotos = editingVehicle?.photos || [];
  const photoCount = editingId ? editingPhotos.length : pendingPhotos.length;

  const applyPhotos = (vehicleId, photos) =>
    setVehicles(list => list.map(v => (v.id === vehicleId ? { ...v, photos } : v)));

  // ── Materiais (condição comercial / ficha técnica) ────────────────────
  const editingTerms = editingVehicle?.payment_terms || [];
  const applyTerms = (vehicleId, payment_terms) =>
    setVehicles(list => list.map(v => (v.id === vehicleId ? { ...v, payment_terms } : v)));

  const addTerm = async (data) => {
    if (!editingId) {
      setPendingTerms(list => [...list, { key: `${Date.now()}-${Math.random()}`, ...data }]);
      return;
    }
    setFeedback(null);
    try {
      const res = await vehiclesApi.addPaymentTerm(editingId, data);
      applyTerms(editingId, res.data.payment_terms);
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.error || 'Erro ao salvar o material' });
      throw err;
    }
  };

  const updateTerm = async (termId, data) => {
    setFeedback(null);
    try {
      const res = await vehiclesApi.updatePaymentTerm(editingId, termId, { title: data.title, description: data.description, file: data.file });
      applyTerms(editingId, res.data.payment_terms);
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.error || 'Erro ao salvar o material' });
      throw err;
    }
  };

  const removeTerm = async (t) => {
    try {
      const res = await vehiclesApi.deletePaymentTerm(editingId, t.id);
      applyTerms(editingId, res.data.payment_terms);
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.error || 'Erro ao remover o material' });
    }
  };

  const removePendingTerm = (key) => setPendingTerms(list => list.filter(p => p.key !== key));

  // O arquivo é autenticado: baixa como blob e abre em nova aba.
  const openTermFile = async (t) => {
    try {
      const res = await vehiclesApi.paymentTermFile(editingId, t.id);
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      setFeedback({ type: 'error', text: 'Erro ao abrir o arquivo' });
    }
  };

  const materialsProps = (kind) => ({
    kind,
    items: editingTerms.filter(t => t.kind === kind),
    pending: pendingTerms.filter(t => t.kind === kind),
    editingId,
    canAdd: Boolean(hasStore || editingId),
    onAdd: addTerm,
    onUpdate: updateTerm,
    onRemove: removeTerm,
    onRemovePending: removePendingTerm,
    onOpenFile: openTermFile,
    onError: (text) => setFeedback({ type: 'error', text }),
  });

  const addPhotos = async (fileList) => {
    const files = Array.from(fileList || []).slice(0, MAX_VEHICLE_PHOTOS - photoCount);
    if (!files.length) return;
    setUploadingPhotos(true);
    setFeedback(null);
    try {
      const compressed = await Promise.all(files.map(compressPhoto));
      if (editingId) {
        const res = await vehiclesApi.uploadPhotos(editingId, compressed);
        applyPhotos(editingId, res.data.photos);
      } else {
        setPendingPhotos(list => [
          ...list,
          ...compressed.map(file => ({ key: `${Date.now()}-${Math.random()}`, file, url: URL.createObjectURL(file) })),
        ]);
      }
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.error || 'Erro ao enviar as fotos' });
    } finally {
      setUploadingPhotos(false);
    }
  };

  const removePhoto = async (photoId) => {
    try {
      const res = await vehiclesApi.deletePhoto(editingId, photoId);
      applyPhotos(editingId, res.data.photos);
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.error || 'Erro ao remover a foto' });
    }
  };

  const removePending = (key) => setPendingPhotos(list => {
    const item = list.find(p => p.key === key);
    if (item) URL.revokeObjectURL(item.url);
    return list.filter(p => p.key !== key);
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFeedback(null);
    try {
      // O campo carrega "R$ 89.900,00" para o usuário; o banco recebe número.
      const payload = { ...form, price: unmaskCurrency(form.price) };

      if (editingId) {
        await vehiclesApi.update(editingId, payload);
        setFeedback({ type: 'success', text: `${form.brand} ${form.model} atualizado.` });
      } else {
        const created = await vehiclesApi.create(payload);
        if (pendingPhotos.length) {
          try {
            await vehiclesApi.uploadPhotos(created.data.id, pendingPhotos.map(p => p.file));
          } catch (err) {
            // O veículo já existe; avisar e seguir, senão a pessoa cadastraria
            // de novo e teria o modelo em duplicidade.
            setFeedback({
              type: 'error',
              text: `${form.brand} ${form.model} cadastrado, mas as fotos não subiram: ${err.response?.data?.error || 'erro no envio'}. Abra o card para tentar de novo.`,
            });
            resetForm();
            load();
            return;
          }
        }
        // Os materiais sobem um a um (cada um é um multipart próprio).
        for (const t of pendingTerms) {
          try {
            await vehiclesApi.addPaymentTerm(created.data.id, t);
          } catch (err) {
            setFeedback({
              type: 'error',
              text: `${form.brand} ${form.model} cadastrado, mas "${t.title}" não subiu: ${err.response?.data?.error || 'erro no envio'}. Abra o card para tentar de novo.`,
            });
            resetForm();
            load();
            return;
          }
        }
        setFeedback({ type: 'success', text: `${form.brand} ${form.model} cadastrado.` });
      }
      resetForm();
      load();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.error || 'Erro ao salvar o veículo' });
    } finally {
      setSaving(false);
    }
  };

  // Chave Ativo/Arquivado no card: reflete na hora e confirma com o servidor.
  const toggleActive = async (v) => {
    setVehicles(list => list.map(x => (x.id === v.id ? { ...x, active: !v.active } : x)));
    try {
      await vehiclesApi.update(v.id, { active: !v.active });
    } catch (err) {
      setFeedback({ type: 'error', text: 'Erro ao alterar a situação do veículo' });
      load();
    }
  };

  // Excluir é definitivo, ao contrário de Arquivar; por isso a confirmação.
  const removeVehicle = async (v) => {
    const label = [v.brand, v.model, v.version].filter(Boolean).join(' ');
    if (!window.confirm(`Excluir ${label} do catálogo? Esta ação não pode ser desfeita.`)) return;
    try {
      await vehiclesApi.remove(v.id);
      if (editingId === v.id) resetForm();
      setFeedback({ type: 'success', text: `${label} excluído do catálogo.` });
      load();
    } catch (err) {
      setFeedback({ type: 'error', text: 'Erro ao excluir o veículo' });
    }
  };

  const formatPrice = (value) =>
    value === null || value === undefined || value === ''
      ? null
      : Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const countKind = (v, kind) => (v.payment_terms || []).filter(t => t.kind === kind).length;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Veículos</h1>
          <p>Catálogo de modelos e quantidade em estoque</p>
        </div>
        <button className="btn btn-primary" onClick={() => (showForm && !editingId ? resetForm() : startNew())} disabled={!hasStore}>
          <Icon name="plus" size={16} /> Novo Veículo
        </button>
      </div>

      {feedback && (
        <div className={`alert alert-${feedback.type}`}>
          <Icon name={feedback.type === 'success' ? 'check' : 'x'} size={18} />
          <div>{feedback.text}</div>
        </div>
      )}

      {/* O estoque é do pátio da loja: sem loja escolhida não há onde guardar o
          veículo. Editar continua liberado — o veículo já tem loja. */}
      {!hasStore && (
        <div className="field-hint" style={{ marginBottom: 12 }}>
          Escolha a <strong>loja ativa</strong> no menu à esquerda para cadastrar um veículo.
        </div>
      )}

      {/* Cards dos veículos cadastrados, no topo. Clicar abre a ficha para
          editar; a chave arquiva/reativa sem abrir. */}
      {loading ? <SkeletonList rows={4} /> : vehicles.length === 0 ? (
        <div className="list-card">
          <EmptyState
            icon="car"
            title="Nenhum veículo no catálogo"
            hint='Clique em "Novo Veículo" para cadastrar o estoque e sugerir veículos na Nova Visita.'
          />
        </div>
      ) : (
        <div className="vehicle-cards">
          {vehicles.map(v => (
            <div
              key={v.id}
              className={`vehicle-card${v.active ? '' : ' vehicle-card--inactive'}${editingId === v.id ? ' vehicle-card--editing' : ''}`}
              onClick={() => startEdit(v)}
              role="button"
              tabIndex={0}
              onKeyDown={e => { if (e.key === 'Enter') startEdit(v); }}
            >
              <div className="vehicle-card-cover">
                {v.photos?.length
                  ? <VehiclePhoto vehicleId={v.id} photoId={v.photos[0].id} alt={`${v.brand} ${v.model}`} />
                  : <Icon name="car" size={28} />}
                {!v.active && <span className="status-badge status-badge--red vehicle-card-flag">Arquivado</span>}
              </div>
              <div className="vehicle-card-body">
                <div className="vehicle-card-title">
                  {v.brand} {v.model}
                  {v.version && <span className="todo-title-soft"> {v.version}</span>}
                </div>
                <div className="vehicle-card-meta">
                  {showStore && <span>🏬 {v.store_name}</span>}
                  {v.model_year && <span>{v.model_year}</span>}
                  {v.color && <span>{v.color}</span>}
                  {v.fuel && <span>{v.fuel}</span>}
                </div>
                <div className="vehicle-card-price">{formatPrice(v.price) || 'Sem preço'}</div>
                <div className="vehicle-card-meta">
                  <span><strong>{v.quantity}</strong> em estoque</span>
                  {v.photos?.length > 0 && <span>📷 {v.photos.length}</span>}
                  {v.features?.length > 0 && <span title={v.features.join(', ')}>✨ {v.features.length}</span>}
                  {countKind(v, 'commercial') > 0 && <span title="Condições comerciais">💳 {countKind(v, 'commercial')}</span>}
                  {countKind(v, 'datasheet') > 0 && <span title="Fichas técnicas">📋 {countKind(v, 'datasheet')}</span>}
                </div>
              </div>
              <div className="vehicle-card-foot" onClick={e => e.stopPropagation()}>
                <label className="switch" title={v.active ? 'Arquivar (sai do catálogo e da Nova Visita)' : 'Reativar no catálogo'}>
                  <input type="checkbox" checked={Boolean(v.active)} onChange={() => toggleActive(v)} />
                  <span className="switch-slider" />
                </label>
                <span className="field-hint">{v.active ? 'No catálogo' : 'Arquivado'}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
      <div className="form-card" ref={formRef}>
        <h3>
          <Icon name={editingId ? 'edit' : 'plus'} size={16} />
          {editingId ? 'Ficha do veículo' : 'Novo veículo'}
        </h3>
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <FipeLookup
              knownBrands={brands}
              fuels={fuels}
              onPick={(data) => setForm(f => ({
                ...f,
                brand: data.brand || f.brand,
                model: data.model || f.model,
                version: data.version || f.version,
                model_year: data.model_year || f.model_year,
                fuel: data.fuel || f.fuel,
                price: data.price || f.price,
              }))}
            />
            <div className="field">
              <label>Marca *</label>
              {/* datalist, e não select: o catálogo pode ter uma marca antiga que
                  saiu da lista, e um select a apagaria ao editar o veículo. */}
              <input
                list="vehicle-brands"
                value={form.brand}
                onChange={e => setForm(f => ({ ...f, brand: e.target.value }))}
                placeholder="Fiat"
                required
              />
              <datalist id="vehicle-brands">
                {brands.map(brand => <option key={brand} value={brand} />)}
              </datalist>
            </div>
            <div className="field">
              <label>Modelo *</label>
              <input value={form.model} onChange={e => setForm(f => ({ ...f, model: e.target.value }))} placeholder="Pulse" required />
            </div>
            <div className="field">
              <label>Versão</label>
              <input value={form.version} onChange={e => setForm(f => ({ ...f, version: e.target.value }))} placeholder="Drive 1.3" />
            </div>
            <div className="field">
              <label>Ano/modelo</label>
              <input type="number" value={form.model_year} onChange={e => setForm(f => ({ ...f, model_year: e.target.value }))} placeholder="2024" />
            </div>
            <div className="field">
              <label>Cor</label>
              <input value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))} placeholder="Prata" />
            </div>
            <div className="field">
              <label>Combustível</label>
              {/* O valor gravado entra como opção mesmo se saiu da lista (ou se a
                  lista não carregou): sem isso o select mostraria "Não informado"
                  e o Salvar apagaria o combustível sem ninguém ter mexido. */}
              <select value={form.fuel} onChange={e => setForm(f => ({ ...f, fuel: e.target.value }))}>
                <option value="">Não informado</option>
                {(form.fuel && !fuels.includes(form.fuel) ? [form.fuel, ...fuels] : fuels)
                  .map(fuel => <option key={fuel} value={fuel}>{fuel}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Valor de tabela</label>
              {/* numeric: só dígitos entram, os centavos vêm pela direita. */}
              <input
                inputMode="numeric"
                value={form.price}
                onChange={e => setForm(f => ({ ...f, price: maskCurrency(e.target.value) }))}
                placeholder="R$ 0,00"
              />
              <div className="field-hint">Digite só os números; os dois últimos são os centavos (18500000 = R$ 185.000,00). A busca FIPE preenche com o valor do mês.</div>
            </div>
            <div className="field">
              <label>Quantidade em estoque</label>
              <input type="number" min="0" value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} placeholder="0" />
            </div>
          </div>
          {visibleFeatureGroups.length > 0 && (
            <div className="features-field">
              <label>Opcionais do veículo <span className="field-hint">{form.features.length} marcado(s)</span></label>
              <div className="features-groups">
                {visibleFeatureGroups.map(g => (
                  <fieldset key={g.group} className="features-group">
                    <legend>{g.group}</legend>
                    {g.items.map(item => (
                      <label key={item} className="features-check">
                        <input type="checkbox" checked={form.features.includes(item)} onChange={() => toggleFeature(item)} />
                        {item}
                      </label>
                    ))}
                  </fieldset>
                ))}
              </div>
              <div className="field-hint">Marque o que o veículo possui. O assistente de IA usa esta lista para responder ao cliente — o que não estiver marcado ele confirma com o vendedor.</div>
            </div>
          )}
          <div className="photos-field">
            <label>Fotos <span className="field-hint">{photoCount} de {MAX_VEHICLE_PHOTOS}</span></label>
            <div className="photo-grid">
              {editingId && editingPhotos.map((p, i) => (
                <div key={p.id} className="photo-thumb">
                  <VehiclePhoto vehicleId={editingId} photoId={p.id} alt={`Foto ${i + 1}`} />
                  {i === 0 && <span className="photo-thumb-cover">Capa</span>}
                  <button type="button" className="photo-thumb-remove" title="Remover foto" onClick={() => removePhoto(p.id)}>×</button>
                </div>
              ))}
              {!editingId && pendingPhotos.map((p, i) => (
                <div key={p.key} className="photo-thumb">
                  <img src={p.url} alt={`Foto ${i + 1}`} />
                  {i === 0 && <span className="photo-thumb-cover">Capa</span>}
                  <button type="button" className="photo-thumb-remove" title="Remover foto" onClick={() => removePending(p.key)}>×</button>
                </div>
              ))}
              {photoCount < MAX_VEHICLE_PHOTOS && (hasStore || editingId) && (
                <label className={`photo-add${uploadingPhotos ? ' photo-add--busy' : ''}`}>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    hidden
                    disabled={uploadingPhotos}
                    onChange={e => { const files = Array.from(e.target.files || []); e.target.value = ''; addPhotos(files); }}
                  />
                  <Icon name="plus" size={18} />
                  {uploadingPhotos ? 'Enviando...' : 'Adicionar fotos'}
                </label>
              )}
            </div>
            <div className="field-hint">A primeira foto é a capa. As imagens são reduzidas antes de subir; até {MAX_VEHICLE_PHOTOS} por veículo.</div>
          </div>
          <VehicleMaterialsField {...materialsProps('datasheet')} />
          <VehicleMaterialsField {...materialsProps('commercial')} />
          <div className="form-actions">
            {editingId && editingVehicle && (
              <button type="button" className="btn btn-danger" onClick={() => removeVehicle(editingVehicle)} title="Excluir do catálogo (definitivo)">
                Excluir
              </button>
            )}
            <button type="button" className="btn btn-secondary" onClick={resetForm}>{editingId ? 'Fechar' : 'Cancelar'}</button>
            <button type="submit" className="btn btn-primary" disabled={saving || !form.brand.trim() || !form.model.trim() || (!hasStore && !editingId)}>
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
      )}
    </div>
  );
}

// ─── PROPOSTAS ────────────────────────────────────────────────────────────────
const PROPOSAL_STATUS = {
  draft: { label: 'Rascunho', color: 'gray' },
  sent: { label: 'Enviada', color: 'blue' },
  accepted: { label: 'Aceita', color: 'green' },
  rejected: { label: 'Recusada', color: 'red' },
};

const formatBRL = (value) =>
  value === null || value === undefined || value === ''
    ? null
    : Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// T12:00 evita o susto do fuso: "2026-09-20" sozinho é meia-noite UTC, dia 19
// no Brasil.
const proposalDate = (value) =>
  value ? new Date(`${String(value).slice(0, 10)}T12:00`).toLocaleDateString('pt-BR') : null;

const isoDateInDays = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

// "Vencida" é derivado, não status: enviada e com validade no passado.
const proposalStatusInfo = (p) => {
  if (p.status === 'sent' && p.valid_until && String(p.valid_until).slice(0, 10) < new Date().toISOString().slice(0, 10)) {
    return { label: 'Vencida', color: 'amber' };
  }
  return PROPOSAL_STATUS[p.status] || { label: p.status, color: 'gray' };
};

const describeVehicleRow = (v) => [v.brand, v.model, v.version, v.model_year, v.color].filter(Boolean).join(' ');

const emptyProposal = () => ({
  client_id: '', visit_id: '', seller_id: '', client_label: '',
  vehicle_id: '', vehicle_description: '', vehicle_price: '',
  down_payment: '', installments: '', monthly_rate: '', installment_value: '',
  valid_until: isoDateInDays(7), notes: '', include_photos: true, include_pdf: true,
});

/**
 * Parcela pela Tabela Price (PMT = PV · i / (1 − (1+i)^−n)), a mesma conta de
 * `services/proposalMessage.js` no backend. Sem taxa divide sem juros.
 * Devolve '' (campo vazio) quando não há o que parcelar.
 */
const installmentFor = (form) => {
  const price = Number(unmaskCurrency(form.vehicle_price)) || 0;
  const down = Number(unmaskCurrency(form.down_payment)) || 0;
  const n = Number(form.installments) || 0;
  const pv = price - down;
  if (pv <= 0 || !Number.isInteger(n) || n <= 0) return '';
  const i = Number(String(form.monthly_rate).replace(',', '.')) / 100;
  const pmt = Number.isFinite(i) && i > 0 ? (pv * i) / (1 - Math.pow(1 + i, -n)) : pv / n;
  return maskCurrency(Math.round(pmt * 100));
};

/**
 * Aplica uma mudança nas condições e já preenche o valor da parcela: o campo
 * é editável (o simulador do banco pode dar outro valor), mas mexer em preço,
 * entrada, parcelas ou taxa recalcula e sobrescreve o que estava lá.
 */
const withInstallment = (patch) => (f) => {
  const next = { ...f, ...patch };
  return { ...next, installment_value: installmentFor(next) };
};

/** O formulário (com máscaras) no formato que a API espera. */
const proposalPayload = (form) => ({
  client_id: form.client_id || null,
  visit_id: form.visit_id || null,
  seller_id: form.seller_id || null,
  vehicle_id: form.vehicle_id || null,
  vehicle_description: form.vehicle_description,
  vehicle_price: unmaskCurrency(form.vehicle_price) || null,
  down_payment: unmaskCurrency(form.down_payment) || '0.00',
  installments: form.installments === '' ? 0 : Number(form.installments),
  monthly_rate: form.monthly_rate === '' ? null : form.monthly_rate,
  installment_value: unmaskCurrency(form.installment_value) || null,
  valid_until: form.valid_until || null,
  notes: form.notes,
  include_photos: Boolean(form.include_photos),
  include_pdf: Boolean(form.include_pdf),
});

/**
 * Propostas: veículo + condições, salvas como rascunho e enviadas ao cliente
 * pelo WhatsApp da loja (texto + fotos do veículo), dentro da conversa dele.
 * `initial` vem do botão "Proposta" do inbox, com o cliente já escolhido.
 */
function ProposalsPage({ hasStore = true, isAdmin = false, initial = null, onInitialConsumed }) {
  const [proposals, setProposals] = useState([]);
  const [visits, setVisits] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [form, setForm] = useState(emptyProposal);
  const [editingId, setEditingId] = useState(null);
  const [clientSearch, setClientSearch] = useState('');
  const [preview, setPreview] = useState({ message: '', installment: null, error: null });
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const showStore = new Set(proposals.map(p => p.organization_id)).size > 1;

  const load = useCallback(async () => {
    try {
      const res = await proposalsApi.list(statusFilter ? { status: statusFilter } : undefined);
      setProposals(res.data.proposals);
    } catch (e) {
      setFeedback({ type: 'error', text: 'Erro ao carregar propostas' });
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    visitsApi.list({ limit: 300 }).then(res => setVisits(res.data.visits)).catch(() => setVisits([]));
    vehiclesApi.list().then(res => setVehicles(res.data)).catch(() => setVehicles([]));
    sellersApi.list().then(res => setSellers(res.data)).catch(() => setSellers([]));
  }, []);

  // Vindo do inbox: cliente, visita e vendedor já preenchidos.
  useEffect(() => {
    if (!initial) return;
    setForm({
      ...emptyProposal(),
      client_id: initial.client_id ? String(initial.client_id) : '',
      visit_id: initial.visit_id ? String(initial.visit_id) : '',
      seller_id: initial.seller_id ? String(initial.seller_id) : '',
      client_label: initial.full_name || '',
    });
    setEditingId(null);
    setFeedback(null);
    if (onInitialConsumed) onInitialConsumed();
  }, [initial, onInitialConsumed]);

  // Prévia pelo backend (mesmo texto do envio), com atraso para não bater na
  // API a cada tecla.
  useEffect(() => {
    const ready = form.client_id && unmaskCurrency(form.vehicle_price) && form.vehicle_description.trim();
    if (!ready) { setPreview({ message: '', installment: null, error: null }); return undefined; }
    const timer = setTimeout(async () => {
      try {
        const res = await proposalsApi.preview(proposalPayload(form));
        setPreview({ message: res.data.message, installment: res.data.installment_value, error: null });
      } catch (err) {
        setPreview(p => ({ ...p, error: err.response?.data?.error || 'Erro ao montar a prévia' }));
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [form]);

  const resetForm = () => { setForm(emptyProposal()); setEditingId(null); setClientSearch(''); };

  const filteredVisits = useMemo(() => {
    const term = clientSearch.trim().toLowerCase();
    const digits = clientSearch.replace(/\D/g, '');
    if (!term) return visits.slice(0, 60);
    return visits.filter(v =>
      String(v.full_name || '').toLowerCase().includes(term)
      || (digits && String(v.phone || '').replace(/\D/g, '').includes(digits))
    ).slice(0, 60);
  }, [visits, clientSearch]);

  const pickVisit = (visitId) => {
    const visit = visits.find(v => String(v.id) === String(visitId));
    if (!visit) { setForm(f => ({ ...f, visit_id: '' })); return; }
    setForm(f => ({
      ...f,
      visit_id: String(visit.id),
      client_id: String(visit.client_id),
      client_label: `${visit.full_name} · ${visit.phone}`,
      seller_id: f.seller_id || (visit.seller_id ? String(visit.seller_id) : ''),
    }));
  };

  const pickVehicle = (vehicleId) => {
    const vehicle = vehicles.find(v => String(v.id) === String(vehicleId));
    setForm(f => withInstallment({
      vehicle_id: vehicle ? String(vehicle.id) : '',
      vehicle_description: vehicle ? describeVehicleRow(vehicle) : f.vehicle_description,
      vehicle_price: vehicle && vehicle.price !== null && vehicle.price !== undefined
        ? maskCurrency(Math.round(Number(vehicle.price) * 100))
        : f.vehicle_price,
    })(f));
  };

  const selectedVehicle = vehicles.find(v => String(v.id) === String(form.vehicle_id)) || null;

  const startEdit = (p) => {
    setForm({
      client_id: String(p.client_id),
      visit_id: p.visit_id ? String(p.visit_id) : '',
      seller_id: p.seller_id ? String(p.seller_id) : '',
      client_label: `${p.client_name} · ${p.client_phone}`,
      vehicle_id: p.vehicle_id ? String(p.vehicle_id) : '',
      vehicle_description: p.vehicle_description || '',
      vehicle_price: maskCurrency(Math.round(Number(p.vehicle_price) * 100)),
      down_payment: Number(p.down_payment) > 0 ? maskCurrency(Math.round(Number(p.down_payment) * 100)) : '',
      installments: p.installments ? String(p.installments) : '',
      monthly_rate: p.monthly_rate === null || p.monthly_rate === undefined ? '' : String(Number(p.monthly_rate)),
      installment_value: p.installment_value ? maskCurrency(Math.round(Number(p.installment_value) * 100)) : '',
      valid_until: p.valid_until ? String(p.valid_until).slice(0, 10) : '',
      notes: p.notes || '',
      include_photos: p.include_photos !== false,
      include_pdf: p.include_pdf !== false,
    });
    setEditingId(p.id);
    setFeedback(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const downloadPdf = async (p) => {
    try {
      const res = await proposalsApi.pdf(p.id);
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `proposta-${String(p.id).padStart(5, '0')}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setFeedback({ type: 'error', text: 'Erro ao gerar o PDF da proposta' });
    }
  };

  const sendProposal = async (p) => {
    const extras = [
      p.include_pdf ? 'o PDF' : null,
      p.include_photos && p.photo_count ? `${p.photo_count} foto(s) do veículo` : null,
    ].filter(Boolean);
    const photos = extras.length ? ` com ${extras.join(' e ')}` : '';
    if (!window.confirm(`Enviar a proposta${photos} para ${p.client_name} pelo WhatsApp?`)) return false;
    setSendingId(p.id);
    try {
      const res = await proposalsApi.send(p.id);
      const d = res.data;
      const pdf = d.pdf_sent ? ' + PDF' : (d.pdf_error ? ` (PDF não saiu: ${d.pdf_error})` : '');
      const sent = (d.photos_sent ? ` + ${d.photos_sent} foto(s)` : '') + pdf;
      const failed = d.photos_failed ? ` (${d.photos_failed} foto(s) falharam)` : '';
      const sim = d.simulated ? ' Modo simulação: a loja não tem WhatsApp configurado, nada chegou ao cliente.' : '';
      setFeedback({ type: 'success', text: `Proposta enviada a ${d.client_name}: mensagem${sent}${failed}.${sim}` });
      return true;
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.error || 'Erro ao enviar a proposta' });
      return false;
    } finally {
      setSendingId(null);
      load();
    }
  };

  const handleSubmit = async (sendAfter) => {
    setSaving(true);
    setFeedback(null);
    try {
      const payload = proposalPayload(form);
      const saved = editingId
        ? (await proposalsApi.update(editingId, payload)).data
        : (await proposalsApi.create(payload)).data;
      if (sendAfter) {
        const ok = await sendProposal(saved);
        if (!ok) { setEditingId(saved.id); return; }
      } else {
        setFeedback({ type: 'success', text: `Proposta para ${saved.client_name} salva.` });
      }
      resetForm();
      load();
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.error || 'Erro ao salvar a proposta' });
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (p, status) => {
    try {
      await proposalsApi.update(p.id, { status });
      load();
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.error || 'Erro ao atualizar a proposta' });
    }
  };

  const removeProposal = async (p) => {
    if (!window.confirm(`Excluir a proposta de ${p.client_name} (${p.vehicle_description})?`)) return;
    try {
      await proposalsApi.remove(p.id);
      if (editingId === p.id) resetForm();
      setFeedback({ type: 'success', text: 'Proposta excluída.' });
      load();
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.error || 'Erro ao excluir a proposta' });
    }
  };

  const price = Number(unmaskCurrency(form.vehicle_price)) || 0;
  const down = Number(unmaskCurrency(form.down_payment)) || 0;
  const installments = Number(form.installments) || 0;
  const installmentValue = Number(unmaskCurrency(form.installment_value)) || preview.installment || 0;
  const canSave = !saving && form.client_id && price > 0 && form.vehicle_description.trim() && (hasStore || editingId);
  const money = (e) => maskCurrency(e.target.value);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Propostas</h1>
          <p>Monte a proposta com o veículo, entrada e parcelas, e envie ao cliente pelo WhatsApp.</p>
        </div>
      </div>

      {feedback && (
        <div className={`alert alert-${feedback.type}`}>
          <Icon name={feedback.type === 'success' ? 'check' : 'x'} size={18} />
          <div>{feedback.text}</div>
        </div>
      )}

      <div className="form-card">
        <h3>
          <Icon name={editingId ? 'edit' : 'plus'} size={16} />
          {editingId ? 'Editando proposta' : 'Nova proposta'}
        </h3>
        {!hasStore && !editingId && (
          <div className="field-hint" style={{ marginBottom: 12 }}>
            Escolha a <strong>loja ativa</strong> no menu à esquerda para criar uma proposta.
          </div>
        )}
        <form onSubmit={e => { e.preventDefault(); handleSubmit(false); }}>
          <div className="form-grid">
            <div className="field field--full">
              <label>Cliente *</label>
              <div className="proposal-client-row">
                <input
                  className="search-input"
                  value={clientSearch}
                  onChange={e => setClientSearch(e.target.value)}
                  placeholder="Buscar por nome ou telefone"
                />
                <select value={form.visit_id} onChange={e => pickVisit(e.target.value)}>
                  <option value="">{form.client_label ? `Selecionado: ${form.client_label}` : 'Escolha o cliente (pela visita)…'}</option>
                  {filteredVisits.map(v => (
                    <option key={v.id} value={v.id}>
                      {v.full_name} · {v.phone} · {v.vehicle_interest || 'sem veículo'} ({new Date(v.visit_date).toLocaleDateString('pt-BR')})
                    </option>
                  ))}
                </select>
              </div>
              <div className="field-hint">A lista vem das visitas registradas. A proposta entra na conversa do cliente no inbox.</div>
            </div>
            <div className="field">
              <label>Veículo do catálogo</label>
              <select value={form.vehicle_id} onChange={e => pickVehicle(e.target.value)}>
                <option value="">Sem vínculo com o catálogo</option>
                {vehicles.map(v => (
                  <option key={v.id} value={v.id}>
                    {describeVehicleRow(v)}{v.photos?.length ? ` · ${v.photos.length} foto(s)` : ''}
                  </option>
                ))}
              </select>
              <div className="field-hint">Preenche descrição, valor e fotos. Pode ajustar depois.</div>
            </div>
            <div className="field">
              <label>Descrição do veículo *</label>
              <input
                value={form.vehicle_description}
                onChange={e => setForm(f => ({ ...f, vehicle_description: e.target.value }))}
                placeholder="Toyota Corolla XEi 2.0 2025 Branco"
                required
              />
            </div>
            <div className="field">
              <label>Valor do veículo *</label>
              <input inputMode="numeric" value={form.vehicle_price} onChange={e => setForm(withInstallment({ vehicle_price: money(e) }))} placeholder="R$ 0,00" required />
            </div>
            <div className="field">
              <label>Entrada</label>
              <input inputMode="numeric" value={form.down_payment} onChange={e => setForm(withInstallment({ down_payment: money(e) }))} placeholder="R$ 0,00" />
              {price > 0 && <div className="field-hint">Saldo a financiar: {formatBRL(Math.max(price - down, 0))}</div>}
            </div>
            <div className="field">
              <label>Nº de parcelas</label>
              <input type="number" min="0" max="120" value={form.installments} onChange={e => setForm(withInstallment({ installments: e.target.value }))} placeholder="48" />
            </div>
            <div className="field">
              <label>Taxa ao mês (%)</label>
              <input type="number" step="0.01" min="0" value={form.monthly_rate} onChange={e => setForm(withInstallment({ monthly_rate: e.target.value }))} placeholder="1,99" />
              <div className="field-hint">Opcional: calcula a parcela pela Tabela Price. Em branco divide sem juros.</div>
            </div>
            <div className="field">
              <label>Valor da parcela</label>
              <input
                inputMode="numeric"
                value={form.installment_value}
                onChange={e => setForm(f => ({ ...f, installment_value: money(e) }))}
                placeholder="R$ 0,00"
              />
              <div className="field-hint">Preenchido pela Tabela Price ao informar valor, entrada, parcelas e taxa. Ajuste se o simulador do banco der outro número.</div>
            </div>
            <div className="field">
              <label>Válida até</label>
              <input type="date" value={form.valid_until} onChange={e => setForm(f => ({ ...f, valid_until: e.target.value }))} />
            </div>
            <div className="field">
              <label>Vendedor</label>
              <select value={form.seller_id} onChange={e => setForm(f => ({ ...f, seller_id: e.target.value }))}>
                <option value="">Sem vendedor</option>
                {sellers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <div className="field-hint">Assina a mensagem e recebe a conversa quando a proposta sai.</div>
            </div>
            <div className="field field--full">
              <label>Observações</label>
              <textarea rows={3} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Inclui emplacamento, tanque cheio, primeira revisão grátis…" />
            </div>
            <div className="field field--full">
              <label className="proposal-check">
                <input type="checkbox" checked={form.include_pdf} onChange={e => setForm(f => ({ ...f, include_pdf: e.target.checked }))} />
                Anexar a proposta em PDF (documento no WhatsApp, com a ficha da loja e as fotos)
              </label>
            </div>
            {selectedVehicle?.photos?.length > 0 && (
              <div className="field field--full">
                <label className="proposal-check">
                  <input type="checkbox" checked={form.include_photos} onChange={e => setForm(f => ({ ...f, include_photos: e.target.checked }))} />
                  Enviar as {selectedVehicle.photos.length} foto(s) do veículo junto com a proposta
                </label>
                <div className="photo-grid photo-grid--compact">
                  {selectedVehicle.photos.slice(0, 10).map((ph, i) => (
                    <div key={ph.id} className="photo-thumb">
                      <VehiclePhoto vehicleId={selectedVehicle.id} photoId={ph.id} alt={`Foto ${i + 1}`} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="proposal-preview">
            <label>Mensagem que o cliente vai receber</label>
            {preview.error
              ? <div className="field-hint field-hint--error">{preview.error}</div>
              : preview.message
                ? <pre>{preview.message}</pre>
                : <div className="field-hint">Escolha o cliente, o veículo e o valor para ver a prévia.</div>}
            {installments > 0 && installmentValue > 0 && (
              <div className="field-hint">
                Total da proposta: {formatBRL(down + installments * installmentValue)} ({formatBRL(down)} de entrada + {installments}x {formatBRL(installmentValue)})
              </div>
            )}
          </div>

          <div className="form-actions">
            {editingId && (
              <button type="button" className="btn btn-secondary" onClick={resetForm}>Cancelar edição</button>
            )}
            <button type="submit" className="btn btn-secondary" disabled={!canSave}>
              {saving ? 'Salvando...' : editingId ? 'Salvar' : 'Salvar rascunho'}
            </button>
            <button type="button" className="btn btn-primary" disabled={!canSave} onClick={() => handleSubmit(true)}>
              <Icon name="send" size={14} /> Salvar e enviar
            </button>
          </div>
        </form>
      </div>

      {loading ? <SkeletonList rows={4} /> : (
        <div className="list-card">
          <div className="list-card-head list-card-head--filters">
            <h3>Propostas <span className="badge">{proposals.length}</span></h3>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="">Todas</option>
              {Object.entries(PROPOSAL_STATUS).map(([value, s]) => <option key={value} value={value}>{s.label}</option>)}
            </select>
          </div>
          {proposals.length === 0 ? (
            <EmptyState
              icon="file"
              title="Nenhuma proposta"
              hint="Monte a primeira no formulário acima, ou pelo botão Proposta no cabeçalho de uma conversa."
            />
          ) : (
            <ul className="todo-list">
              {proposals.map(p => {
                const st = proposalStatusInfo(p);
                return (
                  <li key={p.id} className={`todo-item${editingId === p.id ? ' todo-item--editing' : ''}`}>
                    <div className="todo-mark"><Icon name="file" size={18} /></div>
                    <div className="todo-body">
                      <div className="todo-title">
                        {p.client_name}
                        <span className="todo-title-soft"> · {p.vehicle_description}</span>
                        <span className={`status-badge status-badge--${st.color} ml-8`}>{st.label}</span>
                      </div>
                      <div className="todo-meta">
                        {showStore && <span>{p.store_name}</span>}
                        <span>{formatBRL(p.vehicle_price)}</span>
                        {Number(p.down_payment) > 0 && <span>Entrada {formatBRL(p.down_payment)}</span>}
                        {p.installments > 0 && <span>{p.installments}x de {formatBRL(p.installment_value)}</span>}
                        {p.seller_name && <span>👤 {p.seller_name}</span>}
                        {p.include_photos && p.photo_count > 0 && <span>📷 {p.photo_count}</span>}
                        {p.include_pdf && <span>📎 PDF</span>}
                        {p.sent_at && <span>Enviada {new Date(p.sent_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>}
                        {p.valid_until && <span>Válida até {proposalDate(p.valid_until)}</span>}
                      </div>
                    </div>
                    <div className="todo-actions">
                      <button className="btn btn-secondary btn-sm" onClick={() => startEdit(p)}>
                        <Icon name="edit" size={14} /> Editar
                      </button>
                      <button className="btn btn-secondary btn-sm" title="Baixar a proposta em PDF" onClick={() => downloadPdf(p)}>
                        <Icon name="download" size={14} /> PDF
                      </button>
                      <button
                        className="btn btn-primary btn-sm"
                        disabled={sendingId === p.id || p.client_opted_out}
                        title={p.client_opted_out ? 'O cliente pediu para não receber mensagens' : 'Enviar pelo WhatsApp'}
                        onClick={() => sendProposal(p)}
                      >
                        <Icon name="send" size={14} /> {sendingId === p.id ? 'Enviando...' : p.status === 'draft' ? 'Enviar' : 'Reenviar'}
                      </button>
                      {p.status !== 'draft' && p.status !== 'accepted' && (
                        <button className="btn btn-secondary btn-sm" onClick={() => setStatus(p, 'accepted')}>Aceita</button>
                      )}
                      {p.status !== 'draft' && p.status !== 'rejected' && (
                        <button className="btn btn-secondary btn-sm" onClick={() => setStatus(p, 'rejected')}>Recusada</button>
                      )}
                      {(p.status === 'draft' || isAdmin) && (
                        <button className="btn btn-danger btn-sm" onClick={() => removeProposal(p)}>Excluir</button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ─── TEST-DRIVES ──────────────────────────────────────────────────────────────
const TEST_DRIVE_STATUS = {
  scheduled: { label: 'Agendado', color: 'blue' },
  confirmed: { label: 'Confirmado', color: 'green' },
  completed: { label: 'Realizado', color: 'purple' },
  no_show: { label: 'Não compareceu', color: 'amber' },
  canceled: { label: 'Cancelado', color: 'red' },
};
const TEST_DRIVE_ACTIVE = new Set(['scheduled', 'confirmed']);
const TD_TZ = 'America/Sao_Paulo';

// ISO do banco → "2026-09-26T15:00" em Brasília, o formato do datetime-local.
// O backend lê esse formato como horário de Brasília, então ida e volta batem
// mesmo num navegador configurado em outro fuso.
const tdLocalInput = (value) => {
  if (!value) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: TD_TZ, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(new Date(value));
  const get = (t) => parts.find(p => p.type === t)?.value;
  const hour = get('hour') === '24' ? '00' : get('hour');
  return `${get('year')}-${get('month')}-${get('day')}T${hour}:${get('minute')}`;
};

const tdWhen = (value) => new Date(value).toLocaleString('pt-BR', {
  timeZone: TD_TZ, weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
});

const tdDayKey = (value) => new Date(value).toLocaleDateString('pt-BR', { timeZone: TD_TZ, weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' });

// Sugestão inicial do formulário: amanhã às 10h.
const tdDefaultSlot = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${tdLocalInput(d).slice(0, 10)}T10:00`;
};

const emptyTestDrive = () => ({
  client_id: '', visit_id: '', seller_id: '', client_label: '',
  vehicle_id: '', vehicle_description: '', scheduled_at: tdDefaultSlot(), notes: '', send_confirmation: true,
});

const testDrivePayload = (form) => ({
  client_id: form.client_id || null,
  visit_id: form.visit_id || null,
  seller_id: form.seller_id || null,
  vehicle_id: form.vehicle_id || null,
  vehicle_description: form.vehicle_description,
  scheduled_at: form.scheduled_at,
  notes: form.notes,
  send_confirmation: Boolean(form.send_confirmation),
});

/** Como o painel resume o resultado do envio da confirmação, após salvar. */
const confirmationNote = (d) => {
  if (d.confirmation_skipped === 'opted_out') return ' O cliente pediu para não receber mensagens, então a confirmação não foi enviada.';
  if (d.confirmation_error) return ` A confirmação não saiu pelo WhatsApp: ${d.confirmation_error}.`;
  if (d.confirmation_sent && d.simulated) return ' Modo simulação: a loja não tem WhatsApp configurado, a confirmação não chegou ao cliente.';
  if (d.confirmation_sent) return ' Confirmação enviada ao cliente pelo WhatsApp.';
  return '';
};

/**
 * Test-drives: agenda da loja. O vendedor (ou a recepção) marca aqui à mão; o
 * assistente de IA marca pela conversa e o registro cai na mesma lista, com a
 * etiqueta "IA". Em qualquer caso o cliente recebe a mesma mensagem de
 * confirmação pelo WhatsApp da loja e o vendedor é avisado no WhatsApp dele.
 * `initial` vem do botão "Test-drive" do inbox, com o cliente já escolhido.
 */
function TestDrivesPage({ hasStore = true, isAdmin = false, initial = null, onInitialConsumed }) {
  const [items, setItems] = useState([]);
  const [visits, setVisits] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [form, setForm] = useState(emptyTestDrive);
  const [editingId, setEditingId] = useState(null);
  const [clientSearch, setClientSearch] = useState('');
  const [view, setView] = useState('upcoming');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const showStore = new Set(items.map(t => t.organization_id)).size > 1;

  const load = useCallback(async () => {
    try {
      // Sem período a API traz de 30 dias atrás em diante; "Histórico" pede
      // mais passado para o vendedor rever o mês.
      const params = view === 'history' ? { from: isoDateInDays(-120), to: isoDateInDays(0) } : undefined;
      const res = await testDrivesApi.list(params);
      setItems(res.data.test_drives);
    } catch (e) {
      setFeedback({ type: 'error', text: 'Erro ao carregar os test-drives' });
    } finally {
      setLoading(false);
    }
  }, [view]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    visitsApi.list({ limit: 300 }).then(res => setVisits(res.data.visits)).catch(() => setVisits([]));
    vehiclesApi.list().then(res => setVehicles(res.data)).catch(() => setVehicles([]));
    sellersApi.list().then(res => setSellers(res.data)).catch(() => setSellers([]));
  }, []);

  // Vindo do inbox: cliente, visita, vendedor e veículo de interesse já preenchidos.
  useEffect(() => {
    if (!initial) return;
    setForm({
      ...emptyTestDrive(),
      client_id: initial.client_id ? String(initial.client_id) : '',
      visit_id: initial.visit_id ? String(initial.visit_id) : '',
      seller_id: initial.seller_id ? String(initial.seller_id) : '',
      client_label: [initial.full_name, initial.phone].filter(Boolean).join(' · '),
      vehicle_description: initial.vehicle_interest || '',
    });
    setEditingId(null);
    setFeedback(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (onInitialConsumed) onInitialConsumed();
  }, [initial, onInitialConsumed]);

  const resetForm = () => { setForm(emptyTestDrive()); setEditingId(null); setClientSearch(''); };

  const filteredVisits = useMemo(() => {
    const term = clientSearch.trim().toLowerCase();
    const digits = clientSearch.replace(/\D/g, '');
    if (!term) return visits.slice(0, 60);
    return visits.filter(v =>
      String(v.full_name || '').toLowerCase().includes(term)
      || (digits && String(v.phone || '').replace(/\D/g, '').includes(digits))
    ).slice(0, 60);
  }, [visits, clientSearch]);

  const pickVisit = (visitId) => {
    const visit = visits.find(v => String(v.id) === String(visitId));
    if (!visit) { setForm(f => ({ ...f, visit_id: '' })); return; }
    setForm(f => ({
      ...f,
      visit_id: String(visit.id),
      client_id: String(visit.client_id),
      client_label: `${visit.full_name} · ${visit.phone}`,
      seller_id: f.seller_id || (visit.seller_id ? String(visit.seller_id) : ''),
      vehicle_description: f.vehicle_description || visit.vehicle_interest || '',
    }));
  };

  const pickVehicle = (vehicleId) => {
    const vehicle = vehicles.find(v => String(v.id) === String(vehicleId));
    setForm(f => ({
      ...f,
      vehicle_id: vehicle ? String(vehicle.id) : '',
      vehicle_description: vehicle ? describeVehicleRow(vehicle) : f.vehicle_description,
    }));
  };

  const startEdit = (t) => {
    setForm({
      client_id: String(t.client_id),
      visit_id: t.visit_id ? String(t.visit_id) : '',
      seller_id: t.seller_id ? String(t.seller_id) : '',
      client_label: `${t.client_name} · ${t.client_phone}`,
      vehicle_id: t.vehicle_id ? String(t.vehicle_id) : '',
      vehicle_description: t.vehicle_description || '',
      scheduled_at: tdLocalInput(t.scheduled_at),
      notes: t.notes || '',
      send_confirmation: true,
    });
    setEditingId(t.id);
    setFeedback(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFeedback(null);
    try {
      const payload = testDrivePayload(form);
      const res = editingId
        ? await testDrivesApi.update(editingId, payload)
        : await testDrivesApi.create(payload);
      const d = res.data;
      setFeedback({
        type: 'success',
        text: `Test-drive de ${d.client_name} ${editingId ? 'atualizado' : 'agendado'} para ${tdWhen(d.scheduled_at)}.${confirmationNote(d)}`,
      });
      resetForm();
      load();
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.error || 'Erro ao salvar o test-drive' });
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (t, status) => {
    let notifyClient = false;
    if (status === 'canceled') {
      if (!window.confirm(`Cancelar o test-drive de ${t.client_name} (${tdWhen(t.scheduled_at)})?`)) return;
      notifyClient = !t.client_opted_out && window.confirm('Avisar o cliente do cancelamento pelo WhatsApp?');
    }
    setBusyId(t.id);
    try {
      const res = await testDrivesApi.update(t.id, { status, notify_client: notifyClient });
      const d = res.data;
      setFeedback({ type: 'success', text: `Test-drive de ${d.client_name} marcado como "${TEST_DRIVE_STATUS[status].label}".${status === 'canceled' && notifyClient ? confirmationNote({ ...d, confirmation_sent: d.confirmation_sent }) : ''}` });
      if (editingId === t.id) resetForm();
      load();
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.error || 'Erro ao atualizar o test-drive' });
    } finally {
      setBusyId(null);
    }
  };

  const resend = async (t) => {
    if (!window.confirm(`Reenviar a confirmação do test-drive para ${t.client_name} pelo WhatsApp?`)) return;
    setBusyId(t.id);
    try {
      const res = await testDrivesApi.resendConfirmation(t.id);
      setFeedback({ type: 'success', text: `Confirmação reenviada a ${res.data.client_name}.${res.data.simulated ? ' Modo simulação: a loja não tem WhatsApp configurado, nada chegou ao cliente.' : ''}` });
      load();
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.error || 'Erro ao reenviar a confirmação' });
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Excluir definitivamente o test-drive de ${t.client_name} (${tdWhen(t.scheduled_at)})? Cancelar mantém o histórico; excluir apaga.`)) return;
    setBusyId(t.id);
    try {
      await testDrivesApi.remove(t.id);
      if (editingId === t.id) resetForm();
      setFeedback({ type: 'success', text: 'Test-drive excluído.' });
      load();
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.error || 'Erro ao excluir o test-drive' });
    } finally {
      setBusyId(null);
    }
  };

  // "Próximos" são os ativos ainda por vir (com 1h de folga para quem está
  // acontecendo agora); o resto é histórico, do mais recente ao mais antigo.
  const cutoff = Date.now() - 60 * 60 * 1000;
  const upcoming = items
    .filter(t => TEST_DRIVE_ACTIVE.has(t.status) && new Date(t.scheduled_at).getTime() >= cutoff)
    .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
  const history = items
    .filter(t => !(TEST_DRIVE_ACTIVE.has(t.status) && new Date(t.scheduled_at).getTime() >= cutoff))
    .sort((a, b) => new Date(b.scheduled_at) - new Date(a.scheduled_at));
  const shown = view === 'upcoming' ? upcoming : history;

  // Os próximos agrupados por dia, para a agenda ler como agenda.
  const grouped = useMemo(() => {
    const map = new Map();
    for (const t of shown) {
      const key = tdDayKey(t.scheduled_at);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(t);
    }
    return Array.from(map.entries());
  }, [shown]);

  const canSave = !saving && form.client_id && form.vehicle_description.trim() && form.scheduled_at && (hasStore || editingId);
  const selectedVehicle = vehicles.find(v => String(v.id) === String(form.vehicle_id)) || null;

  const renderItem = (t) => {
    const st = TEST_DRIVE_STATUS[t.status] || { label: t.status, color: 'gray' };
    const active = TEST_DRIVE_ACTIVE.has(t.status);
    const busy = busyId === t.id;
    return (
      <li key={t.id} className={`todo-item${editingId === t.id ? ' todo-item--editing' : ''}${active ? '' : ' todo-item--inactive'}`}>
        <div className="todo-mark"><Icon name="calendar" size={18} /></div>
        <div className="todo-body">
          <div className="todo-title">
            {new Date(t.scheduled_at).toLocaleTimeString('pt-BR', { timeZone: TD_TZ, hour: '2-digit', minute: '2-digit' })}
            {' · '}{t.client_name}
            <span className="todo-title-soft"> · {t.vehicle_description}</span>
            <span className={`status-badge status-badge--${st.color} ml-8`}>{st.label}</span>
            {t.source === 'ai' && <span className="status-badge status-badge--purple ml-8" title="Agendado pelo assistente de IA na conversa do cliente">🤖 IA</span>}
          </div>
          <div className="todo-meta">
            {showStore && <span>{t.store_name}</span>}
            <span>{tdWhen(t.scheduled_at)}</span>
            <span>📞 {t.client_phone}</span>
            {t.seller_name ? <span>👤 {t.seller_name}</span> : <span className="todo-meta--muted">sem vendedor</span>}
            {t.confirmation_sent_at
              ? <span title={new Date(t.confirmation_sent_at).toLocaleString('pt-BR')}>✅ Confirmação enviada</span>
              : <span className="todo-meta--muted">confirmação não enviada</span>}
            {t.created_by_name && <span>por {t.created_by_name}</span>}
            {t.notes && <span title={t.notes}>📝 {t.notes.length > 60 ? `${t.notes.slice(0, 60)}…` : t.notes}</span>}
          </div>
        </div>
        <div className="todo-actions">
          {active && (
            <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => startEdit(t)}>
              <Icon name="edit" size={14} /> Editar
            </button>
          )}
          {active && (
            <button className="btn btn-secondary btn-sm" disabled={busy || t.client_opted_out} title={t.client_opted_out ? 'O cliente pediu para não receber mensagens' : 'Reenviar a confirmação pelo WhatsApp'} onClick={() => resend(t)}>
              <Icon name="send" size={14} /> Confirmação
            </button>
          )}
          {t.status === 'scheduled' && (
            <button className="btn btn-secondary btn-sm" disabled={busy} title="O cliente confirmou que vem" onClick={() => setStatus(t, 'confirmed')}>Confirmado</button>
          )}
          {active && (
            <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => setStatus(t, 'completed')}>Realizado</button>
          )}
          {active && (
            <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => setStatus(t, 'no_show')}>Não veio</button>
          )}
          {active && (
            <button className="btn btn-danger btn-sm" disabled={busy} onClick={() => setStatus(t, 'canceled')}>Cancelar</button>
          )}
          {!active && isAdmin && (
            <button className="btn btn-danger btn-sm" disabled={busy} onClick={() => remove(t)}>Excluir</button>
          )}
        </div>
      </li>
    );
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Test-drives</h1>
          <p>Agende o test-drive do cliente: ele recebe a confirmação pelo WhatsApp da loja e o vendedor é avisado. O assistente de IA também marca por aqui, direto na conversa.</p>
        </div>
      </div>

      {feedback && (
        <div className={`alert alert-${feedback.type}`}>
          <Icon name={feedback.type === 'success' ? 'check' : 'x'} size={18} />
          <div>{feedback.text}</div>
        </div>
      )}

      <div className="form-card">
        <h3>
          <Icon name={editingId ? 'edit' : 'plus'} size={16} />
          {editingId ? 'Editando test-drive' : 'Novo test-drive'}
        </h3>
        {!hasStore && !editingId && (
          <div className="field-hint" style={{ marginBottom: 12 }}>
            Escolha a <strong>loja ativa</strong> no menu à esquerda para agendar um test-drive.
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="field field--full">
              <label>Cliente *</label>
              <div className="proposal-client-row">
                <input
                  className="search-input"
                  value={clientSearch}
                  onChange={e => setClientSearch(e.target.value)}
                  placeholder="Buscar por nome ou telefone"
                />
                <select value={form.visit_id} onChange={e => pickVisit(e.target.value)}>
                  <option value="">{form.client_label ? `Selecionado: ${form.client_label}` : 'Escolha o cliente (pela visita)…'}</option>
                  {filteredVisits.map(v => (
                    <option key={v.id} value={v.id}>
                      {v.full_name} · {v.phone} · {v.vehicle_interest || 'sem veículo'} ({new Date(v.visit_date).toLocaleDateString('pt-BR')})
                    </option>
                  ))}
                </select>
              </div>
              <div className="field-hint">A lista vem das visitas registradas. A confirmação entra na conversa do cliente no inbox.</div>
            </div>
            <div className="field">
              <label>Veículo do catálogo</label>
              <select value={form.vehicle_id} onChange={e => pickVehicle(e.target.value)}>
                <option value="">Sem vínculo com o catálogo</option>
                {vehicles.filter(v => v.active !== false || String(v.id) === String(form.vehicle_id)).map(v => (
                  <option key={v.id} value={v.id}>{describeVehicleRow(v)}</option>
                ))}
              </select>
              <div className="field-hint">Preenche a descrição. Pode ajustar ao lado.</div>
            </div>
            <div className="field">
              <label>Veículo do test-drive *</label>
              <input
                value={form.vehicle_description}
                onChange={e => setForm(f => ({ ...f, vehicle_description: e.target.value, vehicle_id: selectedVehicle && describeVehicleRow(selectedVehicle) !== e.target.value ? '' : f.vehicle_id }))}
                placeholder="Toyota Corolla XEi 2026"
                required
              />
            </div>
            <div className="field">
              <label>Data e hora *</label>
              <input
                type="datetime-local"
                value={form.scheduled_at}
                min={tdLocalInput(new Date()).slice(0, 16)}
                onChange={e => setForm(f => ({ ...f, scheduled_at: e.target.value }))}
                required
              />
              <div className="field-hint">Horário de Brasília.</div>
            </div>
            <div className="field">
              <label>Vendedor que acompanha</label>
              <select value={form.seller_id} onChange={e => setForm(f => ({ ...f, seller_id: e.target.value }))}>
                <option value="">Sem vendedor</option>
                {sellers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <div className="field-hint">Aparece na confirmação ao cliente e recebe o aviso no WhatsApp dele.</div>
            </div>
            <div className="field field--full">
              <label>Observações (internas)</label>
              <textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Cliente quer testar na estrada; trazer o automático também…" />
            </div>
            <div className="field field--full">
              <label className="proposal-check">
                <input type="checkbox" checked={form.send_confirmation} onChange={e => setForm(f => ({ ...f, send_confirmation: e.target.checked }))} />
                {editingId
                  ? 'Se o horário mudar, enviar ao cliente a mensagem de reagendamento pelo WhatsApp'
                  : 'Enviar ao cliente a confirmação pelo WhatsApp (veículo, horário, endereço da loja e vendedor)'}
              </label>
            </div>
          </div>

          <div className="form-actions">
            {editingId && (
              <button type="button" className="btn btn-secondary" onClick={resetForm}>Cancelar edição</button>
            )}
            <button type="submit" className="btn btn-primary" disabled={!canSave}>
              <Icon name="calendar" size={14} /> {saving ? 'Salvando...' : editingId ? 'Salvar alterações' : 'Agendar test-drive'}
            </button>
          </div>
        </form>
      </div>

      {loading ? <SkeletonList rows={4} /> : (
        <div className="list-card">
          <div className="list-card-head list-card-head--filters">
            <h3>{view === 'upcoming' ? 'Próximos' : 'Histórico'} <span className="badge">{shown.length}</span></h3>
            <select value={view} onChange={e => setView(e.target.value)}>
              <option value="upcoming">Próximos</option>
              <option value="history">Histórico</option>
            </select>
          </div>
          {shown.length === 0 ? (
            <EmptyState
              icon="calendar"
              title={view === 'upcoming' ? 'Nenhum test-drive agendado' : 'Nenhum test-drive no histórico'}
              hint={view === 'upcoming' ? 'Agende o primeiro no formulário acima, ou pelo botão Test-drive no cabeçalho de uma conversa.' : 'Os realizados, cancelados e faltas dos últimos 120 dias aparecem aqui.'}
            />
          ) : (
            grouped.map(([day, list]) => (
              <div key={day}>
                <div className="field-hint" style={{ marginTop: 14, textTransform: 'capitalize' }}>{day}</div>
                <ul className="todo-list">{list.map(renderItem)}</ul>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ─── CONFIGURAÇÕES ────────────────────────────────────────────────────────────
// ─── RELATÓRIOS ───────────────────────────────────────────────────────────────

/**
 * Percentual inteiro, ou "—" quando não há base: "0%" de zero follow-up enviado é
 * uma afirmação falsa sobre a equipe, e num relatório isso vira cobrança.
 */
const rate = (part, total) => (total > 0 ? `${Math.round((part / total) * 100)}%` : '—');

// T12:00 evita o susto do fuso: "2026-08-14" sozinho é meia-noite UTC, que no
// Brasil ainda é dia 13 — o relatório mostraria o dia anterior.
const longDate = (value) =>
  new Date(`${String(value).slice(0, 10)}T12:00`).toLocaleDateString('pt-BR');

// Para o que vem do banco com hora (visit_date): aqui a conversão de fuso é a
// certa, e não o T12:00 — uma visita das 23h não pode aparecer no dia seguinte.
const stampDate = (value) => new Date(value).toLocaleDateString('pt-BR');

/** Como o cabeçalho descreve o período escolhido. Sem filtro, o padrão do backend. */
const periodLabel = (range) =>
  !range.from && !range.to
    ? 'Últimos 30 dias'
    : `De ${range.from ? longDate(range.from) : 'o início'} até ${range.to ? longDate(range.to) : 'hoje'}`;

/**
 * Filtro de período dos relatórios — o mesmo par de datas nas duas telas.
 *
 * `onClear` limpa tudo o que a tela tiver de filtro, não só as datas: em
 * Conversão por Vendedor, um "Limpar" que deixasse os vendedores marcados
 * devolveria uma tela que continua filtrada dizendo que não está.
 */
function ReportRange({ range, setRange, active, onClear, children }) {
  const hasFilter = active ?? Boolean(range.from || range.to);
  return (
    <div className="chart-filter">
      <div className="chart-filter-row">
        <label>
          <span>De</span>
          <input
            type="date"
            value={range.from}
            max={range.to || undefined}
            onChange={e => setRange(r => ({ ...r, from: e.target.value }))}
          />
        </label>
        <label>
          <span>Até</span>
          <input
            type="date"
            value={range.to}
            min={range.from || undefined}
            onChange={e => setRange(r => ({ ...r, to: e.target.value }))}
          />
        </label>
        {hasFilter && (
          <button
            className="btn btn-secondary btn-sm"
            onClick={onClear || (() => setRange({ from: '', to: '' }))}
          >
            Limpar
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

/**
 * Carrega um relatório para o período escolhido.
 *
 * A espera de 350ms existe porque o campo de data avisa a cada dígito: digitar o
 * ano "2026" mandava quatro consultas ao servidor, três delas de anos como 0202.
 * Com a pausa, sai só a última.
 *
 * `cancelled` protege a troca rápida de datas: sem ele, uma resposta lenta do
 * período antigo chegaria depois da nova e sobrescreveria a tela com números que
 * não são mais os pedidos.
 */
function useReport(fetcher, params) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  // Os parâmetros viram string para servirem de dependência: um objeto novo a
  // cada render dispararia o efeito sem parar, e listar campo a campo obrigaria a
  // mexer aqui toda vez que um relatório ganhasse um filtro.
  const key = JSON.stringify(params);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const timer = setTimeout(() => {
      fetcher(JSON.parse(key))
        .then(res => { if (!cancelled) { setData(res.data); setError(''); } })
        .catch(err => {
          if (cancelled) return;
          setData(null);
          setError(apiErrorMessage(err, 'Erro ao carregar o relatório'));
        })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 350);

    return () => { cancelled = true; clearTimeout(timer); };
  }, [fetcher, key]);

  return { data, loading, error };
}

/**
 * Botão "Baixar PDF" dos relatórios.
 *
 * O arquivo sai do que já está carregado na tela, então o botão fica desligado
 * enquanto o relatório carrega: gerar um PDF de dados pela metade daria uma folha
 * com números que nunca existiram.
 */
function PdfButton({ onExport, disabled }) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const handleClick = async () => {
    setBusy(true);
    setFailed(false);
    try {
      await onExport();
    } catch (err) {
      console.error('Erro ao gerar o PDF:', err);
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <button className="btn btn-secondary" onClick={handleClick} disabled={disabled || busy}>
        <Icon name="download" size={16} />
        {busy ? 'Gerando...' : 'Baixar PDF'}
      </button>
      {failed && <div className="field-hint field-hint--error">Não foi possível gerar o PDF.</div>}
    </div>
  );
}

/** Cartões de números do topo dos relatórios — mesma moldura do dashboard. */
function ReportCards({ cards }) {
  return (
    <div className="stats-grid">
      {cards.map(card => (
        <div key={card.label} className={`stat-card stat-card--${card.color}`}>
          <div className="stat-icon"><Icon name={card.icon} size={24} /></div>
          <div className="stat-value">{card.value}</div>
          <div className="stat-label">{card.label}</div>
        </div>
      ))}
    </div>
  );
}

/**
 * Relatório de conversão por vendedor.
 *
 * A taxa sozinha mente: um vendedor com metade das visitas ainda em andamento
 * aparece pior do que está. Por isso a tabela traz o funil inteiro — convertidas,
 * perdidas e em aberto — e a taxa fica ao lado, não no lugar dele.
 */
function SellerConversionReport({ user }) {
  const [range, setRange] = useState({ from: '', to: '' });
  const [team, setTeam] = useState([]);          // equipe cadastrada, para os chips
  const [selected, setSelected] = useState([]);  // vendedores escolhidos no filtro

  const { data, loading, error } = useReport(reportsApi.sellerConversion, {
    from: range.from || undefined,
    to: range.to || undefined,
    seller_ids: selected.join(',') || undefined,
  });

  // Inclui os inativos: o relatório serve para olhar para trás, e quem saiu da
  // empresa precisa poder ser escolhido para se ver o que fez enquanto estava.
  useEffect(() => {
    sellersApi.list({ include_inactive: true })
      .then(res => setTeam(res.data))
      .catch(() => setTeam([]));
  }, []);

  const sellers = data?.sellers || [];
  const visits = data?.visits || [];
  const totals = data?.totals || { people: 0, visits: 0, followups_sent: 0, conversions: 0, lost: 0 };

  const filterActive = Boolean(range.from || range.to || selected.length);
  const clearFilter = () => { setRange({ from: '', to: '' }); setSelected([]); };
  const toggleSeller = (id) =>
    setSelected(current => (current.includes(id) ? current.filter(s => s !== id) : [...current, id]));

  // Com várias lojas no escopo, a coluna diz de onde é cada vendedor; com uma só,
  // seria a mesma palavra repetida em todas as linhas.
  const showStore = new Set(sellers.map(s => s.store_name).filter(Boolean)).size > 1;

  // A lista vem cortada. Dizer quantas ficaram de fora é o que impede alguém de
  // ler as 200 primeiras como se fossem o período inteiro.
  const listTruncated = totals.visits > visits.length;

  // import() em vez de import no topo: o gerador de PDF pesa ~140 kB e ficaria no
  // pacote que TODO usuário baixa ao abrir o painel, inclusive quem nunca clica
  // aqui. Assim ele só desce no primeiro clique.
  const exportPdf = async () => {
    const { exportSellerConversionPdf } = await import('./services/reportPdf');
    return exportSellerConversionPdf({
      data,
      range,
      org: user?.organization_name,
      // Os nomes, e não os ids: o cabeçalho do PDF é lido por gente.
      sellerNames: team.filter(s => selected.includes(s.id)).map(s => s.name),
      // O gráfico do PDF é desenhado na hora, com estes dados: a tela não tem mais
      // um para capturar. As chaves são as colunas devolvidas pelo relatório.
      series: [
        { key: 'visits', label: 'Visitas', color: CHART_COLORS[0] },
        { key: 'conversions', label: 'Vendas', color: CHART_COLORS[2] },
      ],
    });
  };

  // O CSV leva as duas tabelas da tela num arquivo só, separadas por uma linha
  // em branco: resumo por vendedor em cima, atendimentos um a um embaixo.
  const exportCsv = () => {
    const summaryRows = sellers.map(s => [
      s.name + (s.active ? '' : ' (desligado)'),
      s.store_name || '',
      s.people, s.visits, s.followups_sent, s.conversions, s.lost, s.open_visits,
      rate(s.conversions, s.visits),
    ]);
    const visitRows = visits.map(v => [
      v.client_name, v.client_phone, v.vehicle_interest || '', v.seller_name || '',
      v.store_name || '', stampDate(v.visit_date),
      VISIT_STATUS_LABELS[v.status] || v.status,
    ]);
    downloadCsv(
      `conversao-vendedores-${new Date().toISOString().slice(0, 10)}.csv`,
      ['Vendedor', 'Loja', 'Pessoas', 'Visitas', 'Follow-ups', 'Convertidas', 'Perdidas', 'Em aberto', 'Taxa'],
      [
        ...summaryRows,
        [],
        ['Cliente', 'Contato', 'Veículo', 'Vendedor', 'Loja', 'Data', 'Situação'],
        ...visitRows,
      ]
    );
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Conversão por vendedor</h1>
          <p>{periodLabel(range)}</p>
        </div>
        <div className="cell-actions-inner">
          <button className="btn btn-secondary" onClick={exportCsv} disabled={loading || !data}>
            <Icon name="download" size={16} /> CSV
          </button>
          <PdfButton onExport={exportPdf} disabled={loading || !data} />
        </div>
      </div>

      {error && (
        <div className="alert alert-error"><Icon name="x" size={18} /><div>{error}</div></div>
      )}

      {/* Pessoas e visitas são números diferentes: o cliente que voltou duas vezes
          é uma pessoa e duas visitas. A taxa se mede pelas visitas. */}
      <ReportCards
        cards={[
          { label: 'Pessoas atendidas', value: totals.people, icon: 'users', color: 'blue' },
          { label: 'Visitas no período', value: totals.visits, icon: 'car', color: 'green' },
          { label: 'Viraram venda', value: totals.conversions, icon: 'check', color: 'amber' },
          { label: 'Taxa de conversão', value: rate(totals.conversions, totals.visits), icon: 'report', color: 'purple' },
        ]}
      />

      {/* Sem gráfico aqui: ele repetia o "Desempenho por vendedor" do Dashboard.
          O PDF continua trazendo um, desenhado na hora com estes mesmos filtros. */}
      <div className="chart-card">
        <div className="chart-card-head">
          <h3>Período e vendedores</h3>
          <p>Escolha o intervalo e quem entra no relatório. Vale para os números, as tabelas e o PDF.</p>
        </div>
        <ReportRange range={range} setRange={setRange} active={filterActive} onClear={clearFilter}>
          {/* Multisseleção por botão em vez de <select multiple>: com select
              múltiplo, um clique desmarca todo o resto sem aviso. */}
          <div className="chart-filter-chips">
            {team.length === 0
              ? <span className="chart-filter-empty">Nenhum vendedor cadastrado</span>
              : team.map(s => (
                <button
                  key={s.id}
                  type="button"
                  className={`chip${selected.includes(s.id) ? ' chip--on' : ''}`}
                  onClick={() => toggleSeller(s.id)}
                  aria-pressed={selected.includes(s.id)}
                >
                  {s.name}{s.active ? '' : ' (desligado)'}
                </button>
              ))}
          </div>
          {selected.length > 0 && (
            <div className="chart-filter-note">
              Mostrando {selected.length} vendedor{selected.length > 1 ? 'es' : ''} — clique de novo para tirar da seleção.
            </div>
          )}
        </ReportRange>
      </div>

      {loading ? <SkeletonList rows={6} /> : (
        <>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Vendedor</th>
                  {showStore && <th>Loja</th>}
                  <th>Pessoas</th>
                  <th>Visitas</th>
                  <th>Follow-ups</th>
                  <th>Convertidas</th>
                  <th>Perdidas</th>
                  <th>Em aberto</th>
                  <th>Taxa</th>
                </tr>
              </thead>
              <tbody>
                {sellers.length === 0 ? (
                  <tr>
                    <td colSpan={showStore ? 9 : 8} className="empty-row">
                      <EmptyState
                        icon="users"
                        title={filterActive
                          ? 'Nenhum vendedor corresponde ao filtro'
                          : 'Nenhum vendedor cadastrado ainda'}
                        hint={filterActive
                          ? 'Ajuste o período ou a seleção de vendedores.'
                          : 'Cadastre a equipe em Cadastro › Usuários (perfil Vendedor).'}
                      />
                    </td>
                  </tr>
                ) : sellers.map(s => (
                  <tr key={s.seller_id ?? 'sem-vendedor'}>
                    <td data-label="Vendedor">
                      {s.name}
                      {/* Vendedor desligado continua no relatório com o que fechou
                          enquanto estava na casa — só fica marcado. */}
                      {!s.active && <span className="status-badge status-badge--gray" style={{ marginLeft: 8 }}>desligado</span>}
                    </td>
                    {showStore && <td data-label="Loja">{s.store_name || '—'}</td>}
                    <td data-label="Pessoas">{s.people}</td>
                    <td data-label="Visitas">{s.visits}</td>
                    <td data-label="Follow-ups">{s.followups_sent}</td>
                    <td data-label="Convertidas">{s.conversions}</td>
                    <td data-label="Perdidas">{s.lost}</td>
                    <td data-label="Em aberto">{s.open_visits}</td>
                    <td data-label="Taxa"><strong>{rate(s.conversions, s.visits)}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Quem foi atendido, um a um. O resumo acima diz que 4 de 10 fecharam;
              esta lista diz QUAIS — é o que se leva para a conversa com a equipe. */}
          <div className="chart-card-head" style={{ marginBottom: 12 }}>
            <h3>Pessoas atendidas no período</h3>
            <p>
              {totals.visits === 0
                ? 'Nenhum atendimento no período selecionado.'
                : `${totals.people} pessoa${totals.people > 1 ? 's' : ''} em ${totals.visits} visita${totals.visits > 1 ? 's' : ''}, ${totals.conversions} convertida${totals.conversions === 1 ? '' : 's'} em venda.`}
              {listTruncated && ` Mostrando as ${visits.length} visitas mais recentes — filtre por período para ver o resto.`}
            </p>
          </div>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Contato</th>
                  <th>Veículo de interesse</th>
                  <th>Vendedor</th>
                  {showStore && <th>Loja</th>}
                  <th>Data</th>
                  <th>Situação</th>
                </tr>
              </thead>
              <tbody>
                {visits.length === 0 ? (
                  <tr>
                    <td colSpan={showStore ? 7 : 6} className="empty-row">
                      <EmptyState
                        icon="report"
                        title="Nenhum atendimento no período selecionado"
                        hint="Amplie o período ou limpe os filtros para ver os atendimentos."
                      />
                    </td>
                  </tr>
                ) : visits.map(v => (
                  <tr key={v.id}>
                    <td data-label="Cliente">{v.client_name}</td>
                    <td data-label="Contato">{v.client_phone}</td>
                    <td data-label="Veículo">{v.vehicle_interest || '—'}</td>
                    <td data-label="Vendedor">{v.seller_name}</td>
                    {showStore && <td data-label="Loja">{v.store_name}</td>}
                    <td data-label="Data">{stampDate(v.visit_date)}</td>
                    <td data-label="Situação">
                      <span className={`status-badge status-badge--${VISIT_STATUS_TONE[v.status] || 'gray'}`}>
                        {VISIT_STATUS_LABELS[v.status] || v.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

/** Data e hora curtas (dd/mm hh:mm) — para "esperando desde", só o dia não basta. */
const stampDateTime = (value) =>
  new Date(value).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

/**
 * "há 3 h", "há 12 dias" — não reusa timeAgo porque ele vira data ("15/08") a
 * partir de uma semana, e "há 15/08" não é português.
 */
function waitingFor(dateStr) {
  const mins = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
  if (mins < 60) return `há ${Math.max(mins, 1)} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `há ${hours} h`;
  const days = Math.floor(hours / 24);
  return `há ${days} dia${days > 1 ? 's' : ''}`;
}

/**
 * Relatório de clientes sem atendimento.
 *
 * Três seções, porque "não atendido" são três situações com providências
 * diferentes: quem respondeu e está esperando um humano (ir ao inbox), quem não
 * respondeu o follow-up (ligar) e a visita que ficou sem vendedor (atribuir).
 * Um número só esconderia qual das três está acontecendo.
 */
function UnattendedReport({ user }) {
  const [range, setRange] = useState({ from: '', to: '' });

  const { data, loading, error } = useReport(reportsApi.unattended, {
    from: range.from || undefined,
    to: range.to || undefined,
  });

  const waiting = data?.waiting || [];
  const noReply = data?.no_reply || [];
  const noSeller = data?.no_seller || [];
  const totals = data?.totals || { waiting: 0, no_reply: 0, no_seller: 0 };

  const filterActive = Boolean(range.from || range.to);

  // Com várias lojas no escopo, a coluna diz de onde é cada cliente; com uma só,
  // seria a mesma palavra repetida em todas as linhas.
  const showStore = new Set(
    [...waiting, ...noReply, ...noSeller].map(r => r.store_name).filter(Boolean)
  ).size > 1;

  // Aviso de corte por seção: cada lista tem o próprio total antes do LIMIT.
  const cut = (rows, total) =>
    total > rows.length ? ` Mostrando ${rows.length} de ${total} — filtre por período para ver o resto.` : '';

  const exportPdf = async () => {
    const { exportUnattendedPdf } = await import('./services/reportPdf');
    return exportUnattendedPdf({ data, range, org: user?.organization_name });
  };

  // As três tabelas num arquivo só, cada uma com o próprio cabeçalho, separadas
  // por linha em branco — o mesmo formato do CSV de Conversão por Vendedor.
  const exportCsv = () => {
    downloadCsv(
      `clientes-sem-atendimento-${new Date().toISOString().slice(0, 10)}.csv`,
      ['Responderam e estão sem resposta da equipe'],
      [
        ['Cliente', 'Contato', 'Veículo', 'Vendedor responsável', 'Loja', 'Esperando desde'],
        ...waiting.map(r => [
          r.client_name, r.client_phone, r.vehicle_interest || '', r.seller_name || '',
          r.store_name || '', stampDateTime(r.last_client_at),
        ]),
        [],
        ['Não responderam o follow-up'],
        ['Cliente', 'Contato', 'Veículo', 'Vendedor', 'Loja', 'Visita', 'Follow-up enviado em'],
        ...noReply.map(r => [
          r.client_name, r.client_phone, r.vehicle_interest || '', r.seller_name || '',
          r.store_name || '', stampDate(r.visit_date), stampDateTime(r.followup_sent_at),
        ]),
        [],
        ['Visitas sem vendedor atribuído'],
        ['Cliente', 'Contato', 'Veículo', 'Loja', 'Visita', 'Situação'],
        ...noSeller.map(r => [
          r.client_name, r.client_phone, r.vehicle_interest || '', r.store_name || '',
          stampDate(r.visit_date), VISIT_STATUS_LABELS[r.status] || r.status,
        ]),
      ]
    );
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Clientes sem atendimento</h1>
          <p>{periodLabel(range)}</p>
        </div>
        <div className="cell-actions-inner">
          <button className="btn btn-secondary" onClick={exportCsv} disabled={loading || !data}>
            <Icon name="download" size={16} /> CSV
          </button>
          <PdfButton onExport={exportPdf} disabled={loading || !data} />
        </div>
      </div>

      {error && (
        <div className="alert alert-error"><Icon name="x" size={18} /><div>{error}</div></div>
      )}

      <ReportCards
        cards={[
          { label: 'Esperando resposta da equipe', value: totals.waiting, icon: 'chat', color: 'amber' },
          { label: 'Não responderam o follow-up', value: totals.no_reply, icon: 'whatsapp', color: 'purple' },
          { label: 'Visitas sem vendedor', value: totals.no_seller, icon: 'users', color: 'blue' },
        ]}
      />

      <div className="chart-card">
        <div className="chart-card-head">
          <h3>Período</h3>
          <p>Vale para os números, as três listas, o CSV e o PDF.</p>
        </div>
        <ReportRange range={range} setRange={setRange} active={filterActive} />
      </div>

      {loading ? <SkeletonList rows={6} /> : (
        <>
          {/* Quem respondeu e está esperando: a fila mais urgente, do mais antigo
              para o mais novo — é a ordem em que se deve responder. */}
          <div className="chart-card-head" style={{ marginBottom: 12 }}>
            <h3>Responderam e estão sem resposta da equipe</h3>
            <p>
              {totals.waiting === 0
                ? 'Ninguém esperando — todas as conversas passadas a vendedor foram respondidas.'
                : `${totals.waiting} cliente${totals.waiting > 1 ? 's' : ''} esperando um vendedor responder. Atenda pelo inbox de Conversas.`}
              {cut(waiting, totals.waiting)}
            </p>
          </div>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Contato</th>
                  <th>Veículo de interesse</th>
                  <th>Vendedor responsável</th>
                  {showStore && <th>Loja</th>}
                  <th>Esperando desde</th>
                </tr>
              </thead>
              <tbody>
                {waiting.length === 0 ? (
                  <tr>
                    <td colSpan={showStore ? 6 : 5} className="empty-row">
                      <EmptyState icon="check" title="Nenhum cliente esperando resposta" hint="Quem respondeu no WhatsApp já foi atendido pela equipe." />
                    </td>
                  </tr>
                ) : waiting.map(r => (
                  <tr key={r.conversation_id}>
                    <td data-label="Cliente">{r.client_name}</td>
                    <td data-label="Contato">{r.client_phone}</td>
                    <td data-label="Veículo">{r.vehicle_interest || '—'}</td>
                    <td data-label="Vendedor">{r.seller_name}</td>
                    {showStore && <td data-label="Loja">{r.store_name}</td>}
                    <td data-label="Esperando desde">
                      {stampDateTime(r.last_client_at)}
                      <span className="status-badge status-badge--amber" style={{ marginLeft: 8 }}>
                        {waitingFor(r.last_client_at)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Follow-up mandado, silêncio do outro lado: candidatos a uma ligação. */}
          <div className="chart-card-head" style={{ marginBottom: 12, marginTop: 24 }}>
            <h3>Não responderam o follow-up</h3>
            <p>
              {totals.no_reply === 0
                ? 'Todo follow-up enviado no período teve resposta.'
                : `${totals.no_reply} cliente${totals.no_reply > 1 ? 's' : ''} recebeu o follow-up e não escreveu de volta — vale uma ligação.`}
              {cut(noReply, totals.no_reply)}
            </p>
          </div>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Contato</th>
                  <th>Veículo de interesse</th>
                  <th>Vendedor</th>
                  {showStore && <th>Loja</th>}
                  <th>Visita</th>
                  <th>Follow-up enviado em</th>
                </tr>
              </thead>
              <tbody>
                {noReply.length === 0 ? (
                  <tr>
                    <td colSpan={showStore ? 7 : 6} className="empty-row">
                      <EmptyState icon="check" title="Sem follow-ups no vácuo" hint="Todos os follow-ups enviados no período tiveram retorno do cliente." />
                    </td>
                  </tr>
                ) : noReply.map(r => (
                  <tr key={r.id}>
                    <td data-label="Cliente">{r.client_name}</td>
                    <td data-label="Contato">{r.client_phone}</td>
                    <td data-label="Veículo">{r.vehicle_interest || '—'}</td>
                    <td data-label="Vendedor">{r.seller_name}</td>
                    {showStore && <td data-label="Loja">{r.store_name}</td>}
                    <td data-label="Visita">{stampDate(r.visit_date)}</td>
                    <td data-label="Follow-up">{stampDateTime(r.followup_sent_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Visita sem dono: ninguém é cobrado por esse cliente. Convertidas não
              entram — alguém claramente atendeu. */}
          <div className="chart-card-head" style={{ marginBottom: 12, marginTop: 24 }}>
            <h3>Visitas sem vendedor atribuído</h3>
            <p>
              {totals.no_seller === 0
                ? 'Toda visita do período tem um vendedor responsável.'
                : `${totals.no_seller} visita${totals.no_seller > 1 ? 's' : ''} sem responsável — atribua um vendedor em Visitas.`}
              {cut(noSeller, totals.no_seller)}
            </p>
          </div>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Contato</th>
                  <th>Veículo de interesse</th>
                  {showStore && <th>Loja</th>}
                  <th>Data</th>
                  <th>Situação</th>
                </tr>
              </thead>
              <tbody>
                {noSeller.length === 0 ? (
                  <tr>
                    <td colSpan={showStore ? 6 : 5} className="empty-row">
                      <EmptyState icon="check" title="Nenhuma visita sem vendedor" hint="Todas as visitas do período têm um responsável." />
                    </td>
                  </tr>
                ) : noSeller.map(r => (
                  <tr key={r.id}>
                    <td data-label="Cliente">{r.client_name}</td>
                    <td data-label="Contato">{r.client_phone}</td>
                    <td data-label="Veículo">{r.vehicle_interest || '—'}</td>
                    {showStore && <td data-label="Loja">{r.store_name}</td>}
                    <td data-label="Data">{stampDate(r.visit_date)}</td>
                    <td data-label="Situação">
                      <span className={`status-badge status-badge--${VISIT_STATUS_TONE[r.status] || 'gray'}`}>
                        {VISIT_STATUS_LABELS[r.status] || r.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Situação de uma proposta ENVIADA, como o relatório a classifica (`outcome` da
 * API). "Vencida" não é status no banco: é a enviada cuja validade já passou e
 * que ninguém marcou como aceita ou recusada — o mesmo cálculo da tela de
 * Propostas, só que feito no servidor para contar e filtrar.
 */
const PROPOSAL_OUTCOMES = [
  { value: 'open', label: 'Em aberto', color: 'blue' },
  { value: 'expired', label: 'Vencida', color: 'amber' },
  { value: 'accepted', label: 'Aceita', color: 'green' },
  { value: 'rejected', label: 'Recusada', color: 'red' },
];
const PROPOSAL_OUTCOME_INFO = Object.fromEntries(PROPOSAL_OUTCOMES.map(o => [o.value, o]));

/** "Entrada R$ 20.000,00 · 48x de R$ 3.500,00", ou "À vista" sem parcelas. */
const proposalTermsLabel = (p) => {
  const parts = [];
  if (Number(p.down_payment) > 0) parts.push(`Entrada ${formatBRL(p.down_payment)}`);
  if (Number(p.installments) > 0) {
    parts.push(p.installment_value ? `${p.installments}x de ${formatBRL(p.installment_value)}` : `${p.installments}x`);
  }
  return parts.length ? parts.join(' · ') : 'À vista';
};

/**
 * Relatório de propostas enviadas.
 *
 * Responde "quantas propostas saíram, para quem, por quanto, e quantas
 * fecharam". Só proposta enviada entra — rascunho é trabalho em andamento —, e o
 * período é o da data de ENVIO. Dois recortes: o resumo por vendedor (a taxa de
 * aceite ao lado do funil inteiro, como na conversão) e a lista proposta a
 * proposta, que é o que se leva para a reunião: quais estão em aberto para
 * cobrar, quais venceram sem resposta.
 */
function ProposalsReport({ user }) {
  const [range, setRange] = useState({ from: '', to: '' });
  const [team, setTeam] = useState([]);          // equipe cadastrada, para os chips
  const [selected, setSelected] = useState([]);  // vendedores escolhidos no filtro
  const [outcome, setOutcome] = useState('');    // situação escolhida ('' = todas)

  const { data, loading, error } = useReport(reportsApi.proposals, {
    from: range.from || undefined,
    to: range.to || undefined,
    seller_ids: selected.join(',') || undefined,
    status: outcome || undefined,
  });

  // Inclui os inativos: quem saiu da empresa continua com as propostas que
  // mandou enquanto estava na casa, e precisa poder ser escolhido.
  useEffect(() => {
    sellersApi.list({ include_inactive: true })
      .then(res => setTeam(res.data))
      .catch(() => setTeam([]));
  }, []);

  const sellers = data?.sellers || [];
  const proposals = data?.proposals || [];
  const totals = data?.totals || {
    proposals: 0, open: 0, expired: 0, accepted: 0, rejected: 0, proposed_value: 0, accepted_value: 0,
  };

  const filterActive = Boolean(range.from || range.to || selected.length || outcome);
  const clearFilter = () => { setRange({ from: '', to: '' }); setSelected([]); setOutcome(''); };
  const toggleSeller = (id) =>
    setSelected(current => (current.includes(id) ? current.filter(s => s !== id) : [...current, id]));

  // Com várias lojas no escopo, a coluna diz de onde é cada proposta; com uma
  // só, seria a mesma palavra repetida em todas as linhas.
  const showStore = new Set(
    [...sellers, ...proposals].map(r => r.store_name).filter(Boolean)
  ).size > 1;

  // A lista vem cortada. Dizer quantas ficaram de fora é o que impede alguém de
  // ler as 200 primeiras como se fossem o período inteiro.
  const listTruncated = totals.proposals > proposals.length;

  const outcomeLabel = outcome ? PROPOSAL_OUTCOME_INFO[outcome].label : '';

  const exportPdf = async () => {
    const { exportProposalsPdf } = await import('./services/reportPdf');
    return exportProposalsPdf({
      data,
      range,
      org: user?.organization_name,
      sellerNames: team.filter(s => selected.includes(s.id)).map(s => s.name),
      outcomeLabel,
    });
  };

  // As duas tabelas num arquivo só: resumo por vendedor em cima, propostas uma
  // a uma embaixo — o mesmo formato do CSV de Conversão por Vendedor.
  const exportCsv = () => {
    const summaryRows = sellers.map(s => [
      s.name + (s.active ? '' : ' (desligado)'),
      s.store_name || '',
      s.proposals, s.open, s.expired, s.accepted, s.rejected,
      rate(s.accepted, s.proposals),
      formatBRL(s.proposed_value), formatBRL(s.accepted_value),
    ]);
    const proposalRows = proposals.map(p => [
      stampDateTime(p.sent_at), p.client_name, p.client_phone, p.vehicle_description,
      formatBRL(p.vehicle_price), proposalTermsLabel(p), p.seller_name || '',
      p.created_by_name || '', p.store_name || '',
      p.valid_until ? proposalDate(p.valid_until) : '',
      PROPOSAL_OUTCOME_INFO[p.outcome]?.label || p.outcome,
    ]);
    downloadCsv(
      `propostas-enviadas-${new Date().toISOString().slice(0, 10)}.csv`,
      ['Vendedor', 'Loja', 'Propostas', 'Em aberto', 'Vencidas', 'Aceitas', 'Recusadas', 'Taxa de aceite', 'Valor proposto', 'Valor aceito'],
      [
        ...summaryRows,
        [],
        ['Enviada em', 'Cliente', 'Contato', 'Veículo', 'Valor', 'Condições', 'Vendedor', 'Enviada por', 'Loja', 'Validade', 'Situação'],
        ...proposalRows,
      ]
    );
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Propostas enviadas</h1>
          <p>{periodLabel(range)}</p>
        </div>
        <div className="cell-actions-inner">
          <button className="btn btn-secondary" onClick={exportCsv} disabled={loading || !data}>
            <Icon name="download" size={16} /> CSV
          </button>
          <PdfButton onExport={exportPdf} disabled={loading || !data} />
        </div>
      </div>

      {error && (
        <div className="alert alert-error"><Icon name="x" size={18} /><div>{error}</div></div>
      )}

      {/* O valor aceito é o número que o dono da loja quer ver: quanto das
          propostas virou negócio. A taxa diz a proporção; o valor, o tamanho. */}
      <ReportCards
        cards={[
          { label: 'Propostas enviadas', value: totals.proposals, icon: 'file', color: 'blue' },
          { label: 'Aceitas', value: totals.accepted, icon: 'check', color: 'green' },
          { label: 'Taxa de aceite', value: rate(totals.accepted, totals.proposals), icon: 'report', color: 'purple' },
          { label: 'Valor aceito', value: formatBRL(totals.accepted_value), icon: 'car', color: 'amber' },
        ]}
      />

      <div className="chart-card">
        <div className="chart-card-head">
          <h3>Período, vendedores e situação</h3>
          <p>O período é o da data de envio. Vale para os números, as tabelas, o CSV e o PDF.</p>
        </div>
        <ReportRange range={range} setRange={setRange} active={filterActive} onClear={clearFilter}>
          <div className="chart-filter-chips">
            {team.length === 0
              ? <span className="chart-filter-empty">Nenhum vendedor cadastrado</span>
              : team.map(s => (
                <button
                  key={s.id}
                  type="button"
                  className={`chip${selected.includes(s.id) ? ' chip--on' : ''}`}
                  onClick={() => toggleSeller(s.id)}
                  aria-pressed={selected.includes(s.id)}
                >
                  {s.name}{s.active ? '' : ' (desligado)'}
                </button>
              ))}
          </div>
          {selected.length > 0 && (
            <div className="chart-filter-note">
              Mostrando {selected.length} vendedor{selected.length > 1 ? 'es' : ''} — clique de novo para tirar da seleção.
            </div>
          )}
          {/* Situação é escolha única: "em aberto" e "aceita" juntas é o mesmo
              que não filtrar, e um clique troca em vez de somar. */}
          <div className="chart-filter-chips">
            {PROPOSAL_OUTCOMES.map(o => (
              <button
                key={o.value}
                type="button"
                className={`chip${outcome === o.value ? ' chip--on' : ''}`}
                onClick={() => setOutcome(current => (current === o.value ? '' : o.value))}
                aria-pressed={outcome === o.value}
              >
                {o.label}
              </button>
            ))}
          </div>
        </ReportRange>
      </div>

      {loading ? <SkeletonList rows={6} /> : (
        <>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Vendedor</th>
                  {showStore && <th>Loja</th>}
                  <th>Propostas</th>
                  <th>Em aberto</th>
                  <th>Vencidas</th>
                  <th>Aceitas</th>
                  <th>Recusadas</th>
                  <th>Taxa de aceite</th>
                  <th>Valor aceito</th>
                </tr>
              </thead>
              <tbody>
                {sellers.length === 0 ? (
                  <tr>
                    <td colSpan={showStore ? 9 : 8} className="empty-row">
                      <EmptyState
                        icon="file"
                        title={filterActive
                          ? 'Nenhuma proposta corresponde ao filtro'
                          : 'Nenhuma proposta enviada nos últimos 30 dias'}
                        hint={filterActive
                          ? 'Ajuste o período, os vendedores ou a situação.'
                          : 'As propostas enviadas pela tela de Propostas aparecem aqui.'}
                      />
                    </td>
                  </tr>
                ) : sellers.map(s => (
                  <tr key={s.seller_id ?? 'sem-vendedor'}>
                    <td data-label="Vendedor">
                      {s.name}
                      {!s.active && <span className="status-badge status-badge--gray" style={{ marginLeft: 8 }}>desligado</span>}
                    </td>
                    {showStore && <td data-label="Loja">{s.store_name || '—'}</td>}
                    <td data-label="Propostas">{s.proposals}</td>
                    <td data-label="Em aberto">{s.open}</td>
                    <td data-label="Vencidas">{s.expired}</td>
                    <td data-label="Aceitas">{s.accepted}</td>
                    <td data-label="Recusadas">{s.rejected}</td>
                    <td data-label="Taxa de aceite"><strong>{rate(s.accepted, s.proposals)}</strong></td>
                    <td data-label="Valor aceito">{formatBRL(s.accepted_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Proposta a proposta, da mais recente para a mais antiga: o resumo
              diz que 3 de 10 fecharam; esta lista diz quais, e quais ainda
              esperam resposta. */}
          <div className="chart-card-head" style={{ marginBottom: 12 }}>
            <h3>Propostas no período</h3>
            <p>
              {totals.proposals === 0
                ? 'Nenhuma proposta enviada no período selecionado.'
                : `${totals.proposals} proposta${totals.proposals > 1 ? 's' : ''} somando ${formatBRL(totals.proposed_value)}: ${totals.accepted} aceita${totals.accepted === 1 ? '' : 's'}, ${totals.rejected} recusada${totals.rejected === 1 ? '' : 's'}, ${totals.open} em aberto e ${totals.expired} vencida${totals.expired === 1 ? '' : 's'}.`}
              {listTruncated && ` Mostrando as ${proposals.length} mais recentes — filtre por período para ver o resto.`}
            </p>
          </div>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Enviada em</th>
                  <th>Cliente</th>
                  <th>Contato</th>
                  <th>Veículo</th>
                  <th>Valor</th>
                  <th>Condições</th>
                  <th>Vendedor</th>
                  {showStore && <th>Loja</th>}
                  <th>Validade</th>
                  <th>Situação</th>
                </tr>
              </thead>
              <tbody>
                {proposals.length === 0 ? (
                  <tr>
                    <td colSpan={showStore ? 10 : 9} className="empty-row">
                      <EmptyState
                        icon="file"
                        title="Nenhuma proposta no período selecionado"
                        hint="Amplie o período ou limpe os filtros para ver as propostas."
                      />
                    </td>
                  </tr>
                ) : proposals.map(p => {
                  const info = PROPOSAL_OUTCOME_INFO[p.outcome] || { label: p.outcome, color: 'gray' };
                  return (
                    <tr key={p.id}>
                      <td data-label="Enviada em">{stampDateTime(p.sent_at)}</td>
                      <td data-label="Cliente">{p.client_name}</td>
                      <td data-label="Contato">{p.client_phone}</td>
                      <td data-label="Veículo">{p.vehicle_description}</td>
                      <td data-label="Valor">{formatBRL(p.vehicle_price)}</td>
                      <td data-label="Condições">{proposalTermsLabel(p)}</td>
                      {/* Quem enviou pode não ser o vendedor da proposta (a
                          recepção monta e manda em nome dele): o title mostra. */}
                      <td data-label="Vendedor" title={p.created_by_name ? `Enviada por ${p.created_by_name}` : undefined}>
                        {p.seller_name}
                      </td>
                      {showStore && <td data-label="Loja">{p.store_name}</td>}
                      <td data-label="Validade">{p.valid_until ? proposalDate(p.valid_until) : '—'}</td>
                      <td data-label="Situação">
                        <span className={`status-badge status-badge--${info.color}`}>{info.label}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ─── PÓS-VENDA ────────────────────────────────────────────────────────────────

const OFFER_STATUS = {
  sent: { label: 'Enviada', color: 'green' },
  failed: { label: 'Falhou', color: 'red' },
  none: { label: 'Aguardando', color: 'amber' },
};
const OFFER_FILTERS = [
  { value: 'none', label: 'Sem oferta ainda' },
  { value: 'sent', label: 'Oferta enviada' },
  { value: 'failed', label: 'Falhou' },
];

const shortDate = (value) =>
  value ? new Date(value).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—';

/**
 * Pós-venda: quem comprou um veículo depois de um test-drive (proposta aceita)
 * recebe a oferta dos serviços da loja — limpeza de duto de ar-condicionado,
 * freio, envernizar o motor, caixa evaporadora, sanitização. A primeira
 * mensagem sai sozinha N dias depois da compra (Configurações › Pós-venda);
 * esta tela mostra a lista, o que já saiu e dispara as seguintes à mão.
 */
function AftersalesPage({ hasStore = true, isAdmin = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [services, setServices] = useState([]);
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    aftersalesApi.list()
      .then(res => {
        setData(res.data);
        setServices(res.data.services || []);
        setError('');
      })
      .catch(err => setError(apiErrorMessage(err, 'Erro ao carregar o pós-venda')))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const clients = useMemo(() => data?.clients || [], [data]);
  const showStore = new Set(clients.map(c => c.organization_id)).size > 1;
  const offerState = (c) => c.last_offer_status || 'none';

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return clients.filter(c =>
      (!filter || offerState(c) === filter) &&
      (!q || [c.full_name, c.phone, c.vehicle_description, c.seller_name].some(v => String(v || '').toLowerCase().includes(q)))
    );
  }, [clients, filter, search]);

  const totals = useMemo(() => ({
    all: clients.length,
    none: clients.filter(c => offerState(c) === 'none').length,
    sent: clients.filter(c => offerState(c) === 'sent').length,
    failed: clients.filter(c => offerState(c) === 'failed').length,
  }), [clients]);

  const toggle = (id) => setSelected(s => (s.includes(id) ? s.filter(x => x !== id) : [...s, id]));
  const selectable = shown.filter(c => !c.opted_out).map(c => c.client_id);
  const allShownSelected = selectable.length > 0 && selectable.every(id => selected.includes(id));
  const toggleAll = () => setSelected(allShownSelected ? [] : selectable);
  const toggleService = (s) => setServices(list => (list.includes(s) ? list.filter(x => x !== s) : [...list, s]));

  const sendTo = async (ids) => {
    const targets = ids.filter(id => clients.some(c => c.client_id === id && !c.opted_out));
    if (!targets.length) return;
    const names = targets.slice(0, 3).map(id => clients.find(c => c.client_id === id)?.full_name.split(' ')[0]).join(', ');
    const more = targets.length > 3 ? ` e mais ${targets.length - 3}` : '';
    if (!window.confirm(`Enviar a oferta de serviços para ${names}${more}? Sai pelo WhatsApp da loja (ou e-mail de reserva) agora.`)) return;
    setSending(true);
    setResults(null);
    try {
      const res = await aftersalesApi.send({ client_ids: targets, services });
      setResults(res.data.results);
      setSelected([]);
      load();
    } catch (err) {
      alert(apiErrorMessage(err, 'Erro ao enviar a oferta'));
    } finally {
      setSending(false);
    }
  };

  const storeServices = data?.services || [];

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Pós-venda</h1>
          <p>
            Clientes que fizeram test-drive e aceitaram a proposta — a oferta dos serviços da loja.
            {data?.auto !== undefined && (
              data.auto
                ? ` A primeira mensagem sai sozinha ${data.delay_days} dias depois da compra.`
                : ' O envio automático está desligado nesta loja.'
            )}
          </p>
        </div>
      </div>

      {error && (
        <div className="alert alert-error"><Icon name="x" size={18} /><div>{error}</div></div>
      )}

      <ReportCards
        cards={[
          { label: 'Compraram após test-drive', value: totals.all, icon: 'check', color: 'blue' },
          { label: 'Sem oferta ainda', value: totals.none, icon: 'clock', color: 'amber' },
          { label: 'Oferta enviada', value: totals.sent, icon: 'whatsapp', color: 'green' },
          { label: 'Falhou', value: totals.failed, icon: 'x', color: 'purple' },
        ]}
      />

      {hasStore && (
        <div className="form-card aftersales-send">
          <h3><Icon name="wrench" size={16} /> Serviços oferecidos</h3>
          {storeServices.length === 0 ? (
            <div className="field-hint">
              Nenhum serviço cadastrado. {isAdmin ? 'Cadastre em Configurações › Pós-venda.' : 'Peça ao administrador para cadastrar em Configurações.'}
            </div>
          ) : (
            <>
              <div className="aftersales-services">
                {storeServices.map(s => (
                  <label key={s} className="proposal-check">
                    <input type="checkbox" checked={services.includes(s)} onChange={() => toggleService(s)} />
                    {s}
                  </label>
                ))}
              </div>
              <div className="field-hint">
                Desmarque o que não quer oferecer neste envio. {isAdmin ? 'A lista é editada em Configurações › Pós-venda.' : ''}
                {data?.template
                  ? ` Sai pelo template "${data.template}".`
                  : ' Sem template cadastrado: pelo WhatsApp sai texto livre, que a Meta só aceita se o cliente escreveu nas últimas 24h.'}
              </div>
              {data?.preview && (
                <div className="proposal-preview">
                  <label>Como o cliente recebe (exemplo)</label>
                  <pre>{data.preview}</pre>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {results && (
        <div className={`alert ${results.every(r => r.success) ? 'alert-success' : 'alert-error'}`}>
          <Icon name={results.every(r => r.success) ? 'check' : 'x'} size={18} />
          <div>
            {results.map(r => (
              <div key={r.client_id}>
                <strong>{r.client_name || `Cliente ${r.client_id}`}:</strong>{' '}
                {r.success
                  ? `enviada por ${r.channel === 'email' ? 'e-mail' : 'WhatsApp'}${r.simulated ? ' (simulação — a loja está sem credenciais, nada chegou)' : ''}`
                  : `não enviada — ${r.error}`}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="chart-card">
        <div className="chart-card-head">
          <h3>Clientes</h3>
          <p>Marque os clientes e envie a oferta, ou envie um a um pela linha. Quem pediu para não receber mensagens fica de fora.</p>
        </div>
        <div className="chart-filter-chips">
          {OFFER_FILTERS.map(f => (
            <button
              key={f.value}
              type="button"
              className={`chip${filter === f.value ? ' chip--on' : ''}`}
              onClick={() => setFilter(current => (current === f.value ? '' : f.value))}
              aria-pressed={filter === f.value}
            >
              {f.label}
            </button>
          ))}
          <input
            className="aftersales-search"
            placeholder="Buscar por nome, telefone, veículo ou vendedor"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="aftersales-actions">
          <button
            className="btn btn-primary"
            disabled={sending || !selected.length}
            onClick={() => sendTo(selected)}
          >
            <Icon name="send" size={16} /> {sending ? 'Enviando...' : `Enviar aos selecionados (${selected.length})`}
          </button>
        </div>
      </div>

      {loading ? <SkeletonList rows={6} /> : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th><input type="checkbox" checked={allShownSelected} onChange={toggleAll} aria-label="Selecionar todos" /></th>
                <th>Cliente</th>
                <th>Contato</th>
                <th>Comprou</th>
                <th>Test-drive</th>
                <th>Vendedor</th>
                {showStore && <th>Loja</th>}
                <th>Última oferta</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 ? (
                <tr>
                  <td colSpan={showStore ? 9 : 8} className="empty-row">
                    <EmptyState
                      icon="wrench"
                      title={clients.length ? 'Nenhum cliente corresponde ao filtro' : 'Nenhuma compra após test-drive ainda'}
                      hint={clients.length
                        ? 'Ajuste a busca ou o filtro de situação.'
                        : 'O cliente entra aqui quando tem um test-drive registrado e a proposta dele é marcada como aceita em Propostas.'}
                    />
                  </td>
                </tr>
              ) : shown.map(c => {
                const state = offerState(c);
                const info = OFFER_STATUS[state];
                return (
                  <tr key={c.client_id} className={c.opted_out ? 'row-muted' : ''}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selected.includes(c.client_id)}
                        disabled={c.opted_out}
                        onChange={() => toggle(c.client_id)}
                        aria-label={`Selecionar ${c.full_name}`}
                      />
                    </td>
                    <td data-label="Cliente">
                      <div>{c.full_name}</div>
                      {c.opted_out && <div className="cell-sub">🚫 pediu para não receber mensagens</div>}
                    </td>
                    <td data-label="Contato">
                      <div>{c.phone || '—'}</div>
                      {c.email && <div className="cell-sub">{c.email}</div>}
                    </td>
                    <td data-label="Comprou">
                      <div>{c.vehicle_description}</div>
                      <div className="cell-sub">{shortDate(c.accepted_at)} · {formatBRL(c.vehicle_price)}</div>
                    </td>
                    <td data-label="Test-drive">{shortDate(c.test_drive_at)}</td>
                    <td data-label="Vendedor">{c.seller_name || '—'}</td>
                    {showStore && <td data-label="Loja">{c.store_name}</td>}
                    <td data-label="Última oferta">
                      <span className={`status-badge status-badge--${info.color}`}>{info.label}</span>
                      {c.last_offer_at && (
                        <div className="cell-sub" title={c.last_offer_error || undefined}>
                          {stampDateTime(c.last_offer_at)}
                          {c.last_offer_channel ? ` · ${c.last_offer_channel === 'email' ? 'e-mail' : 'WhatsApp'}` : ''}
                          {c.last_offer_source === 'auto' ? ' · automática' : c.last_offer_by ? ` · ${c.last_offer_by}` : ''}
                          {c.last_offer_simulated ? ' · simulação' : ''}
                          {c.offers_sent > 1 ? ` · ${c.offers_sent} envios` : ''}
                        </div>
                      )}
                      {state === 'failed' && c.last_offer_error && (
                        <div className="cell-sub field-hint--error">{c.last_offer_error}</div>
                      )}
                    </td>
                    <td>
                      <button
                        className="btn btn-secondary btn-sm"
                        disabled={sending || c.opted_out}
                        onClick={() => sendTo([c.client_id])}
                        title={state === 'none' ? 'Enviar a primeira oferta agora' : 'Enviar outra oferta'}
                      >
                        <Icon name="send" size={14} /> {state === 'none' ? 'Enviar' : 'Reenviar'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SettingsPage({ hasStore = true }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    // As configurações são por LOJA: o nome nas mensagens, o prazo do follow-up e
    // o bot são diferentes em cada ponto de venda. Sem loja escolhida não há um
    // valor para mostrar, e o backend recusaria a chamada.
    if (!hasStore) { setLoading(false); return; }

    settingsApi.get().then(res => {
      const f = {};
      Object.entries(res.data).forEach(([k, v]) => { f[k] = v.value; });
      setForm(f);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [hasStore]);

  // Disponibilidade do assistente de IA na plataforma: a opção "ai" só faz
  // sentido se houver provedor configurado — sem ele o backend recusa (400).
  const [ai, setAi] = useState(null);
  useEffect(() => {
    if (!hasStore) return;
    aiApi.status().then(res => setAi(res.data)).catch(() => setAi({ configured: false }));
  }, [hasStore]);

  const handleSave = async (key) => {
    setSaving(true);
    try {
      await settingsApi.update(key, form[key]);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) { alert(err.response?.data?.error || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="page"><div className="loading">Carregando...</div></div>;

  if (!hasStore) {
    return (
      <div className="page">
        <div className="page-header">
          <h1>Configurações</h1>
          <p>Ajustes do sistema e integração WhatsApp</p>
        </div>
        <div className="alert alert-error">
          <Icon name="x" size={18} />
          <div>
            Estas configurações são de cada loja. Escolha a{' '}
            <strong className="inline">loja ativa</strong> no menu à esquerda para ver e alterar
            as dela.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>Configurações</h1>
        <p>Ajustes do sistema e integração WhatsApp</p>
      </div>

      <div className="settings-grid">
        <div className="form-card">
          <h3>Follow-up Automático</h3>
          <div className="field">
            <label>Nome usado nas mensagens</label>
            <div className="field-with-action">
              <input value={form.concessionaria_name || ''} onChange={e => setForm(f => ({ ...f, concessionaria_name: e.target.value }))} />
              <button className="btn btn-primary" onClick={() => handleSave('concessionaria_name')} disabled={saving}>Salvar</button>
            </div>
            <div className="field-hint">É este nome que o cliente lê no WhatsApp desta loja.</div>
          </div>
          <div className="field">
            <label>Horas para envio do follow-up após visita</label>
            <div className="field-with-action">
              <input type="number" min="1" max="48" value={form.followup_delay_hours || 2} onChange={e => setForm(f => ({ ...f, followup_delay_hours: e.target.value }))} />
              <button className="btn btn-primary" onClick={() => handleSave('followup_delay_hours')} disabled={saving}>Salvar</button>
            </div>
            <div className="field-hint">Atualmente: {form.followup_delay_hours} hora(s) após o cadastro da visita</div>
          </div>
          <div className="field">
            <label>Canais do follow-up automático (ordem de prioridade)</label>
            <select value={form.followup_channels || 'whatsapp,email'} onChange={e => setForm(f => ({ ...f, followup_channels: e.target.value }))}>
              <option value="whatsapp,email">WhatsApp, com e-mail de reserva</option>
              <option value="email">Somente e-mail</option>
              <option value="whatsapp">Somente WhatsApp</option>
            </select>
            <div className="field-hint">
              O follow-up sai pelo primeiro canal disponível; o e-mail de reserva só entra se o WhatsApp
              falhar ou estiver sem credenciais nesta loja (como agora, aguardando a Meta). O canal usado
              e o motivo do desvio ficam registrados na conversa do inbox.
            </div>
            <button className="btn btn-primary mt-8" onClick={() => handleSave('followup_channels')} disabled={saving}>Salvar</button>
          </div>
          <div className="field">
            <label>2º follow-up se o cliente não responder (dias)</label>
            <div className="field-with-action">
              <input type="number" min="0" max="30" value={form.followup_reminder_days ?? 0} onChange={e => setForm(f => ({ ...f, followup_reminder_days: e.target.value }))} />
              <button className="btn btn-primary" onClick={() => handleSave('followup_reminder_days')} disabled={saving}>Salvar</button>
            </div>
            <div className="field-hint">
              0 desliga. Com 3, por exemplo: cliente que não respondeu nada em 3 dias recebe um único
              lembrete, pelo mesmo template aprovado. Quem pediu para não receber mensagens fica de fora.
            </div>
          </div>
        </div>

        <div className="form-card">
          <h3>Pós-venda</h3>
          <div className="field">
            <label>Oferta de serviços a quem comprou</label>
            <select value={form.aftersales_auto || 'true'} onChange={e => setForm(f => ({ ...f, aftersales_auto: e.target.value }))}>
              <option value="true">Enviar a primeira oferta sozinho, depois do prazo</option>
              <option value="false">Só envio manual pela tela Pós-venda</option>
            </select>
            <div className="field-hint">
              Entra quem fez test-drive e teve a proposta marcada como aceita. A mensagem vai pela
              conversa do cliente e fica com o vendedor da venda.
            </div>
            <button className="btn btn-primary mt-8" onClick={() => handleSave('aftersales_auto')} disabled={saving}>Salvar</button>
          </div>
          <div className="field">
            <label>Dias depois da compra para a primeira oferta</label>
            <div className="field-with-action">
              <input type="number" min="1" max="730" value={form.aftersales_delay_days ?? 90} onChange={e => setForm(f => ({ ...f, aftersales_delay_days: e.target.value }))} />
              <button className="btn btn-primary" onClick={() => handleSave('aftersales_delay_days')} disabled={saving}>Salvar</button>
            </div>
            <div className="field-hint">A compra é a data em que a proposta foi marcada como aceita. 90 dias = 3 meses.</div>
          </div>
          <div className="field">
            <label>Serviços oferecidos (um por linha)</label>
            <textarea
              rows={6}
              value={form.aftersales_services || ''}
              onChange={e => setForm(f => ({ ...f, aftersales_services: e.target.value }))}
              placeholder={'Limpeza de duto de ar-condicionado\nLimpeza de freio'}
            />
            <div className="field-hint">
              É a lista que o cliente recebe. Na tela Pós-venda dá para desmarcar itens num envio específico.
            </div>
            <button className="btn btn-primary mt-8" onClick={() => handleSave('aftersales_services')} disabled={saving}>Salvar</button>
          </div>
          <div className="field">
            <label>Template aprovado na Meta (nome)</label>
            <div className="field-with-action">
              <input value={form.aftersales_template || ''} onChange={e => setForm(f => ({ ...f, aftersales_template: e.target.value }))} placeholder="ex.: pos_venda_servicos" />
              <button className="btn btn-primary" onClick={() => handleSave('aftersales_template')} disabled={saving}>Salvar</button>
            </div>
            <div className="field-hint">
              Três meses depois da compra o cliente está fora da janela de 24h, e a Meta só entrega
              template aprovado. Cadastre um com 4 parâmetros, nesta ordem: nome do cliente, nome da
              loja, veículo e lista de serviços. Vazio, sai texto livre (só chega se o cliente escreveu
              nas últimas 24h; senão a tentativa fica como "falhou" na tela Pós-venda).
            </div>
          </div>
        </div>

        <div className="form-card">
          <h3>Bot Automático</h3>
          <div className="field">
            <label>Bot Habilitado</label>
            <select value={form.bot_enabled} onChange={e => setForm(f => ({ ...f, bot_enabled: e.target.value }))}>
              <option value="true">Sim — responder automaticamente</option>
              <option value="false">Não — apenas encaminhar para vendedor</option>
            </select>
            <button className="btn btn-primary mt-8" onClick={() => handleSave('bot_enabled')} disabled={saving}>Salvar</button>
          </div>
          <div className="field">
            <label>Quem escreve no WhatsApp da loja sem visita cadastrada</label>
            <select value={form.inbound_leads || 'true'} onChange={e => setForm(f => ({ ...f, inbound_leads: e.target.value }))}>
              <option value="true">Cadastrar como lead e responder</option>
              <option value="false">Ignorar a mensagem</option>
            </select>
            <div className="field-hint">
              Número que a loja não conhece (anúncio, site, indicação) vira cliente + visita "Lead do WhatsApp" + conversa
              no inbox, com o nome do perfil do WhatsApp. O bot (ou o assistente de IA, que pergunta nome e veículo e
              registra na ficha) responde na hora; a equipe recebe aviso e completa o cadastro em Visitas.
            </div>
            <button className="btn btn-primary mt-8" onClick={() => handleSave('inbound_leads')} disabled={saving}>Salvar</button>
          </div>
          <div className="field">
            <label>Como o bot responde</label>
            <select value={form.bot_mode || 'keywords'} onChange={e => setForm(f => ({ ...f, bot_mode: e.target.value }))}>
              <option value="keywords">Regras fixas — respostas prontas por palavras-chave</option>
              <option value="ai" disabled={ai && !ai.configured}>Assistente de IA — conversa com o catálogo desta loja</option>
            </select>
            <div className="field-hint">
              {ai === null && 'Verificando o assistente de IA...'}
              {ai && !ai.configured && 'O assistente de IA não está disponível na plataforma (sem chave do provedor). As lojas seguem nas regras fixas.'}
              {ai && ai.configured && (
                <>
                  IA disponível ({ai.provider} / {ai.model}). Ela se apresenta como assistente virtual, responde com o
                  catálogo desta loja e com as informações abaixo, manda as condições de pagamento pré-prontas dos
                  veículos e transfere ao vendedor em negociação, desconto, avaliação de usado, agendamento ou pedido
                  de pessoa. O que não souber, pergunta ao vendedor pelo inbox sem transferir. Se a IA falhar, a
                  resposta sai pelas regras fixas. Teste abaixo antes de ligar.
                </>
              )}
            </div>
            <button className="btn btn-primary mt-8" onClick={() => handleSave('bot_mode')} disabled={saving}>Salvar</button>
          </div>
          {ai?.configured && (
            <>
              <div className="field">
                <label>Assuntos que o assistente de IA responde sozinho</label>
                <textarea
                  value={form.ai_answer_topics || ''}
                  onChange={e => setForm(f => ({ ...f, ai_answer_topics: e.target.value }))}
                  rows={3}
                  maxLength={1000}
                  placeholder="preço de tabela, cores disponíveis, versões, opcionais, horário de funcionamento, endereço da loja, garantia"
                />
                <div className="field-hint">
                  Palavras-chave separadas por vírgula. Nestes assuntos a IA responde em vez de transferir, usando o
                  catálogo e as informações abaixo. Sem a informação cadastrada, ela pergunta ao vendedor pelo inbox
                  e segue na conversa.
                </div>
                <button className="btn btn-primary mt-8" onClick={() => handleSave('ai_answer_topics')} disabled={saving}>Salvar</button>
              </div>
              <div className="field">
                <label>Informações da loja para o assistente</label>
                <textarea
                  value={form.ai_store_info || ''}
                  onChange={e => setForm(f => ({ ...f, ai_store_info: e.target.value }))}
                  rows={6}
                  maxLength={3000}
                  placeholder={'Horário: segunda a sexta 8h às 18h, sábado 8h às 13h.\nEndereço: Av. Brasil, 1000 — Centro.\nGarantia: 1 ano de motor e câmbio nos seminovos.\nAceitamos usado na troca (avaliação com o vendedor).\nEntrega: 0 km a pronta entrega quando em estoque; encomenda em até 30 dias.'}
                />
                <div className="field-hint">
                  Texto livre, uma informação por linha. É daqui que a IA tira o que não está no catálogo (horário,
                  endereço, garantia, troca, entrega). O que não estiver aqui ela não inventa. Até 3000 caracteres.
                </div>
                <button className="btn btn-primary mt-8" onClick={() => handleSave('ai_store_info')} disabled={saving}>Salvar</button>
              </div>
            </>
          )}
          <div className="field">
            <label>Palavras-chave para transferir ao vendedor</label>
            <textarea value={form.seller_handoff_keywords || ''} onChange={e => setForm(f => ({ ...f, seller_handoff_keywords: e.target.value }))} rows={3} />
            <div className="field-hint">
              Separadas por vírgula. Nas regras fixas, qualquer mensagem com uma delas transfere na hora; o
              assistente de IA as entende como pedido de transferência quando usadas nesse sentido.
            </div>
            <button className="btn btn-primary mt-8" onClick={() => handleSave('seller_handoff_keywords')} disabled={saving}>Salvar</button>
          </div>
        </div>

        <div className="form-card">
          <h3>Respostas rápidas do chat</h3>
          <div className="field">
            <label>Uma resposta por linha</label>
            <textarea
              rows={6}
              value={form.quick_replies || ''}
              onChange={e => setForm(f => ({ ...f, quick_replies: e.target.value }))}
              placeholder={'Olá! Posso ajudar com mais alguma informação?\nPodemos agendar um test drive sem compromisso?'}
            />
            <div className="field-hint">
              Aparecem no botão ⚡ do chat, para quem atende responder as perguntas de sempre num
              toque. Clicar numa resposta preenche o campo — dá para ajustar antes de enviar.
            </div>
            <button className="btn btn-primary mt-8" onClick={() => handleSave('quick_replies')} disabled={saving}>Salvar</button>
          </div>
        </div>

        {ai?.configured && <AiSandbox storeName={form.concessionaria_name} />}

        <WhatsappSettings />
      </div>

      {saved && <div className="toast toast--success"><Icon name="check" size={16} /> Configuração salva!</div>}
    </div>
  );
}

// ─── SIMULADOR DO ASSISTENTE DE IA ────────────────────────────────────────────
// Conversa de teste com a mesma IA e o mesmo catálogo da loja ativa, sem
// cliente real e sem gravar nada: o histórico fica só nesta tela e vai inteiro
// ao backend a cada mensagem. Serve para ver o tom, se ela inventa preço e se
// transfere na hora certa — ANTES de ligar o modo "ai" para os clientes.
function AiSandbox({ storeName }) {
  const [clientName, setClientName] = useState('Josef');
  const [sellerName, setSellerName] = useState('');
  const [vehicle, setVehicle] = useState('');
  // Lead que escreveu sem visita: sem follow-up antes, nome pode ficar vazio
  // (= perfil do WhatsApp sem nome) e a IA registra o que descobrir.
  const [lead, setLead] = useState(false);
  const [history, setHistory] = useState([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [transfer, setTransfer] = useState(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [history, busy]);

  const followup = `Olá ${clientName || 'Cliente'}! Como foi sua experiência em ${storeName || 'nossa loja'}? Ficou com alguma dúvida sobre ${vehicle || 'o veículo que você viu'}?`;

  const reset = () => { setHistory([]); setTransfer(null); setError(null); setText(''); };

  const send = async () => {
    const msg = text.trim();
    if (!msg || busy || transfer) return;
    // A 1ª mensagem do cliente responde ao follow-up, como no fluxo real —
    // exceto o lead, que chega sem nada antes.
    const base = history.length ? history : (lead ? [] : [{ role: 'assistant', text: followup, followup: true }]);
    const next = [...base, { role: 'user', text: msg }];
    setHistory(next);
    setText('');
    setBusy(true);
    setError(null);
    try {
      const res = await aiApi.sandbox({
        history: next.map(m => ({ role: m.role, text: m.text })),
        client_first_name: clientName,
        seller_name: sellerName,
        vehicle_interest: vehicle,
        lead,
      });
      const d = res.data;
      // As ferramentas que encerram o turno sem transferir (condição pronta,
      // pedido de proposta) viram uma nota abaixo da resposta: no WhatsApp o
      // material sairia em seguida / o vendedor seria avisado, e a IA continua.
      const note = d.kind === 'payment_terms' && d.materials
        ? `${d.materials.kind === 'datasheet' ? '📋 Enviaria a ficha técnica' : '💳 Enviaria as condições comerciais'} do ${d.materials.vehicle} logo após esta mensagem: ${d.materials.titles.join(', ')}.`
        : d.kind === 'proposal_request'
          ? `📝 Avisaria o vendedor para montar uma proposta: ${d.reason}. A IA continua atendendo.`
          : d.kind === 'seller_question'
            ? `❓ Deixaria a dúvida para o vendedor no inbox: "${d.reason}". A IA continua atendendo.`
            : d.kind === 'vehicle_photos' && d.vehicle
              ? `🖼️ Enviaria ${d.vehicle.photos} ${d.vehicle.photos === 1 ? 'foto' : 'fotos'} do ${d.vehicle.label} logo após esta mensagem.`
              : d.kind === 'test_drive' && d.test_drive
                ? `🚗 ${d.test_drive.reschedule ? 'Remarcaria' : 'Agendaria'} o test-drive do ${d.test_drive.vehicle || 'veículo de interesse'} para ${d.test_drive.when}, mandaria a confirmação ao cliente e avisaria o vendedor. A IA continua atendendo.`
                : d.kind === 'test_drive_cancel'
                  ? '❌ Cancelaria o test-drive marcado do cliente e avisaria o vendedor. A IA continua atendendo.'
                  : null;
      // Lead simulado: o que registrar_lead gravaria na ficha (nome, veículo).
      const leadNote = d.lead_update
        ? `📝 Registraria no cadastro: ${[d.lead_update.name ? `nome "${d.lead_update.name}"` : null, d.lead_update.vehicles?.length ? `interesse "${d.lead_update.vehicles.join(', ')}"` : null].filter(Boolean).join('; ')}.`
        : null;
      setHistory(h => [...h, { role: 'assistant', text: d.text, trace: d.trace, usage: d.usage, kind: d.kind, note: [note, leadNote].filter(Boolean).join(' ') || null }]);
      if (d.kind === 'transfer') setTransfer(d.reason_label);
    } catch (err) {
      setError(err.response?.data?.error || 'Falha ao falar com a IA');
      setHistory(h => h.slice(0, -1));
      setText(msg);
    } finally {
      setBusy(false);
    }
  };

  const tokens = (u) => u ? `${u.promptTokenCount || 0} + ${u.candidatesTokenCount || 0} tokens` : null;

  return (
    <div className="form-card ai-sandbox">
      <h3>Testar o assistente de IA</h3>
      <div className="field-hint mb-8">
        Converse como se fosse o cliente. Usa o catálogo real desta loja; nada é enviado nem gravado.
        {lead
          ? ' Simulando um lead: alguém que escreveu no WhatsApp da loja sem visita cadastrada — a IA descobre nome e veículo e registraria na ficha.'
          : ' A primeira mensagem sua responde ao follow-up, como acontece no WhatsApp.'}
      </div>
      <div className="ai-sandbox-setup">
        <input placeholder={lead ? 'Nome do perfil do WhatsApp (vazio = sem nome)' : 'Nome do cliente'} value={clientName} onChange={e => setClientName(e.target.value)} disabled={history.length > 0} />
        <input placeholder="Vendedor (opcional)" value={sellerName} onChange={e => setSellerName(e.target.value)} disabled={history.length > 0} />
        <input placeholder={lead ? 'Interesse já registrado (opcional)' : 'Veículo da visita (opcional)'} value={vehicle} onChange={e => setVehicle(e.target.value)} disabled={history.length > 0} />
        <label className="checkbox-inline">
          <input type="checkbox" checked={lead} onChange={e => setLead(e.target.checked)} disabled={history.length > 0} />
          {' '}Lead que escreveu sem visita
        </label>
      </div>
      <div className="ai-sandbox-messages">
        {!history.length && !lead && (
          <div className="ai-sandbox-msg ai-sandbox-msg--bot ai-sandbox-msg--muted">{followup}</div>
        )}
        {history.map((m, i) => (
          <div key={i} className={`ai-sandbox-msg ${m.role === 'user' ? 'ai-sandbox-msg--client' : 'ai-sandbox-msg--bot'}`}>
            {m.text}
            {m.trace?.length > 0 && (
              <div className="ai-sandbox-trace">
                {m.trace.map((t, j) => (
                  <span key={j}>
                    {t.tool === 'buscar_veiculos'
                      ? `🔎 buscou "${t.args?.termo || 'tudo'}" (${t.encontrados} no catálogo)`
                      : t.tool === 'enviar_condicao_comercial'
                        ? `💳 condições comerciais do veículo #${t.args?.veiculo_id} (${t.itens} ${t.itens === 1 ? 'item' : 'itens'})`
                        : t.tool === 'enviar_ficha_tecnica'
                          ? `📋 ficha técnica do veículo #${t.args?.veiculo_id} (${t.itens} ${t.itens === 1 ? 'item' : 'itens'})`
                        : t.tool === 'pedir_proposta_personalizada'
                          ? `📝 pedir proposta: ${t.args?.motivo}`
                          : t.tool === 'perguntar_ao_vendedor'
                            ? `❓ perguntar ao vendedor: ${t.args?.pergunta}`
                            : t.tool === 'enviar_fotos_veiculo'
                              ? `🖼️ fotos do veículo #${t.args?.veiculo_id} (${t.fotos} cadastradas)`
                              : t.tool === 'registrar_lead'
                                ? `📝 registrar lead: ${[t.args?.nome, ...(Array.isArray(t.args?.veiculos) ? t.args.veiculos : [])].filter(Boolean).join(', ') || 'nada'}`
                              : `🤝 transferir: ${t.args?.motivo}`}
                  </span>
                ))}
                {tokens(m.usage) && <span>{tokens(m.usage)}</span>}
              </div>
            )}
            {m.note && <div className="ai-sandbox-note">{m.note}</div>}
          </div>
        ))}
        {busy && <div className="ai-sandbox-msg ai-sandbox-msg--bot ai-sandbox-msg--muted">digitando...</div>}
        {transfer && (
          <div className="ai-sandbox-system">🤖 Transferido ao vendedor: {transfer}. No WhatsApp, a conversa passaria ao vendedor aqui.</div>
        )}
        <div ref={endRef} />
      </div>
      {error && <div className="alert alert-error mt-8"><Icon name="x" size={16} /><div>{error}</div></div>}
      <div className="ai-sandbox-input">
        <input
          placeholder={transfer ? 'Conversa transferida — reinicie para testar de novo' : 'Escreva como o cliente...'}
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') send(); }}
          disabled={busy || Boolean(transfer)}
        />
        <button className="btn btn-primary" onClick={send} disabled={busy || Boolean(transfer) || !text.trim()}>Enviar</button>
        <button className="btn btn-secondary" onClick={reset} disabled={busy || !history.length}>Reiniciar</button>
      </div>
    </div>
  );
}

// ─── WHATSAPP DA LOJA ─────────────────────────────────────────────────────────
function WhatsappSettings() {
  const [form, setForm] = useState({ phone_number_id: '', access_token: '', verify_token: '', template: '' });
  const [current, setCurrent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    organizationsApi.getWhatsapp().then(res => {
      setCurrent(res.data);
      setForm({
        phone_number_id: res.data.phone_number_id || '',
        access_token: '',
        verify_token: res.data.verify_token || '',
        template: res.data.template || 'followup_visit',
      });
    }).catch(() => setFeedback({ type: 'error', text: 'Erro ao carregar a configuração' }));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFeedback(null);
    try {
      await organizationsApi.updateWhatsapp('me', form);
      const res = await organizationsApi.getWhatsapp();
      setCurrent(res.data);
      // O token não volta do servidor: limpar o campo evita dar a impressão
      // de que o valor exibido é o que está gravado.
      setForm(f => ({ ...f, access_token: '' }));
      setFeedback({ type: 'success', text: 'Configuração do WhatsApp salva!' });
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.error || 'Erro ao salvar' });
    } finally {
      setSaving(false);
    }
  };

  // A URL precisa apontar para a API, não para o painel: em produção eles ficam
  // em domínios diferentes, e copiar o endereço do painel para a Meta faria o
  // webhook nunca entregar mensagem nenhuma.
  const apiBase = process.env.REACT_APP_API_URL || `${window.location.origin}/api`;
  const webhookUrl = `${apiBase.replace(/\/$/, '')}/webhook`.replace(/^http:/, 'https:');

  return (
    <div className="form-card form-card--full">
      <h3>WhatsApp Business API</h3>

      {feedback && (
        <div className={`alert alert-${feedback.type}`}>
          <Icon name={feedback.type === 'success' ? 'check' : 'x'} size={18} />
          <div>{feedback.text}</div>
        </div>
      )}

      <div className="info-box">
        <p>
          <strong>Situação: </strong>
          {current?.configured
            ? '✅ Número conectado — as mensagens são enviadas de verdade.'
            : '⚠️ Ainda não configurado — o sistema funciona em modo simulação (nada é enviado ao cliente).'}
        </p>
        <p>Pegue estes dados no <strong>Meta Business Manager → WhatsApp → Configuração da API</strong>.</p>
      </div>

      <form onSubmit={handleSave}>
        <div className="form-grid">
          <div className="field">
            <label>Phone Number ID *</label>
            <input value={form.phone_number_id} required
              onChange={e => setForm(f => ({ ...f, phone_number_id: e.target.value }))} />
            <div className="field-hint">Identificador do número, só números.</div>
          </div>
          <div className="field">
            <label>Token de acesso permanente</label>
            <input type="password" autoComplete="new-password" value={form.access_token} placeholder={current?.access_token_preview || 'Cole o token aqui'}
              onChange={e => setForm(f => ({ ...f, access_token: e.target.value }))} />
            <div className="field-hint">
              {current?.access_token_preview
                ? `Já existe um token salvo (${current.access_token_preview}). Deixe vazio para mantê-lo.`
                : 'Guardado criptografado; nunca é exibido de volta.'}
            </div>
          </div>
          <div className="field">
            <label>Token de verificação do webhook *</label>
            <input value={form.verify_token} required
              onChange={e => setForm(f => ({ ...f, verify_token: e.target.value }))} />
            <div className="field-hint">Uma senha que você inventa e repete na Meta.</div>
          </div>
          <div className="field">
            <label>Nome do template de follow-up</label>
            <input value={form.template}
              onChange={e => setForm(f => ({ ...f, template: e.target.value }))} />
            <div className="field-hint">Template aprovado na Meta, com 3 parâmetros.</div>
          </div>
        </div>

        <div className="field">
          <label>URL do webhook para cadastrar na Meta</label>
          <code className="code-block">{webhookUrl}</code>
          <div className="field-hint">Marque o campo <strong>messages</strong> ao configurar.</div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar configuração'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── USUÁRIOS ─────────────────────────────────────────────────────────────────
function UsersPage({ currentUser, stores = [], scopeOrgs = [], activeStore }) {
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const loginKinds = creatableLoginKinds(currentUser);
  const emptyForm = {
    name: '', email: '', password: '',
    kind: (loginKinds[0] || {}).key || 'receptionist',
    organization_id: '',
    // Só para o perfil Vendedor: o telefone para onde vai o aviso de cliente
    // esperando, e o vendedor sem login que este cadastro vai vincular.
    phone: '', seller_id: '',
  };
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);
  // Vendedores cadastrados antes de 03/11/2026 sem conta de acesso. Desde então
  // o vendedor nasce do usuário (perfil Vendedor cria o registro de vendedor
  // junto), e a antiga tela de Vendedores saiu; estes ficam listados à parte,
  // com "Criar login" — que vincula em vez de duplicar a pessoa.
  const [sellersNoLogin, setSellersNoLogin] = useState([]);

  const kind = LOGIN_KINDS.find(k => k.key === form.kind) || LOGIN_KINDS[2];
  const isSellerKind = kind.role === 'seller';

  // Cada tipo de login pertence a um nível: diretor à concessionária, gerente,
  // recepcionista e vendedor à loja. O select de empresa só oferece esse nível —
  // escolher "gerente" e apontar para um grupo seria recusado pelo backend.
  const orgOptions = scopeOrgs.filter(
    org => org.type === kind.orgType && String(org.id) !== String(currentUser?.organization_id)
  );

  const load = useCallback(async () => {
    try {
      const res = await usersApi.list();
      setUsers(res.data);
    } catch (e) {
      setFeedback({ type: 'error', text: 'Erro ao carregar usuários' });
    } finally {
      setLoading(false);
    }
    // Falhar aqui não derruba a tela: a seção simplesmente não aparece.
    sellersApi.list({ include_inactive: true })
      .then(res => setSellersNoLogin((res.data || []).filter(s => !s.user_id)))
      .catch(() => setSellersNoLogin([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // O tipo de login escolhido carrega o papel; a empresa vem do select, e a
    // loja ativa da sidebar serve de padrão quando o alvo é uma loja.
    const payload = {
      name: form.name,
      email: form.email,
      password: form.password,
      role: kind.role,
      organization_id:
        form.organization_id || (kind.orgType === 'loja' && activeStore ? activeStore : ''),
    };
    if (!payload.organization_id) delete payload.organization_id;
    if (isSellerKind) {
      payload.phone = form.phone;
      if (form.seller_id) payload.seller_id = Number(form.seller_id);
    }

    try {
      const res = await usersApi.create(payload);
      setUsers(u => [...u, res.data]);
      setForm({ ...emptyForm, kind: form.kind });
      setShowForm(false);
      setFeedback({
        type: 'success',
        text: isSellerKind
          ? `${res.data.name} cadastrado — já aparece em Nova Visita e recebe os avisos no WhatsApp.`
          : `${res.data.name} cadastrado.`,
      });
      load();
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.error || 'Erro ao criar usuário' });
    }
  };

  // "Criar login" de um vendedor sem conta: abre o cadastro já preenchido e
  // guarda o id para o backend vincular o registro existente (histórico e
  // visitas continuam com ele) em vez de criar um segundo vendedor.
  const startLoginFor = (seller) => {
    setEditing(null);
    setForm({
      ...emptyForm,
      name: seller.name || '',
      email: seller.email || '',
      kind: 'seller',
      organization_id: String(seller.organization_id),
      phone: seller.phone || '',
      seller_id: String(seller.id),
    });
    setShowForm(true);
    setFeedback(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleSellerNoLogin = async (seller) => {
    try {
      await sellersApi.update(seller.id, { active: !seller.active });
      setFeedback({
        type: 'success',
        text: seller.active
          ? `${seller.name} desativado — não aparece mais para novas visitas.`
          : `${seller.name} reativado.`,
      });
      load();
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.error || 'Erro ao alterar a situação do vendedor' });
    }
  };

  // O superadmin não tem organização própria: o alvo vai na query. O admin
  // dispensa — o escopo vem da conta dele.
  const orgParam = (u) => (currentUser?.role === 'superadmin' ? u.organization_id : undefined);

  const toggleActive = async (u) => {
    try {
      await usersApi.update(u.id, { active: !u.active }, orgParam(u));
      setFeedback({
        type: 'success',
        text: u.active
          ? (u.role === 'seller'
            ? `${u.name} desativado — perde o acesso agora e deixa de receber visitas e avisos.`
            : `${u.name} desativado — perde o acesso ao sistema agora.`)
          : `${u.name} reativado.`,
      });
      // Reflete na hora, sem esperar o reload: a chave precisa responder ao toque.
      setUsers(list => list.map(x => (x.id === u.id ? { ...x, active: !u.active } : x)));
      if (editing?.id === u.id) setEditing(e => ({ ...e, active: !u.active }));
      load();
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.error || 'Erro ao alterar o acesso' });
    }
  };

  // ── Ficha do usuário (clicar na linha) ─────────────────────────────────
  // Abre no topo da tela, como a edição das outras telas. Nome, e-mail e senha
  // nova; o perfil só entre os que este administrador pode conceder no nível
  // da empresa daquela pessoa (mesma regra do cadastro). Empresa não muda.
  // Para o vendedor, também o telefone do aviso.
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', password: '', kind: '', phone: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  const kindOf = (u) => LOGIN_KINDS.find(k => k.role === u.role && k.orgType === u.organization_type)?.key || '';
  const editKinds = editing ? loginKinds.filter(k => k.orgType === editing.organization_type) : [];
  const editIsSelf = editing && String(editing.id) === String(currentUser?.id);
  const editIsSeller = Boolean(editing) && (
    (LOGIN_KINDS.find(k => k.key === editForm.kind)?.role || editing.role) === 'seller'
  );

  const startEdit = (u) => {
    setEditing(u);
    setEditForm({ name: u.name || '', email: u.email || '', password: '', kind: kindOf(u), phone: u.seller_phone || '' });
    setShowForm(false);
    setFeedback(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    setSavingEdit(true);
    setFeedback(null);
    try {
      const payload = { name: editForm.name.trim(), email: editForm.email.trim() };
      if (editForm.password) payload.password = editForm.password;
      const chosen = LOGIN_KINDS.find(k => k.key === editForm.kind);
      if (chosen && chosen.role !== editing.role && !editIsSelf) payload.role = chosen.role;
      if (editIsSeller) payload.phone = editForm.phone;
      await usersApi.update(editing.id, payload, orgParam(editing));
      setFeedback({ type: 'success', text: `${payload.name} atualizado.` });
      setEditing(null);
      load();
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.error || 'Erro ao salvar o usuário' });
    } finally {
      setSavingEdit(false);
    }
  };

  // A loja só aparece nas linhas de vendedor sem login quando há mais de uma
  // no escopo: para um admin de loja seria a mesma palavra em todas.
  const showStoreOnSellers = new Set(sellersNoLogin.map(s => s.organization_id)).size > 1;

  return (
    <div className="page">
      <div className="page-header">
        <div><h1>Usuários</h1><p>Acessos ao sistema e equipe de vendas</p></div>
        <button className="btn btn-primary" onClick={() => { setEditing(null); if (showForm) setForm({ ...emptyForm, kind: form.kind }); setShowForm(!showForm); }}>
          <Icon name="plus" size={16} /> Novo Usuário
        </button>
      </div>

      {feedback && (
        <div className={`alert alert-${feedback.type}`}>
          <Icon name={feedback.type === 'success' ? 'check' : 'x'} size={18} />
          <div>{feedback.text}</div>
        </div>
      )}

      {editing && (
        <div className="form-card">
          <h3><Icon name="edit" size={16} /> Ficha do usuário</h3>
          <form onSubmit={saveEdit}>
            <div className="form-grid">
              <div className="field"><label>Nome *</label><input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} required /></div>
              <div className="field"><label>E-mail *</label><input type="email" autoComplete="off" value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} required /></div>
              <div className="field">
                <label>Nova senha <span className="label-note">(deixe em branco para manter)</span></label>
                <input type="password" autoComplete="new-password" value={editForm.password} onChange={e => setEditForm(f => ({ ...f, password: e.target.value }))} minLength={8} placeholder="••••••••" />
              </div>
              <div className="field">
                <label>Perfil</label>
                {editKinds.length > 0 && !editIsSelf ? (
                  <select value={editForm.kind} onChange={e => setEditForm(f => ({ ...f, kind: e.target.value }))}>
                    {!editKinds.some(k => k.key === editForm.kind) && <option value={editForm.kind}>{loginKindLabel(editing)}</option>}
                    {editKinds.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
                  </select>
                ) : (
                  <input value={loginKindLabel(editing)} disabled />
                )}
                {editIsSelf && <div className="field-hint">O próprio perfil não se altera por aqui.</div>}
                {!editIsSelf && editing.role === 'seller' && !editIsSeller && (
                  <div className="field-hint field-hint--warn">
                    Ao deixar de ser vendedor, {editing.name.split(' ')[0]} sai de Nova Visita e dos avisos; o histórico das visitas fica.
                  </div>
                )}
              </div>
              {editIsSeller && (
                <div className="field">
                  <label>Telefone/WhatsApp do vendedor</label>
                  <input value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} placeholder="(81) 99999-9999" />
                  <div className="field-hint">
                    É para onde vai o aviso de cliente esperando.
                    {editing.seller_id ? ` ${editing.total_visits || 0} visitas · ${editing.total_conversations || 0} conversas.` : ''}
                  </div>
                </div>
              )}
              <div className="field">
                <label>Empresa</label>
                <input value={`${editing.organization_name || ''}${editing.organization_type ? ` (${ORG_TYPE_LABELS[editing.organization_type]})` : ''}`} disabled />
                <div className="field-hint">A empresa não muda: um login pertence ao nível em que foi criado.</div>
              </div>
              <div className="field">
                <label>Acesso</label>
                <div className="switch-row">
                  <label className="switch" title={editIsSelf ? 'Você não pode desativar a própria conta' : editing.active ? 'Desativar acesso' : 'Reativar acesso'}>
                    <input type="checkbox" checked={Boolean(editing.active)} disabled={editIsSelf} onChange={() => toggleActive(editing)} />
                    <span className="switch-slider" />
                  </label>
                  <span className={`status-badge status-badge--${editing.active ? 'green' : 'red'}`}>{editing.active ? 'Ativo' : 'Inativo'}</span>
                  <span className="field-hint">Criado em {new Date(editing.created_at).toLocaleDateString('pt-BR')}</span>
                </div>
                {editing.role === 'seller' && (
                  <div className="field-hint">Desativar tira o acesso e também o vendedor de Nova Visita e dos avisos no WhatsApp.</div>
                )}
              </div>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setEditing(null)}>Cancelar</button>
              <button type="submit" className="btn btn-primary" disabled={savingEdit || !editForm.name.trim() || !editForm.email.trim()}>
                {savingEdit ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </form>
        </div>
      )}

      {showForm && (
        <div className="form-card">
          <h3>{form.seller_id ? `Criar login para ${form.name || 'o vendedor'}` : 'Novo Usuário'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="field"><label>Nome *</label><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required /></div>
              <div className="field"><label>E-mail *</label><input type="email" autoComplete="off" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required /></div>
              <div className="field"><label>Senha *</label><input type="password" autoComplete="new-password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required minLength={8} /></div>
              <div className="field">
                <label>Perfil *</label>
                {/* Só os logins que este usuário pode conceder: o gerente de uma
                    loja cadastra recepcionista e vendedor, não outro gerente.
                    Vinculando um vendedor existente, o perfil é fixo. */}
                <select
                  value={form.kind}
                  disabled={Boolean(form.seller_id)}
                  onChange={e => setForm(f => ({ ...f, kind: e.target.value, organization_id: '' }))}
                >
                  {loginKinds.map(k => (
                    <option key={k.key} value={k.key}>{k.label}</option>
                  ))}
                </select>
              </div>

              {isSellerKind && (
                <div className="field">
                  <label>Telefone/WhatsApp do vendedor</label>
                  <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="(81) 99999-9999" />
                  <div className="field-hint">É para onde vai o aviso de cliente esperando. O vendedor já entra em Nova Visita ao salvar.</div>
                </div>
              )}

              {/* O select de empresa só aparece quando há escolha a fazer: um
                  gerente de loja cadastrando recepcionista já sabe a loja. */}
              {orgOptions.length > 0 && (
                <div className="field field--full">
                  <label>{ORG_TYPE_LABELS[kind.orgType]} *</label>
                  <select
                    value={form.organization_id || (kind.orgType === 'loja' && activeStore ? String(activeStore) : '')}
                    disabled={Boolean(form.seller_id)}
                    onChange={e => setForm(f => ({ ...f, organization_id: e.target.value }))}
                    required={!(kind.orgType === 'loja' && activeStore)}
                  >
                    <option value="">Selecione...</option>
                    {orgOptions.map(org => (
                      <option key={org.id} value={org.id}>
                        {org.parent_name ? `${org.parent_name} — ${org.name}` : org.name}
                      </option>
                    ))}
                  </select>
                  <div className="field-hint">
                    Em qual {ORG_TYPE_LABELS[kind.orgType].toLowerCase()} esta pessoa trabalha.
                  </div>
                </div>
              )}

              {orgOptions.length === 0 && currentUser?.organization_type !== kind.orgType && (
                <div className="field field--full">
                  <div className="field-hint field-hint--error">
                    Nenhuma {ORG_TYPE_LABELS[kind.orgType].toLowerCase()} cadastrada ainda.
                    Crie em Cadastro › Minha Rede antes de dar um login a ela.
                  </div>
                </div>
              )}
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => { setShowForm(false); setForm({ ...emptyForm, kind: form.seller_id ? emptyForm.kind : form.kind }); }}>Cancelar</button>
              <button type="submit" className="btn btn-primary">{form.seller_id ? 'Criar login' : 'Criar Usuário'}</button>
            </div>
          </form>
        </div>
      )}

      {loading ? <SkeletonList rows={6} /> : (
        <div className="table-container">
          <table className="table">
            <thead><tr><th>Nome</th><th>E-mail</th><th>Perfil</th><th>Acesso</th><th>Criado em</th></tr></thead>
            <tbody>
              {users.length === 0 && (
                <tr><td colSpan={5} className="empty-row">
                  <EmptyState
                    icon="users"
                    title="Nenhum usuário neste escopo"
                    hint="Cadastre o primeiro acesso no formulário acima."
                  />
                </td></tr>
              )}
              {users.map(u => {
                // Desativar a si mesmo trancaria o admin para fora do sistema.
                // O backend também recusa; aqui a ação nem é oferecida.
                const isSelf = String(u.id) === String(currentUser?.id);
                // Outro administrador do mesmo nível: o backend recusa a
                // alteração, então a chave nem é oferecida.
                const locked = isLockedAdmin(u, currentUser);
                return (
                  // Clicar na linha abre a ficha; a chave de acesso fica fora
                  // do clique da linha, senão ligar/desligar também abriria a ficha.
                  <tr key={u.id} className={`row-click${editing?.id === u.id ? ' row-click--active' : ''}`} onClick={() => startEdit(u)}>
                    <td data-label="Nome">
                      <div className="cell-name">{u.name}</div>
                      {isSelf && <div className="cell-sub">você</div>}
                      {/* O telefone do vendedor é o do aviso de cliente esperando;
                          sem ele, só o push e o inbox avisam. */}
                      {u.role === 'seller' && (
                        <div className="cell-sub">{u.seller_phone || 'sem telefone para avisos'}</div>
                      )}
                    </td>
                    <td data-label="E-mail">{u.email}</td>
                    {/* "Diretor"/"Gerente" em vez de "Administrador": é o nível da
                        empresa que dá o nome ao cargo. */}
                    <td data-label="Perfil"><span className="role-badge">{loginKindLabel(u)}</span></td>
                    <td data-label="Acesso" onClick={e => e.stopPropagation()}>
                      <div className="switch-row">
                        <label className="switch" title={isSelf ? 'Você não pode desativar a própria conta' : locked ? 'Administrador do seu próprio nível: a alteração é feita pelo suporte' : u.active ? 'Desativar acesso' : 'Reativar acesso'}>
                          <input type="checkbox" checked={Boolean(u.active)} disabled={isSelf || locked} onChange={() => toggleActive(u)} />
                          <span className="switch-slider" />
                        </label>
                        <span className={`status-badge status-badge--${u.active ? 'green' : 'red'}`}>{u.active ? 'Ativo' : 'Inativo'}</span>
                      </div>
                    </td>
                    <td data-label="Criado em">{new Date(u.created_at).toLocaleDateString('pt-BR')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {sellersNoLogin.length > 0 && (
        <div className="list-card">
          <div className="list-card-head">
            <h3>Vendedores sem login <span className="badge">{sellersNoLogin.length}</span></h3>
          </div>
          <div className="field-hint" style={{ padding: '0 16px 12px' }}>
            Cadastrados antes de o vendedor nascer do usuário: recebem visitas e avisos, mas não entram no sistema.
            "Criar login" dá o acesso à mesma pessoa, mantendo o histórico.
          </div>
          <ul className="todo-list">
            {sellersNoLogin.map(s => (
              <li key={s.id} className={`todo-item${s.active ? '' : ' todo-item--inactive'}`}>
                <div className="todo-mark">{s.name.charAt(0)}</div>
                <div className="todo-body">
                  <div className="todo-title">
                    {s.name}
                    {!s.active && <span className="status-badge status-badge--red ml-8">Inativo</span>}
                  </div>
                  <div className="todo-meta">
                    {showStoreOnSellers && <span><Icon name="car" size={13} /> {s.store_name}</span>}
                    {s.phone && <span><Icon name="phone" size={13} /> {s.phone}</span>}
                    {s.email && <span>{s.email}</span>}
                    <span>{s.total_visits} visitas</span>
                    <span>{s.total_conversations} conversas</span>
                  </div>
                </div>
                <div className="todo-actions">
                  <button className="btn btn-primary btn-sm" onClick={() => startLoginFor(s)}>
                    <Icon name="user" size={14} /> Criar login
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => toggleSellerNoLogin(s)}>
                    {s.active ? 'Desativar' : 'Reativar'}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ─── CLIENTES (superadmin) ────────────────────────────────────────────────────
const EMPTY_ORG_FORM = {
  type: 'grupo', parent_id: '', brand: '', brand_other: '',
  name: '', notes: '',
  // Ficha cadastral — o "Buscar CNPJ" preenche daqui para baixo.
  cnpj: '', razao_social: '', cep: '', logradouro: '', numero: '',
  complemento: '', bairro: '', cidade: '', uf: '', codigo_ibge: '',
  phone: '', email: '', inscricao_estadual: '', inscricao_municipal: '',
  contacts: [],
  admin_name: '', admin_email: '', admin_password: '',
  // Só no cadastro de loja, e só para filtrar as concessionárias: o backend
  // recebe o parent_id (a concessionária), nunca o grupo.
  group_id: '',
};

// Campos que a consulta de CNPJ devolve e o formulário absorve. `name` fica fora:
// o nome fantasia é sugerido, mas não sobrescreve o que a pessoa já digitou.
const CNPJ_FILLED_FIELDS = [
  'razao_social', 'cep', 'logradouro', 'numero', 'complemento',
  'bairro', 'cidade', 'uf', 'codigo_ibge', 'phone',
];

/** "8132220000" → "(81) 3222-0000"; "81997856194" → "(81) 99785-6194". */
function maskOrgPhone(value) {
  const d = String(value || '').replace(/\D/g, '').slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

/**
 * Níveis que o usuário pode cadastrar. Espelha creatableTypes no backend — aqui é
 * só para não oferecer o que seria recusado; a autorização de verdade é lá.
 *
 * Grupo é cliente novo da plataforma (uma venda), então fica com o superadmin.
 */
function creatableOrgTypes(user) {
  if (user?.role === 'superadmin') return ORG_TYPES.map(t => t.value);
  if (user?.organization_type === 'grupo') return ['concessionaria', 'loja'];
  if (user?.organization_type === 'concessionaria') return ['loja'];
  return [];
}

/**
 * Cadastro de UM nível da árvore de clientes: grupos, concessionárias ou lojas.
 *
 * O nível vem do menu (`level`), não de um seletor dentro da tela: são três
 * tarefas distintas, e a ordem no menu — grupo, concessionária, loja — é a própria
 * hierarquia. Cada tela lista o nível dela e oferece os pais que ele precisa.
 *
 * Serve dois públicos: o superadmin, que administra a plataforma inteira e pode
 * suspender contas, e o administrador de um grupo ou concessionária, que expande a
 * PRÓPRIA rede — a API devolve só a subárvore dele.
 */
function OrganizationsPage({ user, level, selfMode = false, storeId = null }) {
  const isSuperadmin = user?.role === 'superadmin';
  // selfMode: a tela "Minha Loja" — o MESMO formulário desta página, aberto
  // direto na loja ativa, sem lista nem "+ nova". A ficha (CNPJ, endereço,
  // telefone, inscrições) alimenta o PDF da proposta e a nota fiscal.
  const ownLevel = selfMode && Number(user?.organization_id) === Number(storeId);
  // O dono da plataforma faz só o primeiro acesso: CNPJ, nome e o login do
  // administrador. Razão social, endereço, telefone, inscrições e contatos são
  // do cliente, que preenche em Cadastro › Minha Loja (decisão de 09/09/2026).
  const fullProfile = !isSuperadmin;
  const allowedTypes = creatableOrgTypes(user);
  const levelMeta = ORG_TYPES.find(t => t.value === level) || ORG_TYPES[2];
  const [orgs, setOrgs] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  // O nível do formulário é o da tela: não há o que escolher.
  const [form, setForm] = useState({ ...EMPTY_ORG_FORM, type: level });
  // UM formulário serve cadastrar e editar. Antes eram dois, e o de edição só
  // tinha nome, marca e observações — CNPJ, endereço e contatos ficavam de fora
  // sem que ninguém notasse. Um formulário só não tem como divergir do outro.
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await organizationsApi.list();
      setOrgs(res.data);
    } catch (e) {
      setFeedback({ type: 'error', text: 'Erro ao carregar os clientes' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // A lista de marcas vem da API, e não de uma constante daqui, para que atualizar
  // as marcas do mercado não exija rebuild do painel.
  useEffect(() => {
    brandsApi.list().then(res => setBrands(res.data)).catch(() => setBrands([]));
  }, []);

  const groups = orgs.filter(o => o.type === 'grupo');
  const dealers = orgs.filter(o => o.type === 'concessionaria');

  /**
   * As empresas DESTE nível, com o caminho até elas.
   *
   * A API devolve os três níveis numa lista plana; cada tela filtra o seu. O
   * caminho ("Grupo Alpha › Fiat Recife") substitui a indentação da árvore: numa
   * lista de um nível só, indentar não diria nada, mas saber de quem a loja é, sim.
   */
  const rows = useMemo(() => {
    const byId = new Map(orgs.map(o => [o.id, o]));

    const pathOf = (org) => {
      const parts = [];
      let cursor = byId.get(org.parent_id);
      while (cursor) {
        parts.unshift(cursor.name);
        cursor = byId.get(cursor.parent_id);
      }
      return parts.join(' › ');
    };

    return orgs
      .filter(o => o.type === level)
      .map(o => ({ ...o, path: pathOf(o) }))
      .sort((a, b) =>
        a.path.localeCompare(b.path, 'pt-BR') || a.name.localeCompare(b.name, 'pt-BR'));
  }, [orgs, level]);

  // ─── Buscar CNPJ ────────────────────────────────────────────────────────────
  const [lookingUp, setLookingUp] = useState(false);
  const [cnpjNote, setCnpjNote] = useState(null);

  const buscarCnpj = async () => {
    setLookingUp(true);
    setCnpjNote(null);
    try {
      const { data } = await cnpjApi.lookup(form.cnpj);

      setForm(f => {
        const filled = { ...f, cnpj: data.cnpj };
        for (const field of CNPJ_FILLED_FIELDS) {
          if (data[field]) filled[field] = data[field];
        }
        // O nome fantasia é sugestão: só entra se o campo estiver vazio, para não
        // apagar o nome que a pessoa já escolheu para a loja.
        if (!f.name.trim() && data.name) filled.name = data.name;
        // O telefone da Receita vira o primeiro contato, se ainda não houver nenhum.
        if (data.phone && f.contacts.length === 0) {
          filled.contacts = [{ ...emptyContact(), name: data.name || 'Contato principal', phone: data.phone }];
        }
        return filled;
      });

      setCnpjNote({
        type: 'success',
        text: `${data.razao_social || 'Empresa'}${data.situacao ? ` — situação ${data.situacao}` : ''}.`,
      });
    } catch (err) {
      // A consulta é conveniência: falhar não impede o cadastro manual.
      setCnpjNote({ type: 'error', text: apiErrorMessage(err, 'Não foi possível consultar o CNPJ') });
    } finally {
      setLookingUp(false);
    }
  };

  // ─── "+ novo grupo" / "+ nova concessionária" ───────────────────────────────
  // Cadastrar a loja costuma ser o momento em que se descobre que o pai dela ainda
  // não existe. Sair da tela para criá-lo perderia o que já foi digitado, então o
  // pai é criado aqui mesmo e entra já selecionado.
  const [inlineParent, setInlineParent] = useState(null);

  // O tipo vem de quem chamou: no cadastro de loja há DOIS atalhos — "+ novo"
  // no select de grupo e "+ nova" no de concessionária.
  const openInlineParent = (type) => {
    setInlineParent({
      type,
      name: '',
      brand: '',
      // Uma concessionária criada aqui precisa do grupo dela; havendo um só, já vem.
      parent_id: type === 'concessionaria' && groups.length === 1 ? String(groups[0].id) : '',
    });
    setFeedback(null);
  };

  const saveInlineParent = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const payload = { type: inlineParent.type, name: inlineParent.name };
      if (inlineParent.type === 'concessionaria') {
        payload.parent_id = inlineParent.parent_id;
        payload.brand = inlineParent.brand;
      }
      const res = await organizationsApi.create(payload);
      const created = res.data.organization;

      await load();
      if (created.type === 'grupo' && level === 'loja') {
        // No cadastro de loja o grupo é o PRIMEIRO select da cascata (filtro),
        // não o pai direto: entra ali, e a concessionária volta a "escolher" —
        // um grupo recém-criado ainda não tem nenhuma.
        setForm(f => ({ ...f, group_id: String(created.id), parent_id: '' }));
      } else {
        setForm(f => ({ ...f, parent_id: String(created.id) }));
      }
      setInlineParent(null);
      setFeedback({
        type: 'success',
        text: `${ORG_TYPE_LABELS[created.type]} "${created.name}" ${created.type === 'grupo' ? 'criado e já selecionado' : 'criada e já selecionada'}.`,
      });
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.error || 'Erro ao criar' });
    } finally {
      setSaving(false);
    }
  };

  // ─── Login admin da empresa em edição ───────────────────────────────────────
  // O login não entra no payload da empresa: mexer no acesso de alguém é outra
  // ação, com outro efeito (a pessoa passa a entrar, ou deixa de entrar), e
  // misturar as duas coisas num "Salvar" só esconderia isso.
  const editingOrg = editingId ? orgs.find(o => o.id === editingId) : null;

  // ── Logo da empresa ────────────────────────────────────────────────────
  // Object URL da logo da empresa em edição, buscada pela rota autenticada.
  // Sobe e sai na hora, fora do Salvar: é um arquivo, não um campo do form.
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const editingHasLogo = Boolean(editingOrg?.has_logo);
  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    setLogoUrl(null);
    if (!editingId || !editingHasLogo) return undefined;
    organizationsApi.logo(editingId)
      .then((res) => {
        objectUrl = URL.createObjectURL(res.data);
        if (!cancelled) setLogoUrl(objectUrl); else URL.revokeObjectURL(objectUrl);
      })
      .catch(() => {});
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [editingId, editingHasLogo]);

  const applyHasLogo = (id, has_logo) => setOrgs(list => list.map(o => (o.id === id ? { ...o, has_logo } : o)));

  const uploadLogo = async (file) => {
    if (!file || !editingId) return;
    if (!/\.(png|jpe?g)$/i.test(file.name)) { setFeedback({ type: 'error', text: 'Envie a logo em PNG ou JPG' }); return; }
    if (file.size > 2 * 1024 * 1024) { setFeedback({ type: 'error', text: 'Logo maior que 2MB' }); return; }
    setLogoBusy(true);
    setFeedback(null);
    try {
      await organizationsApi.uploadLogo(editingId, file);
      setLogoUrl(URL.createObjectURL(file));
      applyHasLogo(editingId, true);
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.error || 'Erro ao enviar a logo' });
    } finally {
      setLogoBusy(false);
    }
  };

  const removeLogo = async () => {
    if (!editingId) return;
    setLogoBusy(true);
    try {
      await organizationsApi.deleteLogo(editingId);
      setLogoUrl(null);
      applyHasLogo(editingId, false);
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.error || 'Erro ao remover a logo' });
    } finally {
      setLogoBusy(false);
    }
  };

  const [adminDraft, setAdminDraft] = useState({ id: null, name: '', email: '', password: '' });
  // Sem login nenhum o bloco já vem aberto; havendo, só abre quando se pede.
  const [addingAdmin, setAddingAdmin] = useState(false);

  const startAdminEdit = (admin) => {
    setAddingAdmin(false);
    setAdminDraft({ id: admin.id, name: admin.name, email: admin.email, password: '' });
  };
  const closeAdminDraft = () => {
    setAddingAdmin(false);
    setAdminDraft({ id: null, name: '', email: '', password: '' });
  };

  const saveAdmin = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      if (adminDraft.id) {
        const payload = { name: adminDraft.name, email: adminDraft.email };
        // Senha em branco mantém a atual: o campo é para redefinir, não para
        // confirmar o que já existe.
        if (adminDraft.password) payload.password = adminDraft.password;
        await usersApi.update(adminDraft.id, payload, editingId);
        setFeedback({ type: 'success', text: `Login de ${adminDraft.name} atualizado.` });
      } else {
        await usersApi.create({
          name: adminDraft.name,
          email: adminDraft.email,
          password: adminDraft.password,
          role: 'admin',
          organization_id: editingId,
        });
        setFeedback({ type: 'success', text: `Login criado. ${adminDraft.email} já pode entrar.` });
      }
      closeAdminDraft();
      load();
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.error || 'Erro ao salvar o login' });
    } finally {
      setSaving(false);
    }
  };

  const toggleAdminActive = async (admin) => {
    setFeedback(null);
    try {
      await usersApi.update(admin.id, { active: !admin.active }, editingId);
      setFeedback({
        type: 'success',
        text: admin.active
          ? `${admin.name} perdeu o acesso agora.`
          : `${admin.name} voltou a ter acesso.`,
      });
      load();
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.error || 'Erro ao alterar o acesso' });
    }
  };

  // ─── Contatos ───────────────────────────────────────────────────────────────
  const addContact = () => setForm(f => ({ ...f, contacts: [...f.contacts, emptyContact()] }));
  const changeContact = (index, field, value) => setForm(f => ({
    ...f,
    contacts: f.contacts.map((c, i) => (i === index ? { ...c, [field]: value } : c)),
  }));
  const removeContact = (index) => setForm(f => ({
    ...f, contacts: f.contacts.filter((_, i) => i !== index),
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFeedback(null);

    const payload = {
      type: level,
      name: form.name,
      notes: form.notes,
      cnpj: form.cnpj,
      razao_social: form.razao_social,
      cep: form.cep,
      logradouro: form.logradouro,
      numero: form.numero,
      complemento: form.complemento,
      bairro: form.bairro,
      cidade: form.cidade,
      uf: form.uf,
      codigo_ibge: form.codigo_ibge,
      phone: form.phone,
      email: form.email,
      inscricao_estadual: form.inscricao_estadual,
      inscricao_municipal: form.inscricao_municipal,
      // Linhas em branco do repetidor não viram contato.
      contacts: form.contacts.filter(c => c.name.trim()),
    };
    if (level !== 'grupo') payload.parent_id = form.parent_id;
    if (level === 'concessionaria') {
      payload.brand = form.brand === BRAND_OTHER ? form.brand_other.trim() : form.brand;
    }
    // Os campos do administrador só vão quando preenchidos: o backend trata a
    // ausência deles como "monte só a estrutura".
    if (wantsAdmin) {
      payload.admin_name = form.admin_name;
      payload.admin_email = form.admin_email;
      payload.admin_password = form.admin_password;
    }

    try {
      if (editingId) {
        // Editando: o nível e o pai não mudam, e o administrador tem bloco
        // próprio (o de cima só cria o login junto com a empresa).
        delete payload.type;
        delete payload.parent_id;
        delete payload.admin_name;
        delete payload.admin_email;
        delete payload.admin_password;

        const res = await organizationsApi.update(editingId, payload);
        setFeedback({
          type: 'success',
          text: `${ORG_TYPE_LABELS[res.data.type]} "${res.data.name}" atualizada.`,
        });
      } else {
        const res = await organizationsApi.create(payload);
        const { organization, admin } = res.data;
        const label = ORG_TYPE_LABELS[organization.type];
        setFeedback({
          type: 'success',
          text: admin
            ? `${label} "${organization.name}" cadastrada. O administrador já pode entrar com ${admin.email}.`
            : `${label} "${organization.name}" cadastrada, sem login próprio — quem administra o nível de cima já a enxerga. Para dar um login a ela, edite a empresa na lista abaixo.`,
        });
      }
      if (selfMode && editingId) {
        const fresh = await organizationsApi.list();
        setOrgs(fresh.data);
        const org = fresh.data.find(o => o.id === editingId);
        if (org) openEdit(org, { keepFeedback: true });
      } else {
        closeForm();
        load();
      }
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.error || 'Erro ao salvar' });
    } finally {
      setSaving(false);
    }
  };

  const closeForm = () => {
    setForm({ ...EMPTY_ORG_FORM, type: level });
    setEditingId(null);
    setCnpjNote(null);
    setShowForm(false);
  };

  const openCreate = () => {
    setForm({ ...EMPTY_ORG_FORM, type: level });
    setEditingId(null);
    setCnpjNote(null);
    setFeedback(null);
    setShowForm(true);
  };

  /**
   * Abre a empresa no MESMO formulário do cadastro, preenchido.
   *
   * Tudo o que se cadastra se edita: era aqui que faltava a ficha (CNPJ, endereço)
   * e os contatos, porque a edição tinha um formulário próprio, menor, e ninguém
   * notava a diferença até precisar corrigir um endereço.
   */
  const openEdit = (org, { keepFeedback = false } = {}) => {
    const known = brands.includes(org.brand);
    // A loja guarda a concessionária; o grupo dela é o avô, e é ele que preenche o
    // primeiro select da cascata.
    const parent = orgs.find(o => o.id === org.parent_id);

    setForm({
      ...EMPTY_ORG_FORM,
      type: org.type,
      parent_id: org.parent_id ? String(org.parent_id) : '',
      group_id: parent?.parent_id ? String(parent.parent_id) : '',
      name: org.name || '',
      notes: org.notes || '',
      // Marca fora da lista cai em "Outra..." com o valor no campo de texto, em
      // vez de sumir do select e ser apagada no primeiro salvamento.
      brand: org.brand ? (known ? org.brand : BRAND_OTHER) : '',
      brand_other: org.brand && !known ? org.brand : '',
      cnpj: org.cnpj || '',
      razao_social: org.razao_social || '',
      cep: org.cep || '',
      logradouro: org.logradouro || '',
      numero: org.numero || '',
      complemento: org.complemento || '',
      bairro: org.bairro || '',
      cidade: org.cidade || '',
      uf: org.uf || '',
      codigo_ibge: org.codigo_ibge || '',
      phone: org.phone || '',
      email: org.email || '',
      inscricao_estadual: org.inscricao_estadual || '',
      inscricao_municipal: org.inscricao_municipal || '',
      contacts: (org.contacts || []).map(c => ({
        key: `id-${c.id}`,
        name: c.name || '',
        job_title: c.job_title || '',
        email: c.email || '',
        phone: c.phone || '',
      })),
    });
    setEditingId(org.id);
    setCnpjNote(null);
    if (!keepFeedback) setFeedback(null);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleActive = async (org) => {
    try {
      await organizationsApi.update(org.id, { active: !org.active });
      const label = ORG_TYPE_LABELS[org.type];
      setFeedback({
        type: 'success',
        text: org.active
          ? `${label} "${org.name}" suspensa — os usuários dela e de tudo abaixo perdem o acesso agora.`
          : `${label} "${org.name}" reativada.`,
      });
      load();
    } catch (err) {
      setFeedback({ type: 'error', text: 'Erro ao alterar a situação' });
    }
  };

  const typeMeta = levelMeta;

  /**
   * Os pais oferecidos. No cadastro de loja há DOIS selects em cascata — grupo e
   * concessionária —, como a hierarquia pede: escolher o grupo primeiro reduz a
   * lista de concessionárias, que numa rede grande é o que torna o campo usável.
   * O grupo é só filtro; o que vai para o backend é a concessionária.
   */
  const dealersOfGroup = form.group_id
    ? dealers.filter(d => String(d.parent_id) === String(form.group_id))
    : dealers;
  const parentOptions = level === 'concessionaria' ? groups : dealersOfGroup;

  // Havendo um único pai possível — o caso comum de quem administra a própria rede
  // — já vem escolhido. Obrigar a selecionar a única opção é atrito sem ganho.
  useEffect(() => {
    if (level !== 'grupo' && parentOptions.length === 1 && !form.parent_id) {
      const only = String(parentOptions[0].id);
      setForm(f => (f.parent_id ? f : { ...f, parent_id: only }));
    }
  }, [parentOptions, form.parent_id, level]);

  // Mesma ideia para o grupo do cadastro de loja: com um grupo só, ele já vem.
  useEffect(() => {
    if (level === 'loja' && groups.length === 1 && !form.group_id) {
      const only = String(groups[0].id);
      setForm(f => (f.group_id ? f : { ...f, group_id: only }));
    }
  }, [groups, form.group_id, level]);

  // O login é obrigatório só no grupo — é a raiz, sem ela ninguém entra. Nos
  // níveis de baixo, quem administra acima já alcança: exigir um e-mail inédito
  // ali impedia montar a estrutura de uma rede que já existe.
  const adminRequired = level === 'grupo';
  // Começou a preencher um dos três campos? Então os outros passam a ser
  // obrigatórios — meio preenchido é engano, não intenção.
  const wantsAdmin = Boolean(form.admin_name || form.admin_email || form.admin_password);

  // Minha Loja: assim que a rede chega, a loja ativa abre no formulário. Só
  // quando não há edição aberta, para não descartar o que está sendo digitado
  // a cada recarga da lista.
  const selfOrg = selfMode && storeId ? orgs.find(o => o.id === Number(storeId)) : null;
  useEffect(() => {
    if (!selfMode || editingId || !selfOrg) return;
    openEdit(selfOrg, { keepFeedback: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selfMode, selfOrg, editingId]);

  if (selfMode && (!storeId || (!loading && !selfOrg))) {
    return (
      <div className="page">
        <div className="page-header">
          <div>
            <h1>Cadastro da loja</h1>
            <p>Ficha da loja: nome, CNPJ, inscrições, endereço, telefone, e-mail e contatos.</p>
          </div>
        </div>
        <div className="form-card">
          <div className="field-hint">
            {!storeId
              ? <>Escolha a <strong>loja ativa</strong> no menu à esquerda para abrir a ficha dela.</>
              : 'Esta loja não está na sua rede.'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>
            {selfMode
              ? 'Cadastro da loja'
              : <>Cadastro de {levelMeta.plural} <span className="badge">{rows.length}</span></>}
          </h1>
          <p>
            {level === 'grupo' && 'O topo da rede. Cada grupo reúne concessionárias, que reúnem lojas.'}
            {level === 'concessionaria' && 'Pertence a um grupo e representa uma marca.'}
            {selfMode
              ? `Ficha d${selfOrg?.name ? `a ${selfOrg.name}` : 'a loja'}: o que sai no cabeçalho da proposta em PDF e na nota fiscal.`
              : <>
                  {level === 'loja' && 'O ponto de venda, onde as visitas acontecem.'}
                  {!isSuperadmin && ` Rede d${withArticlePt(user?.organization_type)} ${user?.organization_name || ''}.`}
                </>}
          </p>
        </div>
        {!selfMode && (
          <button className="btn btn-primary" onClick={() => (showForm ? closeForm() : openCreate())}>
            <Icon name="plus" size={16} />
            {level === 'grupo' ? ' Novo grupo' : ` Nova ${levelMeta.label.toLowerCase()}`}
          </button>
        )}
      </div>

      {feedback && (
        <div className={`alert alert-${feedback.type}`}>
          <Icon name={feedback.type === 'success' ? 'check' : 'x'} size={18} />
          <div>{feedback.text}</div>
        </div>
      )}

      {showForm && (
        <div className="form-card">
          <h3>
            {selfMode
              ? form.name
              : editingId
                ? `Editando ${withArticlePt(level)} ${form.name}`
                : `Nova ${levelMeta.label.toLowerCase()}`}
          </h3>
          <form onSubmit={handleSubmit}>
            <div className="form-section">
              <h3><Icon name="car" size={16} /> Dados da empresa</h3>
              <div className="form-grid">
                <div className="field field--full">
                  <div className="field-hint">
                    {level === 'grupo' && 'O grupo é o topo da rede: ele não recebe visitas, mas soma o movimento de todas as concessionárias.'}
                    {level === 'concessionaria' && 'A concessionária pertence a um grupo e representa uma marca. As visitas ficam nas lojas dela.'}
                    {level === 'loja' && 'A loja é o ponto de venda: é onde entram as visitas, os vendedores, o estoque e o WhatsApp.'}
                  </div>
                </div>

                {/* No cadastro de loja, o grupo vem antes e filtra as
                    concessionárias — a hierarquia na ordem em que se pensa nela. */}
                {editingId && editingOrg?.parent_name && (
                  <div className="field field--full">
                    <div className="field-hint">
                      {level === 'concessionaria' ? 'Grupo' : 'Concessionária'}: <strong>{editingOrg.parent_name}</strong>
                      {' '}— o vínculo não muda na edição (mover a empresa trocaria o dono do histórico dela).
                    </div>
                  </div>
                )}

                {/* Logo: só na edição (precisa do id, como as fotos do veículo).
                    Sobe na hora, fora do Salvar — é um arquivo, não um campo. */}
                {editingId && (
                  <div className="field field--full">
                    <label>Logo da empresa</label>
                    <div className="org-logo-row">
                      <div className="org-logo-preview">
                        {logoUrl ? <img src={logoUrl} alt="Logo" /> : <span className="field-hint">Sem logo</span>}
                      </div>
                      <div className="org-logo-actions">
                        <label className="btn btn-secondary btn-sm">
                          <input
                            type="file"
                            accept=".png,.jpg,.jpeg"
                            hidden
                            disabled={logoBusy}
                            onChange={e => { const file = e.target.files?.[0]; e.target.value = ''; uploadLogo(file); }}
                          />
                          <Icon name="paperclip" size={14} /> {logoBusy ? 'Enviando...' : logoUrl ? 'Trocar logo' : 'Enviar logo (PNG ou JPG)'}
                        </label>
                        {logoUrl && (
                          <button type="button" className="btn btn-secondary btn-sm" onClick={removeLogo} disabled={logoBusy}>Remover</button>
                        )}
                      </div>
                    </div>
                    <div className="field-hint">
                      Aparece no cabeçalho do PDF da proposta. PNG com fundo transparente fica melhor; até 2MB.
                    </div>
                  </div>
                )}

                {!editingId && level === 'loja' && (
                  <div className="field field--full">
                    <label>Grupo</label>
                    <div className="field-with-action">
                      <select
                        value={form.group_id}
                        onChange={e => setForm(f => ({ ...f, group_id: e.target.value, parent_id: '' }))}
                      >
                        <option value="">Todos os grupos</option>
                        {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                      </select>
                      {/* Grupo novo é cliente novo da plataforma — o atalho só
                          existe para quem pode criá-lo (o superadmin). */}
                      {allowedTypes.includes('grupo') && (
                        <button type="button" className="btn btn-secondary" onClick={() => openInlineParent('grupo')}>
                          + novo
                        </button>
                      )}
                    </div>
                    <div className="field-hint">Filtra as concessionárias abaixo. A loja fica atrelada à concessionária.</div>
                  </div>
                )}

                {!editingId && level !== 'grupo' && (
                  <div className="field field--full">
                    <label>{level === 'concessionaria' ? 'Grupo *' : 'Concessionária *'}</label>
                    {/* O "+ novo" ao lado: cadastrar a loja é quando se descobre que
                        o pai dela não existe, e sair da tela perderia o digitado. */}
                    <div className="field-with-action">
                      <select
                        value={form.parent_id}
                        onChange={e => setForm(f => ({ ...f, parent_id: e.target.value }))}
                        required
                      >
                        <option value="">
                          {level === 'concessionaria' ? 'Buscar grupo...' : 'Buscar concessionária...'}
                        </option>
                        {parentOptions.map(p => (
                          <option key={p.id} value={p.id}>
                            {p.type === 'concessionaria' && p.parent_name
                              ? `${p.parent_name} — ${p.name}${p.brand ? ` (${p.brand})` : ''}`
                              : p.name}
                          </option>
                        ))}
                      </select>
                      {/* Um admin de grupo não cria outro grupo, então o atalho só
                          aparece quando o nível do pai também é permitido a ele. */}
                      {allowedTypes.includes(level === 'concessionaria' ? 'grupo' : 'concessionaria') && (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => openInlineParent(level === 'concessionaria' ? 'grupo' : 'concessionaria')}
                        >
                          + {level === 'concessionaria' ? 'novo' : 'nova'}
                        </button>
                      )}
                    </div>
                    {parentOptions.length === 0 && (
                      <div className="field-hint">
                        {level === 'concessionaria'
                          ? 'Nenhum grupo cadastrado ainda — use "+ novo" para criar agora.'
                          : 'Nenhuma concessionária cadastrada ainda — use "+ nova" para criar agora.'}
                      </div>
                    )}
                  </div>
                )}

                {/* Criador do pai, embutido. Fecha assim que cria e já deixa o pai
                    selecionado no campo acima. */}
                {inlineParent && (
                  <div className="field field--full inline-creator">
                    <label>{inlineParent.type === 'grupo' ? 'Novo grupo' : 'Nova concessionária'}</label>
                    <div className="form-grid">
                      {inlineParent.type === 'concessionaria' && (
                        <div className="field">
                          <label>Grupo *</label>
                          <select
                            value={inlineParent.parent_id}
                            onChange={e => setInlineParent(p => ({ ...p, parent_id: e.target.value }))}
                          >
                            <option value="">Selecione...</option>
                            {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                          </select>
                        </div>
                      )}
                      <div className="field">
                        <label>Nome *</label>
                        <input
                          value={inlineParent.name}
                          onChange={e => setInlineParent(p => ({ ...p, name: e.target.value }))}
                        />
                      </div>
                      {inlineParent.type === 'concessionaria' && (
                        <div className="field">
                          <label>Marca *</label>
                          <select
                            value={inlineParent.brand}
                            onChange={e => setInlineParent(p => ({ ...p, brand: e.target.value }))}
                          >
                            <option value="">Selecione...</option>
                            {brands.map(b => <option key={b} value={b}>{b}</option>)}
                          </select>
                        </div>
                      )}
                    </div>
                    <div className="form-actions">
                      <button type="button" className="btn btn-secondary" onClick={() => setInlineParent(null)}>
                        Cancelar
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={
                          saving
                          || !inlineParent.name.trim()
                          || (inlineParent.type === 'concessionaria' && (!inlineParent.parent_id || !inlineParent.brand))
                        }
                        onClick={saveInlineParent}
                      >
                        {saving ? 'Criando...' : 'Criar e selecionar'}
                      </button>
                    </div>
                  </div>
                )}

                <div className={level === 'concessionaria' ? 'field' : 'field field--full'}>
                  <label>
                    Nome d{withArticlePt(level)} *
                    {level === 'loja' && <span className="label-note"> (fantasia)</span>}
                  </label>
                  <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
                </div>

                {level === 'concessionaria' && (
                  <div className="field">
                    <label>Marca *</label>
                    <select
                      value={form.brand}
                      onChange={e => setForm(f => ({ ...f, brand: e.target.value }))}
                      required
                    >
                      <option value="">Selecione...</option>
                      {brands.map(brand => (
                        <option key={brand} value={brand}>{brand}</option>
                      ))}
                      <option value={BRAND_OTHER}>Outra...</option>
                    </select>
                    {form.brand === BRAND_OTHER && (
                      <input
                        style={{ marginTop: 8 }}
                        placeholder="Nome da marca"
                        value={form.brand_other}
                        onChange={e => setForm(f => ({ ...f, brand_other: e.target.value }))}
                        required
                      />
                    )}
                  </div>
                )}

                <div className="field field--full">
                  <label>CNPJ <span className="label-note">{fullProfile ? '(preenche os demais)' : '(consulta a Receita)'}</span></label>
                  <div className="field-with-action">
                    <input
                      inputMode="numeric"
                      placeholder="00.000.000/0000-00"
                      value={maskCnpj(form.cnpj)}
                      onChange={e => setForm(f => ({ ...f, cnpj: e.target.value.replace(/\D/g, '') }))}
                    />
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={lookingUp || form.cnpj.replace(/\D/g, '').length !== 14}
                      onClick={buscarCnpj}
                    >
                      {lookingUp ? 'Buscando...' : 'Buscar CNPJ'}
                    </button>
                  </div>
                  {cnpjNote && (
                    <div className={`field-hint field-hint--${cnpjNote.type}`}>{cnpjNote.text}</div>
                  )}
                  {!fullProfile && (
                    <div className="field-hint">
                      Razão social, endereço, telefone, inscrições e contatos ficam com o cliente, em Cadastro › Minha Loja.
                      O que a consulta trouxer já fica gravado.
                    </div>
                  )}
                </div>

                {fullProfile && (<>
                <div className="field field--full">
                  <label>
                    Razão social
                    <span className="label-note"> (nome empresarial — tomador da NF)</span>
                  </label>
                  <input
                    placeholder="conforme o CNPJ"
                    value={form.razao_social}
                    onChange={e => setForm(f => ({ ...f, razao_social: e.target.value }))}
                  />
                </div>

                <div className="field">
                  <label>Inscrição estadual</label>
                  <input
                    placeholder="número ou ISENTO"
                    value={form.inscricao_estadual}
                    onChange={e => setForm(f => ({ ...f, inscricao_estadual: e.target.value }))}
                  />
                </div>
                <div className="field">
                  <label>Inscrição municipal</label>
                  <input
                    placeholder="número na prefeitura"
                    value={form.inscricao_municipal}
                    onChange={e => setForm(f => ({ ...f, inscricao_municipal: e.target.value }))}
                  />
                </div>
                <div className="field">
                  <label>Telefone</label>
                  <input
                    inputMode="tel"
                    placeholder="(81) 3222-0000"
                    value={maskOrgPhone(form.phone)}
                    onChange={e => setForm(f => ({ ...f, phone: maskOrgPhone(e.target.value) }))}
                  />
                  <div className="field-hint">Telefone da empresa; sai no PDF da proposta. Pessoas ficam em Contatos.</div>
                </div>
                <div className="field">
                  <label>E-mail</label>
                  <input
                    type="email"
                    placeholder="contato@loja.com.br"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  />
                </div>
                </>)}

                <div className="field field--full">
                  <label>Observações</label>
                  <textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="Contrato, plano, contato comercial..." />
                </div>
              </div>
            </div>

            {fullProfile && (<>
            {/* ─── Endereço ───────────────────────────────────────────────────── */}
            <div className="form-section">
              <h3>
                <Icon name="car" size={16} /> Endereço
                <span className="label-note"> (tomador na NFS-e)</span>
              </h3>
              <div className="form-grid">
                <div className="field field--full">
                  <label>Logradouro</label>
                  <input value={form.logradouro}
                    onChange={e => setForm(f => ({ ...f, logradouro: e.target.value }))} />
                </div>
                <div className="field">
                  <label>Número</label>
                  <input value={form.numero}
                    onChange={e => setForm(f => ({ ...f, numero: e.target.value }))} />
                </div>
                <div className="field">
                  <label>Complemento</label>
                  <input value={form.complemento}
                    onChange={e => setForm(f => ({ ...f, complemento: e.target.value }))} />
                </div>
                <div className="field">
                  <label>Bairro</label>
                  <input value={form.bairro}
                    onChange={e => setForm(f => ({ ...f, bairro: e.target.value }))} />
                </div>
                <div className="field">
                  <label>Município</label>
                  <input value={form.cidade}
                    onChange={e => setForm(f => ({ ...f, cidade: e.target.value }))} />
                </div>
                <div className="field">
                  <label>UF</label>
                  <select value={form.uf} onChange={e => setForm(f => ({ ...f, uf: e.target.value }))}>
                    <option value="">—</option>
                    {UFS.map(uf => <option key={uf} value={uf}>{uf}</option>)}
                  </select>
                </div>
                <div className="field">
                  <label>CEP</label>
                  <input
                    inputMode="numeric"
                    placeholder="00000-000"
                    value={maskCep(form.cep)}
                    onChange={e => setForm(f => ({ ...f, cep: e.target.value.replace(/\D/g, '') }))}
                  />
                </div>
                <div className="field">
                  <label>Código IBGE do município</label>
                  <input
                    inputMode="numeric"
                    placeholder="preenchido pelo CNPJ"
                    value={form.codigo_ibge}
                    onChange={e => setForm(f => ({ ...f, codigo_ibge: e.target.value.replace(/\D/g, '') }))}
                  />
                  <div className="field-hint">Vem da consulta de CNPJ. É o que a NFS-e usa para identificar o município.</div>
                </div>
              </div>
            </div>

            {/* ─── Contatos ───────────────────────────────────────────────────── */}
            <div className="form-section">
              <h3><Icon name="users" size={16} /> Contatos</h3>
              <div className="field-hint" style={{ marginBottom: 12 }}>
                Agenda de quem falar nesta empresa. Não é login nem vendedor: contato
                não entra no sistema e não recebe aviso de cliente esperando.
              </div>

              {form.contacts.map((contact, index) => (
                <div key={contact.key || contact.id} className="contact-row">
                  <div className="form-grid">
                    <div className="field">
                      <label>Nome</label>
                      <input value={contact.name}
                        onChange={e => changeContact(index, 'name', e.target.value)} />
                    </div>
                    <div className="field">
                      <label>Função</label>
                      <input placeholder="Gerente, financeiro..." value={contact.job_title}
                        onChange={e => changeContact(index, 'job_title', e.target.value)} />
                    </div>
                    <div className="field">
                      <label>E-mail</label>
                      <input type="email" value={contact.email}
                        onChange={e => changeContact(index, 'email', e.target.value)} />
                    </div>
                    <div className="field">
                      <label>Telefone</label>
                      <input value={contact.phone}
                        onChange={e => changeContact(index, 'phone', e.target.value)} />
                    </div>
                  </div>
                  <button type="button" className="btn btn-secondary btn-sm"
                    onClick={() => removeContact(index)}>
                    Remover
                  </button>
                </div>
              ))}

              <button type="button" className="btn btn-secondary" onClick={addContact}>
                <Icon name="plus" size={14} /> Adicionar novo contato
              </button>
            </div>
            </>)}

            {/* ─── Login admin ─────────────────────────────────────────────────
                No cadastro, os três campos criam o login junto com a empresa. Na
                edição eles dão lugar aos logins que já existem, cada um com o seu
                Editar — o acesso de uma pessoa é ação própria, com efeito próprio,
                e não algo que se altera de raspão no "Salvar" da empresa. */}
            {!ownLevel && (
            <div className="form-section">
              <h3>
                <Icon name="user" size={16} /> {typeMeta?.admin || 'Administrador da conta'}
                {!editingId && !adminRequired && <span className="cell-tag">opcional</span>}
              </h3>

              {!editingId ? (
                <>
                  <div className="field-hint" style={{ marginBottom: 12 }}>
                    {adminRequired
                      ? 'O grupo é a raiz da rede: sem um login aqui, ninguém consegue entrar. Ele enxerga, somado, o movimento de todas as lojas abaixo.'
                      : `Deixe em branco para montar só a estrutura — o administrador do ${level === 'loja' ? 'grupo e da concessionária' : 'grupo'} já enxerga ${withArticlePt(level)}. Um login próprio pode ser criado depois, editando a empresa.`}
                  </div>
                  <div className="form-grid">
                    <div className="field">
                      <label>Nome {adminRequired ? '*' : ''}</label>
                      <input value={form.admin_name} onChange={e => setForm(f => ({ ...f, admin_name: e.target.value }))} required={adminRequired || wantsAdmin} />
                    </div>
                    <div className="field">
                      <label>E-mail {adminRequired ? '*' : ''}</label>
                      <input type="email" autoComplete="off" value={form.admin_email} onChange={e => setForm(f => ({ ...f, admin_email: e.target.value }))} required={adminRequired || wantsAdmin} />
                    </div>
                    <div className="field">
                      <label>Senha provisória {adminRequired ? '*' : ''}</label>
                      <input type="password" autoComplete="new-password" value={form.admin_password} minLength={8}
                        onChange={e => setForm(f => ({ ...f, admin_password: e.target.value }))} required={adminRequired || wantsAdmin} />
                      <div className="field-hint">Mínimo 8 caracteres. O cliente troca em Meu Perfil.</div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {(editingOrg?.admins || []).length === 0 && (
                    <div className="field-hint" style={{ marginBottom: 12 }}>
                      Esta empresa não tem login próprio — quem administra o nível de
                      cima já a enxerga. Preencha abaixo para dar um a ela.
                    </div>
                  )}

                  {(editingOrg?.admins || []).map(admin => (
                    <div key={admin.id} className="admin-row">
                      <div>
                        <div className="cell-name">
                          {admin.name}
                          {!admin.active && <span className="status-badge status-badge--red ml-8">Sem acesso</span>}
                        </div>
                        <div className="cell-sub">{admin.email}</div>
                      </div>
                      <div className="cell-actions">
                        <button type="button" className="btn btn-secondary btn-sm"
                          onClick={() => (adminDraft.id === admin.id ? closeAdminDraft() : startAdminEdit(admin))}>
                          <Icon name="edit" size={14} /> {adminDraft.id === admin.id ? 'Fechar' : 'Editar'}
                        </button>
                        <button type="button" className="btn btn-secondary btn-sm"
                          onClick={() => toggleAdminActive(admin)}>
                          {admin.active ? 'Desativar' : 'Reativar'}
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* Um bloco só, que serve para editar o login aberto ou criar
                      outro — os campos são os mesmos. */}
                  {(adminDraft.id || addingAdmin || (editingOrg?.admins || []).length === 0) && (
                    <div className="inline-creator" style={{ marginTop: 12 }}>
                      <label>{adminDraft.id ? 'Editando o login' : 'Novo login'}</label>
                      <div className="form-grid">
                        <div className="field">
                          <label>Nome</label>
                          <input value={adminDraft.name}
                            onChange={e => setAdminDraft(d => ({ ...d, name: e.target.value }))} />
                        </div>
                        <div className="field">
                          <label>E-mail</label>
                          <input type="email" autoComplete="off" value={adminDraft.email}
                            onChange={e => setAdminDraft(d => ({ ...d, email: e.target.value }))} />
                        </div>
                        <div className="field">
                          <label>{adminDraft.id ? 'Nova senha' : 'Senha'}</label>
                          <input type="password" autoComplete="new-password" value={adminDraft.password} minLength={8}
                            onChange={e => setAdminDraft(d => ({ ...d, password: e.target.value }))} />
                          <div className="field-hint">
                            {adminDraft.id
                              ? 'Deixe em branco para manter a senha atual.'
                              : 'Mínimo 8 caracteres. O cliente troca em Meu Perfil.'}
                          </div>
                        </div>
                      </div>
                      <div className="form-actions">
                        {adminDraft.id && (
                          <button type="button" className="btn btn-secondary" onClick={closeAdminDraft}>
                            Cancelar
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-primary"
                          disabled={
                            saving
                            || !adminDraft.name.trim()
                            || !adminDraft.email.trim()
                            || (!adminDraft.id && adminDraft.password.length < 8)
                          }
                          onClick={saveAdmin}
                        >
                          {saving ? 'Salvando...' : (adminDraft.id ? 'Salvar login' : 'Criar login')}
                        </button>
                      </div>
                    </div>
                  )}

                  {(editingOrg?.admins || []).length > 0 && !adminDraft.id && !addingAdmin && (
                    <button type="button" className="btn btn-secondary" style={{ marginTop: 12 }}
                      onClick={() => setAddingAdmin(true)}>
                      <Icon name="plus" size={14} /> Adicionar outro login
                    </button>
                  )}
                </>
              )}
            </div>
            )}

            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={closeForm}>{selfMode ? 'Desfazer' : 'Cancelar'}</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Salvando...' : (editingId ? 'Salvar alterações' : 'Cadastrar')}
              </button>
            </div>
          </form>
        </div>
      )}

      {selfMode ? null : loading ? <SkeletonList rows={5} /> : (
        <div className="table-container">
          <table className="table">
            <thead>
              {/* As colunas seguem o nível da tela: marca só existe em
                  concessionária, e WhatsApp só em loja. */}
              <tr>
                <th>{levelMeta.label}</th>
                {level === 'concessionaria' && <th>Marca</th>}
                <th>{level === 'loja' ? 'WhatsApp' : 'Lojas'}</th>
                <th>Usuários</th>
                <th>Visitas</th>
                <th>Cliente desde</th>
                <th>Situação</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  {/* A coluna Marca só existe em concessionária, então o colSpan
                      acompanha — fixo em 8, a linha vazia inventaria uma coluna. */}
                  <td colSpan={level === 'concessionaria' ? 8 : 7} className="empty-row">
                    <EmptyState
                      icon="dashboard"
                      title={`Nenhum${level === 'grupo' ? '' : 'a'} ${levelMeta.label.toLowerCase()} cadastrad${level === 'grupo' ? 'o' : 'a'}`}
                      actionLabel={level === 'grupo' ? 'Novo grupo' : `Nova ${levelMeta.label.toLowerCase()}`}
                      onAction={openCreate}
                    />
                  </td>
                </tr>
              ) : rows.map(org => {
                const isStore = level === 'loja';
                const isDealer = org.type === 'concessionaria';
                // Uma concessionária sem loja não recebe visita nenhuma: o aviso
                // aparece no lugar do status de WhatsApp, que ali não existe.
                const noStores = isDealer && org.total_stores === 0;
                // Concessionária vinda da migração para hierarquia: não havia de
                // onde tirar a marca, então ela fica pendente até alguém informar.
                const brandMissing = isDealer && !org.brand;
                return (
                  <React.Fragment key={org.id}>
                  <tr className={org.active ? '' : 'row-inactive'}>
                    <td data-label={levelMeta.label}>
                      <div className="cell-name">{org.name}</div>
                      {/* O caminho até a empresa substitui a indentação da árvore:
                          numa lista de um nível só, saber de quem ela é é o que
                          importa. */}
                      <div className="cell-sub">
                        {org.path && <span className="tree-branch">{org.path}</span>}
                        {org.cnpj ? maskCnpj(org.cnpj) : (org.notes || org.slug)}
                      </div>
                    </td>
                    {level === 'concessionaria' && (
                      <td data-label="Marca">
                        {org.brand || (brandMissing
                          ? <span className="status-badge status-badge--amber">Informe a marca</span>
                          : '—')}
                      </td>
                    )}
                    <td data-label={level === 'loja' ? 'WhatsApp' : 'Lojas'}>
                      {isStore ? (
                        <span className={`status-badge status-badge--${org.whatsapp_configured ? 'green' : 'gray'}`}>
                          {org.whatsapp_configured ? 'Configurado' : 'Pendente'}
                        </span>
                      ) : noStores ? (
                        <span className="cell-sub">nenhuma loja cadastrada</span>
                      ) : (
                        <span className="cell-sub">{org.total_stores} loja{org.total_stores === 1 ? '' : 's'}</span>
                      )}
                    </td>
                    <td data-label="Usuários">
                      {org.total_users}
                      {/* own_users = 0 significa que este nível não tem login
                          próprio: quem administra acima é que cuida dele. Não é
                          defeito, mas precisa estar visível. */}
                      {org.own_users === 0 && (
                        <div className="cell-sub">sem login próprio</div>
                      )}
                    </td>
                    <td data-label="Visitas">{org.total_visits}</td>
                    <td data-label="Cliente desde">{new Date(org.created_at).toLocaleDateString('pt-BR')}</td>
                    <td data-label="Situação">
                      {/* effective_active false com active true = suspensa por um
                          nível acima. Sem distinguir, o botão "Reativar" prometeria
                          algo que só a reativação do pai resolve. */}
                      <span className={`status-badge status-badge--${org.effective_active ? 'green' : 'red'}`}>
                        {org.active
                          ? (org.effective_active ? 'Ativa' : 'Suspensa acima')
                          : 'Suspensa'}
                      </span>
                    </td>
                    <td className="cell-actions">
                      {/* O flex fica neste div, e não no td: numa célula ele
                          quebraria o layout da tabela. */}
                      <div className="cell-actions-inner">
                        {/* Abre o MESMO formulário do cadastro, preenchido e no topo
                            da tela: é lá que estão a ficha, os contatos e o login.
                            Nível e vínculo não são editáveis — mover uma loja de
                            concessionária trocaria o dono do histórico dela. */}
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => (editingId === org.id ? closeForm() : openEdit(org))}
                        >
                          <Icon name="edit" size={14} /> {editingId === org.id ? 'Fechar' : 'Editar'}
                        </button>
                        {/* Suspender é o botão de inadimplência da plataforma: só o
                            superadmin. Um admin poderia reativar a própria conta. */}
                        {isSuperadmin && (
                          <button className="btn btn-secondary btn-sm" onClick={() => toggleActive(org)}>
                            {org.active ? 'Suspender' : 'Reativar'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── MEU PERFIL ───────────────────────────────────────────────────────────────
function ProfilePage({ user, onUserUpdate }) {
  const [name, setName] = useState(user?.name || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // O superadmin não tem organização no banco (users_org_role_check exige NULL),
  // mas o campo em branco parecia cadastro incompleto: quem entra por aqui está
  // operando a plataforma, então o dono aparece no lugar.
  const organizationLabel =
    user?.role === 'superadmin'
      ? PLATFORM_OWNER
      : [user?.organization_name, ORG_TYPE_LABELS[user?.organization_type]]
          .filter(Boolean)
          .join(' — ');

  const handleSaveName = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFeedback(null);
    try {
      const res = await authApi.updateMe({ name });
      // Sem isto o nome antigo continuaria na sidebar até o próximo login,
      // porque o App lê o usuário do localStorage.
      onUserUpdate(res.data.user);
      setFeedback({ type: 'success', text: 'Nome atualizado!' });
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.error || 'Erro ao salvar' });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setFeedback({ type: 'error', text: 'A confirmação não confere com a nova senha' });
      return;
    }
    setSaving(true);
    setFeedback(null);
    try {
      await authApi.updateMe({ current_password: currentPassword, new_password: newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setFeedback({ type: 'success', text: 'Senha alterada!' });
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.error || 'Erro ao trocar a senha' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1>Meu Perfil</h1>
        <p>Seus dados de acesso ao sistema</p>
      </div>

      {feedback && (
        <div className={`alert alert-${feedback.type}`}>
          <Icon name={feedback.type === 'success' ? 'check' : 'x'} size={18} />
          <div>{feedback.text}</div>
        </div>
      )}

      <div className="settings-grid">
        <div className="form-card">
          <h3><Icon name="user" size={16} /> Dados pessoais</h3>
          <form onSubmit={handleSaveName}>
            <div className="field">
              <label>Nome de exibição</label>
              <input value={name} onChange={e => setName(e.target.value)} required />
              <div className="field-hint">É o nome que aparece na barra lateral do sistema.</div>
            </div>
            <div className="field">
              <label>E-mail</label>
              <input value={user?.email || ''} disabled />
              <div className="field-hint">O e-mail de login não pode ser alterado por aqui.</div>
            </div>
            <div className="field">
              <label>Perfil de acesso</label>
              <input value={ROLE_LABELS[user?.role] || user?.role || ''} disabled />
            </div>
            <div className="field">
              <label>Empresa</label>
              <input value={organizationLabel} disabled />
              <div className="field-hint">
                {user?.role === 'superadmin'
                  ? 'Dona da plataforma — administra todos os clientes.'
                  : user?.organization_type === 'loja'
                  ? 'Loja à qual sua conta pertence.'
                  : `${ORG_TYPE_LABELS[user?.organization_type] || 'Empresa'} à qual sua conta pertence — você enxerga, somado, o movimento de todas as lojas abaixo.`}
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={saving || !name.trim()}>
                {saving ? 'Salvando...' : 'Salvar nome'}
              </button>
            </div>
          </form>
        </div>

        <div className="form-card">
          <h3><Icon name="settings" size={16} /> Trocar senha</h3>
          <form onSubmit={handleChangePassword}>
            <div className="field">
              <label>Senha atual *</label>
              <input type="password" autoComplete="current-password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} required />
            </div>
            <div className="field">
              <label>Nova senha *</label>
              <input type="password" autoComplete="new-password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required minLength={8} />
              <div className="field-hint">Mínimo de 8 caracteres.</div>
            </div>
            <div className="field">
              <label>Confirme a nova senha *</label>
              <input type="password" autoComplete="new-password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required minLength={8} />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={saving || !currentPassword || !newPassword}>
                {saving ? 'Salvando...' : 'Trocar senha'}
              </button>
            </div>
          </form>
        </div>

        {user?.role !== 'superadmin' && <PushSettingsCard onFeedback={setFeedback} />}
      </div>
    </div>
  );
}

/**
 * "Notificações neste aparelho": liga e desliga o Web Push do navegador em
 * uso. É por aparelho — o vendedor ativa no celular e, se quiser, no
 * computador. O superadmin não tem conversa, então não vê o bloco.
 */
function PushSettingsCard({ onFeedback }) {
  const [status, setStatus] = useState('loading');
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try { setStatus(await pushStatus()); } catch { setStatus('unsupported'); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const toggle = async () => {
    setBusy(true);
    onFeedback(null);
    try {
      if (status === 'on') {
        await unsubscribeFromPush();
        onFeedback({ type: 'success', text: 'Notificações desativadas neste aparelho.' });
      } else {
        const result = await subscribeToPush();
        if (result.ok) {
          onFeedback({ type: 'success', text: 'Notificações ativadas neste aparelho. Enviamos uma de teste.' });
          pushApi.test().catch(() => {});
        } else if (result.reason === 'denied') {
          onFeedback({ type: 'error', text: 'O navegador está bloqueando notificações deste site. Libere nas configurações do site e tente de novo.' });
        } else if (result.reason === 'ios-install') {
          onFeedback({ type: 'error', text: 'No iPhone, adicione o painel à tela de início (Compartilhar › Adicionar à Tela de Início) e ative por lá.' });
        } else if (result.reason === 'disabled') {
          onFeedback({ type: 'error', text: 'As notificações push ainda não estão configuradas na plataforma.' });
        } else if (result.reason !== 'default') {
          onFeedback({ type: 'error', text: 'Não foi possível ativar as notificações neste navegador.' });
        }
      }
    } catch (err) {
      onFeedback({ type: 'error', text: apiErrorMessage(err, 'Não foi possível alterar as notificações') });
    } finally {
      setBusy(false);
      refresh();
    }
  };

  const HINTS = {
    loading: 'Verificando este aparelho...',
    on: 'Você recebe aviso de mensagem nova, cliente passado a você, dúvida do assistente e test-drive — mesmo com o painel fechado.',
    off: 'Ative para receber aviso no celular ou no computador mesmo com o painel fechado. Cada aparelho é ativado separadamente.',
    denied: 'O navegador está bloqueando notificações deste site. Libere nas configurações do site (cadeado na barra de endereço) e tente de novo.',
    'ios-install': 'No iPhone e no iPad, o push só funciona com o painel instalado: toque em Compartilhar › Adicionar à Tela de Início, abra por lá e ative aqui.',
    unsupported: 'Este navegador não suporta notificações push. Você continua recebendo o aviso com o painel aberto.',
    disabled: 'As notificações push ainda não estão configuradas na plataforma.',
  };
  const canToggle = status === 'on' || status === 'off';

  return (
    <div className="form-card">
      <h3><Icon name="bell" size={16} /> Notificações neste aparelho</h3>
      <div className="field">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <label className="switch" title={status === 'on' ? 'Desativar neste aparelho' : 'Ativar neste aparelho'}>
            <input type="checkbox" checked={status === 'on'} disabled={!canToggle || busy} onChange={toggle} />
            <span className="switch-slider" />
          </label>
          <span>{status === 'on' ? 'Ativadas' : status === 'off' ? 'Desativadas' : 'Indisponíveis'}</span>
        </div>
        <div className={`field-hint${status === 'denied' || status === 'ios-install' ? ' field-hint--warn' : ''}`}>
          {HINTS[status] || HINTS.unsupported}
        </div>
      </div>
    </div>
  );
}

// ─── APP PRINCIPAL ────────────────────────────────────────────────────────────
export default function App() {
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem('crm_user');
    return stored ? JSON.parse(stored) : null;
  });
  // Página inicial por papel: cada um cai na tela que abre o dia dele — o
  // gerente (admin) nos números, a recepção no cadastro de visita, o vendedor
  // nas conversas. O superadmin cai nos Clientes, que é o que ele administra —
  // ele não tem dashboard próprio, e abri-lo daria "cliente não informado".
  const HOME_BY_ROLE = {
    superadmin: orgPageId('grupo'),
    admin: 'dashboard',
    receptionist: 'new-visit',
    seller: 'conversations',
  };
  const homePage = (u) => HOME_BY_ROLE[u?.role] || 'dashboard';
  const [page, setPage] = useState(() => homePage(user));
  // Botão "Proposta" do inbox: leva o cliente da conversa para a tela de
  // Propostas com o formulário já preenchido.
  const [proposalDraft, setProposalDraft] = useState(null);
  const clearProposalDraft = useCallback(() => setProposalDraft(null), []);
  const openProposalFor = useCallback((conv) => {
    setProposalDraft({
      client_id: conv.client_id,
      visit_id: conv.visit_id,
      seller_id: conv.assigned_seller_id,
      full_name: conv.full_name,
    });
    setPage('proposals');
  }, []);

  // Botão "Test-drive" do inbox: leva o cliente da conversa para a tela de
  // Test-drives com o formulário já preenchido.
  const [testDriveDraft, setTestDriveDraft] = useState(null);
  const clearTestDriveDraft = useCallback(() => setTestDriveDraft(null), []);
  const openTestDriveFor = useCallback((conv) => {
    setTestDriveDraft({
      client_id: conv.client_id,
      visit_id: conv.visit_id,
      seller_id: conv.assigned_seller_id,
      full_name: conv.full_name,
      phone: conv.phone,
      vehicle_interest: conv.vehicle_interest,
    });
    setPage('test-drives');
  }, []);

  // Lojas que o usuário pode operar, e qual delas está ativa. Um admin de loja
  // tem exatamente uma; um admin de grupo tem a rede inteira e escolhe.
  const [stores, setStores] = useState([]);
  // A subárvore em todos os níveis, para a tela de Usuários: administrador existe
  // em grupo, concessionária e loja, e `stores` só traz lojas.
  const [scopeOrgs, setScopeOrgs] = useState([]);
  const [activeStore, setActiveStore] = useState(() => getActiveStoreId());

  const handleLogin = (userData) => {
    setUser(userData);
    setPage(homePage(userData));
  };

  // Destino vindo de fora do painel: o toque numa notificação push abre
  // "/?conversation=12" (ou "/?page=test-drives"). Sem roteador, a URL é lida
  // aqui e convertida em estado; depois é limpa, para um F5 não reabrir a
  // mesma conversa. Antes do login a URL fica como está — o efeito abaixo
  // roda de novo quando o usuário entra.
  const [initialConversationId, setInitialConversationId] = useState(null);
  const clearInitialConversation = useCallback(() => setInitialConversationId(null), []);
  const LAUNCH_PAGES = ['conversations', 'test-drives', 'visits', 'dashboard'];
  const applyLaunchUrl = useCallback((href) => {
    let url;
    try { url = new URL(href, window.location.origin); } catch { return; }
    const conv = Number(url.searchParams.get('conversation'));
    const target = url.searchParams.get('page');
    if (conv) {
      setInitialConversationId(conv);
      setPage('conversations');
    } else if (target && LAUNCH_PAGES.includes(target)) {
      setPage(target);
    } else {
      return;
    }
    if (href === window.location.href) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user) return;
    if (window.location.search) applyLaunchUrl(window.location.href);
    // Reata o push deste aparelho a quem acabou de entrar (silencioso).
    resyncPush();
  }, [user, applyLaunchUrl]);

  // Painel já aberto: o service worker foca a janela e manda a URL da
  // notificação por mensagem, em vez de abrir outra aba.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;
    const onMessage = (event) => {
      if (event.data?.type === 'open-url' && event.data.url) applyLaunchUrl(event.data.url);
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [applyLaunchUrl]);

  const changeStore = useCallback((id) => {
    // O interceptor do axios é quem anexa o id a cada requisição, então a troca
    // precisa chegar até ele — e não só ao estado do React.
    setActiveStoreId(id);
    setActiveStore(id);
  }, []);

  // Carrega o escopo depois do login. Quem tem uma loja só nem vê o seletor: a
  // loja é adotada automaticamente para que cadastrar visita continue funcionando
  // sem passo extra.
  useEffect(() => {
    if (!user || user.role === 'superadmin') {
      setStores([]);
      setScopeOrgs([]);
      return;
    }

    let cancelled = false;
    organizationsApi
      .scope()
      .then(res => {
        if (cancelled) return;
        const list = res.data.stores || [];
        setStores(list);
        setScopeOrgs(res.data.organizations || []);

        const stillValid = list.some(s => String(s.id) === String(getActiveStoreId()));
        if (!stillValid) changeStore(list.length === 1 ? list[0].id : null);
      })
      .catch(() => {
        if (!cancelled) { setStores([]); setScopeOrgs([]); }
      });

    return () => { cancelled = true; };
  }, [user, changeStore]);

  // Total de mensagens não lidas, para o aviso FORA do inbox: badge no menu,
  // número no título da aba e notificação do navegador. Polling próprio de 30s
  // (o inbox tem o dele, de 10s, mas só roda com a tela aberta). O superadmin
  // fica de fora: ele não tem dados de conversa.
  const [unreadCount, setUnreadCount] = useState(0);
  const prevUnreadRef = useRef(0);

  useEffect(() => {
    if (!user || user.role === 'superadmin') { setUnreadCount(0); return; }

    let cancelled = false;
    const poll = async () => {
      try {
        const res = await conversationsApi.list();
        if (cancelled) return;
        const total = (res.data.conversations || [])
          .reduce((sum, c) => sum + (c.unread || 0), 0);
        setUnreadCount(total);
      } catch { /* rede fora: o próximo ciclo tenta de novo */ }
    };

    poll();
    const timer = setInterval(poll, 30000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [user, activeStore]);

  useEffect(() => {
    document.title = unreadCount > 0 ? `(${unreadCount}) New Car CRM` : 'New Car CRM';

    // Notifica só quando o total SOBE (mensagem nova de fato) e com a permissão
    // já concedida — o pedido de permissão fica no botão de sino do inbox, para
    // não abrir popup do navegador na cara de quem acabou de logar.
    //
    // Aparelho com push inscrito não recebe esta: o service worker já mostra a
    // do push (com o nome do cliente), e as duas juntas seriam aviso em dobro.
    let pushDevice = false;
    try { pushDevice = localStorage.getItem(PUSH_DEVICE_FLAG) === '1'; } catch { /* sem storage */ }
    if (
      unreadCount > prevUnreadRef.current &&
      !pushDevice &&
      typeof Notification !== 'undefined' &&
      Notification.permission === 'granted'
    ) {
      // tag: a notificação nova substitui a anterior em vez de empilhar.
      new Notification('New Car CRM — mensagem de cliente', {
        body: `Você tem ${unreadCount} mensagem${unreadCount > 1 ? 'ns' : ''} sem ler no inbox.`,
        icon: '/icons/app-192.png',
        tag: 'autocrm-unread',
      });
    }
    prevUnreadRef.current = unreadCount;
  }, [unreadCount]);

  // Rascunhos offline de visita: tenta enviar quando o painel abre logado e
  // quando o navegador avisa que a conexão voltou. Fica AQUI, e não na tela de
  // Nova Visita, para o envio acontecer mesmo com a pessoa em outra tela — o
  // guard interno de flushVisitDrafts impede levas simultâneas.
  useEffect(() => {
    if (!user) return;
    flushVisitDrafts();
    const onOnline = () => flushVisitDrafts();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [user]);

  // Mantém localStorage e sidebar em sincronia após edição do perfil.
  //
  // MESCLA em vez de substituir: PATCH /auth/me devolve só id/nome/e-mail/papel,
  // e trocar o objeto inteiro apagaria organization_name e organization_type —
  // o nome da empresa sairia da sidebar e o seletor de loja desapareceria até o
  // próximo login.
  const handleUserUpdate = (userData) => {
    const merged = { ...user, ...userData };
    localStorage.setItem('crm_user', JSON.stringify(merged));
    setUser(merged);
  };

  const handleLogout = useCallback(() => {
    // Avisa o servidor antes de descartar o token: é isso que libera o próximo
    // login deste usuário (sessão única). Se a rede falhar, a inatividade
    // libera em 5 minutos — não vale segurar a saída por causa disso.
    const token = localStorage.getItem('crm_token');
    if (token) {
      // Solta o push deste aparelho do usuário que está saindo: sem isto ele
      // continuaria recebendo aviso das conversas dele neste navegador.
      detachPush(token);
      authApi.logout(token).catch(() => { /* sem rede: expira por inatividade */ });
    }
    localStorage.removeItem('crm_token');
    localStorage.removeItem('crm_user');
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    setActiveStoreId(null);
    setActiveStore(null);
    setUser(null);
  }, []);

  const handleInactivityLogout = useCallback(() => {
    localStorage.setItem(INACTIVITY_LOGOUT_FLAG, '1');
    handleLogout();
  }, [handleLogout]);
  useInactivityLogout(Boolean(user), handleInactivityLogout);

  // Única página pública do painel: o nginx e o service worker servem o
  // index.html para qualquer caminho, então o desvio é feito aqui pela URL,
  // antes do login. Não há roteador — o resto do painel navega por estado.
  if (window.location.pathname.replace(/\/$/, '') === '/privacidade') return <PrivacyPage />;
  if (!user) return <LoginPage onLogin={handleLogin} />;

  const pages = {
    // Uma página por nível da árvore: o menu tem um item para cada, na ordem da
    // hierarquia (grupo, concessionária, loja).
    ...ORG_TYPES.reduce((acc, t) => ({
      ...acc,
      [orgPageId(t.value)]: <OrganizationsPage user={user} level={t.value} />,
    }), {}),
    // Um destino por submenu de Relatórios: relatório novo é uma linha em
    // REPORT_ITEMS mais a tela dele aqui.
    'report-seller-conversion': <SellerConversionReport user={user} />,
    'report-unattended': <UnattendedReport user={user} />,
    'report-proposals': <ProposalsReport user={user} />,
    dashboard: <Dashboard />,
    'store-profile': (
      <OrganizationsPage user={user} level="loja" selfMode storeId={activeStore ? Number(activeStore) : null} />
    ),
    'new-visit': <NewVisitPage hasStore={Boolean(activeStore)} />,
    visits: <VisitsPage isAdmin={user?.role === 'admin'} isSeller={user?.role === 'seller'} />,
    conversations: (
      <ConversationsPage
        isAdmin={user?.role === 'admin'}
        onNewProposal={openProposalFor}
        onNewTestDrive={openTestDriveFor}
        initialConversationId={initialConversationId}
        onInitialConsumed={clearInitialConversation}
      />
    ),
    'test-drives': (
      <TestDrivesPage
        hasStore={Boolean(activeStore)}
        isAdmin={user?.role === 'admin'}
        initial={testDriveDraft}
        onInitialConsumed={clearTestDriveDraft}
      />
    ),
    proposals: (
      <ProposalsPage
        hasStore={Boolean(activeStore)}
        isAdmin={user?.role === 'admin'}
        initial={proposalDraft}
        onInitialConsumed={clearProposalDraft}
      />
    ),
    aftersales: <AftersalesPage hasStore={Boolean(activeStore)} isAdmin={user?.role === 'admin'} />,
    vehicles: <VehiclesPage hasStore={Boolean(activeStore)} />,
    users: <UsersPage currentUser={user} stores={stores} scopeOrgs={scopeOrgs} activeStore={activeStore} />,
    settings: <SettingsPage hasStore={Boolean(activeStore)} />,
    profile: <ProfilePage user={user} onUserUpdate={handleUserUpdate} />,
  };

  return (
    <div className="app">
      <Sidebar
        user={user}
        page={page}
        setPage={setPage}
        onLogout={handleLogout}
        stores={stores}
        activeStore={activeStore}
        onChangeStore={changeStore}
        unreadCount={unreadCount}
      />
      {/* A key remonta a página ao trocar de loja: cada tela já busca os dados no
          mount, então não precisa de lógica de recarga própria. */}
      <main className="main-content" key={activeStore || 'all'}>
        {pages[page] || pages[homePage(user)]}
      </main>
    </div>
  );
}
